// REISOR_STAGE22P2P6_CLIENT_DRIVER_ARCHIVE_POLICY_CAPACITOR_FAST_LOAD_V1
import i18next from "i18next";
const t = i18next.t.bind(i18next);
// src/components/DriverWebApp.jsx
// REISOR_STAGE22P1B_DRIVER_MAIN_SCREENS_I18N_CLEANUP_V1
import React, { useEffect, useMemo, useRef, useState } from "react";
import { App as CapApp } from "@capacitor/app";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { useTranslation } from "react-i18next";
import { humanOrderNo } from "../utils/orderNo";
import { useSound } from "../hooks/useSound";
import { useDriverFreightInviteInbox } from "../hooks/useDriverFreightInviteInbox.js";
import useDraggableFloatingPill from "../hooks/useDraggableFloatingPill.js";
import { ClipboardList, CalendarDays, Settings, ChevronLeft, RefreshCw, Wallet, Search, UserRound, BriefcaseBusiness, FileText, LifeBuoy, Share2, Volume2, ChevronRight, Sun, Bell, LogOut, Sparkles, Plus, Truck, X, Star } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import NativeSafeSelect from "./ui/NativeSafeSelect.jsx";
import LanguageSelector from "./LanguageSelector.jsx";
import DriverProfile from "./DriverProfile";
import DriverBalance from "./DriverBalance";
import BidInline from "./BidInline.jsx";
import { placeBid, cancelBid } from "../api/orders.js";
import { pingOrderGps, syncPeriodicOrderGpsForOrders } from "../utils/gpsPing";
import { syncNativeTrackingForOrders } from "../utils/nativeTracking";
import { formatWhen } from "../utils/when";
import { reisorProDriverRequest } from "../utils/reisorProMarketHttp.js";
import { paymentItemsFromOrder, paymentTitleFromOrder, paymentIsFinal } from "../utils/paymentSummary";
import PaymentChecks from "./PaymentChecks";
import DriverReferralPromoCard from "./DriverReferralPromoCard.jsx";
import DriverSupportChat from "./DriverSupportChat.jsx";
import DriverFreightMarket from "./DriverFreightMarket.jsx"; // REISOR_STAGE133C_DRIVER_FREIGHT_MARKET_LAUNCH_V1
import DriverAiAssistant from "./DriverAiAssistant.jsx"; // REISOR_AI_V03B_DRIVER_UX
import DriverContactActions from "./DriverContactActions.jsx"; // REISOR_DRIVER_CONTACT_ACTIONS_V1
import LegalDocsModal from "./LegalDocsModal.jsx";
import ClientTrustReviewModal from "./ClientTrustReviewModal.jsx";
import DriverAvailableVehicleModal from "./DriverAvailableVehicleModal.jsx";
import DriverMarketStatusQuickBar from "./DriverMarketStatusQuickBar.jsx"; // REISOR_STAGE160A1_DRIVER_AVAILABLE_VEHICLE_UI_V1
import ReisorVehicleMarket from "./ReisorVehicleMarket.jsx"; // REISOR_STAGE160B2_UNIFIED_VEHICLE_MARKET_UI_V1
import DriverMyAdsSheet from "./DriverMyAdsSheet.jsx"; // REISOR_STAGE160C3_MY_ADS_NAV_V1
import DriverMyVehicles from "./DriverMyVehicles.jsx"; // REISOR_STAGE160C2_MY_VEHICLES_V1
import DriverMyFreights from "./DriverMyFreights.jsx"; // REISOR_STAGE161B2_DRIVER_MY_FREIGHTS_V1
import DriverQuickAccessSheet from "./DriverQuickAccessSheet.jsx";
import { getDriverReferralPromoCopy } from "../utils/driverReferralPromoCopy";
import {
  DRIVER_APP_VERSION_NAME,
  DRIVER_APP_VERSION_CODE,
} from "../appVersion.js";

const DriverNativeBridge = registerPlugin("DriverTracking");

/* START REISOR_STAGE20A_EXTERNAL_TRANSLATE_HELPER_V1 */
const REISOR_STAGE20A_EXTERNAL_TRANSLATE_URL = "https://translate.yandex.ru/";

async function reisorStage20ACopyAndOpenTranslator(text) {
  const clean = String(text || "").trim();
  if (!clean) return;
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(clean).catch(() => {});
    }
  } catch {}
  try {
    if (typeof window !== "undefined") {
      const win = window.open(REISOR_STAGE20A_EXTERNAL_TRANSLATE_URL, "_blank", "noopener,noreferrer");
      if (!win) window.location.href = REISOR_STAGE20A_EXTERNAL_TRANSLATE_URL;
    }
  } catch {
    try { if (typeof window !== "undefined") window.location.href = REISOR_STAGE20A_EXTERNAL_TRANSLATE_URL; } catch {}
  }
}
/* END REISOR_STAGE20A_EXTERNAL_TRANSLATE_HELPER_V1 */
// === BALANCE HELPERS v4 (do not edit) ===
// === ROUTE META HELPERS (CANON) ===
// CANON: distance/time берём из сохранённого order.routeMeta.
// НЕ пересчитывать маршрут на каждом открытии (клиент/водитель) — защита от платных лимитов.
function formatDurationMin(min) {
  const n = Math.round(Number(min || 0));
  const h = Math.floor(n / 60);
  const m = n % 60;
  if (!h && !m) return `0 ${i18next.t("units.minuteShort")}`;
  if (!h) return `${m} ${i18next.t("units.minuteShort")}`;
  return m
    ? `${h} ${i18next.t("units.hourShort")} ${m} ${i18next.t("units.minuteShort")}`
    : `${h} ${i18next.t("units.hourShort")}`;
}
function formatDistanceKm(km) {
  return `${Math.round(Number(km || 0))} ${i18next.t("units.km")}`;
}
const nfRub = new Intl.NumberFormat("ru-RU");
function moneyText(value, { showPlus = false, sym = "₽" } = {}) {
  const n = Number(value ?? 0) || 0;
  const abs = Math.abs(n);
  const body = `${nfRub.format(Math.round(abs))} ${sym}`;
  if (n < 0) return `-${body}`;
  if (showPlus && n > 0) return `+${body}`;
  return body;
}
function fixedOrderPriceOf(o) {
  if (!o) return null;
  // CANON: финальная цена заказа = выбранная ставка (order.meta.selectedBid.bidAmount)
  const candidates = [
    o?.meta?.selectedBid?.bidAmount,
    o?.meta?.selectedBid?.amount,
    o?.meta?.selectedBid?.price,
    o?.selectedBid?.bidAmount,
    o?.selectedBid?.amount,
    o?.selectedBid?.price,
    // some payloads may keep chosen bid under "bid"
    o?.bid?.bidAmount,
    o?.bidAmount,
    // base price for FIX orders (stored in meta)
    o?.meta?.priceAmount,
    o?.meta?.price_amount,
    o?.meta?.priceRub,
    o?.meta?.price_rub,
    // legacy / fallback fields (do not break older payloads)
    o?.fixedOrderPrice,
    o?.fixed_price,
    o?.orderPrice,
    o?.price,
    o?.amount,
    o?.totalPrice,
    o?.finalPrice,
    o?.acceptedPrice,
    o?.agreedPrice,
  ];
  for (const v of candidates) {
    if (v === null || v === undefined || v === "") continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function priceModeOf(o) {
  const m = String(o?.meta?.priceMode || o?.meta?.price_mode || "").trim().toLowerCase();
  return m;
}
function isFixedPriceMode(mode) {
  const m = String(mode || "").trim().toLowerCase();
  return m === "fixed" || m === "fix";
}
function basePriceRubOf(o) {
  const raw = o?.meta?.priceRub ?? o?.meta?.price_rub ?? null;
  if (raw === null || raw === undefined || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}
function basePriceCurrencyOf(o) {
  const c = String(o?.meta?.priceCurrency || o?.meta?.price_currency || o?.meta?.currency || "RUB").trim().toUpperCase();
  return c || "RUB";
}
function basePriceAmountOf(o) {
  const raw = o?.meta?.priceAmount ?? o?.meta?.price_amount ?? null;
  if (raw === null || raw === undefined || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function currencySymbolOf(code) {
  const c = String(code || "RUB").trim().toUpperCase();
  if (c === "USD") return "$";
  if (c === "EUR") return "€";
  if (c === "UZS") return i18next.t("units.uzs");
  return "₽";
}

// BEST_PRICE_FULL_TRIP_ONLY: dogruz/add_load bids are useful offers,
// but they must not compete for the visible best price.
function isDogruzBidForBestPrice(b) {
  const values = [
    b?.loadType,
    b?.load_type,
    b?.bidType,
    b?.bid_type,
    b?.type,
    b?.meta?.loadType,
    b?.meta?.load_type,
    b?.extra?.loadType,
    b?.extra?.load_type,
  ]
    .map((x) => String(x || "").trim().toLowerCase())
    .filter(Boolean);

  return values.some((v) =>
    v === "add_load" ||
    v === "addload" ||
    v === "dogruz" ||
    v === "dogrus" ||
    v === "\u0434\u043e\u0433\u0440\u0443\u0437" ||
    v === "\u0434\u043e\u0433\u0440\u0443\u0437\u043e\u043c" ||
    v === "partial" ||
    v === "part_load" ||
    v === "partload" ||
    v === "way_load" ||
    v === "on_way"
  );
}

function bidRubForBestPrice(b) {
  const direct = Number(
    b?.bidAmountRub ??
    b?.bid_amount_rub ??
    b?.amountRub ??
    b?.amount_rub ??
    b?.rubEquivalent ??
    b?.rub_equivalent ??
    0
  ) || 0;
  if (direct > 0) return direct;

  const cur = String(b?.bidCurrency || b?.currency || b?.bid_currency || "RUB").trim().toUpperCase();
  const amount = Number(b?.bidAmount ?? b?.amount ?? b?.price ?? 0) || 0;
  if (!(amount > 0)) return 0;
  if (cur === "RUB" || cur === "RUR") return amount;
  return 0;
}

function priceCurrencyOf(o) {
  const c = String(
    o?.meta?.selectedBid?.bidCurrency || o?.meta?.selectedBid?.currency ||
    o?.selectedBid?.bidCurrency || o?.selectedBid?.currency ||
    o?.bidCurrency || o?.currency ||
    o?.meta?.priceCurrency || o?.meta?.price_currency || o?.meta?.currency ||
    "RUB"
  ).trim().toUpperCase();
  return c || "RUB";
}


function txType(tx) {
  return String(tx?.type || "").toUpperCase();
}
function txIsDebit(tx) {
  const t = txType(tx);
  return t === "DRIVER_COMMISSION" || t === "COMMISSION" || t === "DRIVER_PAYOUT" || t === "PAYOUT";
}
function txSignedAmount(tx) {
  const raw = Number(tx?.amount ?? 0) || 0;
  const abs = Math.abs(raw);
  if (abs === 0) return 0;
  if (txIsDebit(tx)) return -abs;   // debit is always negative
  return raw;                       // keep source sign for other tx types
}
function txAmountText(tx) {
  return moneyText(txSignedAmount(tx), { showPlus: true });
}
// === /BALANCE HELPERS v4 ===
// ---------- helpers ----------
function ruDateTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString("ru-RU");
}
function safeJson(v) {
  try { return JSON.parse(v); } catch { return null; }
}
function getDriverObjectFromStorage() {
  const keys = ["driver_data", "driver", "driver_profile", "driverAuth", "auth"];
  for (const k of keys) {
    try {
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      const obj = safeJson(raw);
      if (obj && typeof obj === "object") return obj;
    } catch {}
  }
  return null;
}
function getDriverTokenFromStorage() {
  const obj = getDriverObjectFromStorage();
  const rawToken =
    obj?.token ||
    obj?.accessToken ||
    obj?.authToken ||
    obj?.driverToken ||
    obj?.jwt ||
    localStorage.getItem("driver_token") ||
    localStorage.getItem("token") ||
    null;
  const t = String(rawToken || "").trim();
  return t || null;
}
function getDriverFromStorage() {
  const obj = getDriverObjectFromStorage();
  if (obj) {
    const id = obj?.id || obj?._id || obj?.driverId || obj?.driver_id;
    const name =
      obj?.name ||
      obj?.fullName ||
      [obj?.lastName, obj?.firstName, obj?.middleName].filter(Boolean).join(" ") ||
      [obj?.firstName, obj?.lastName].filter(Boolean).join(" ") ||
      obj?.driverName;
    if (id) return { id: String(id), name: name || i18next.t("driverFallback.name") };
  }
  const rawId = localStorage.getItem("driver_id");
  if (rawId) return { id: String(rawId), name: i18next.t("driverFallback.name") };
  return { id: "driver-demo", name: i18next.t("driverFallback.name") };
}
function extractList(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.items)) return data.items;
  if (data && Array.isArray(data.orders)) return data.orders;
  return [];
}
function isExpeditorOwnedOrder(o) {
  return Boolean(String(o?.expeditorId || o?.meta?.expeditorId || "").trim());
}

function orderCounterpartyPhone(o) {
  return String(isExpeditorOwnedOrder(o) ? (o?.expeditorPhone || "") : (o?.clientPhone || "")).trim();
}

function orderChatLabelKey(o) {
  return isExpeditorOwnedOrder(o) ? "chat.with_expeditor" : "chat.with_client";
}

function orderCallLabelKey(o) {
  return isExpeditorOwnedOrder(o) ? "driverOrder.callExpeditor" : "driverOrder.callClient";
}

function orderIdOf(o) {
  if (!o) return "";
  const id =
    o.orderId ||
    o.order_id ||
    o.order?.id ||
    o.order?.orderId ||
    o.orderInfo?.id ||
    o.orderInfo?.orderId ||
    o.id ||
    o._id ||
    o.uuid ||
    "";
  return String(id || "");
}
function orderNoOf(o) {
  // пытаемся найти "человеческий" номер заказа из разных возможных полей
  // (подстраховка под разные ответы API)
  return (
    o?.orderNo ??
    o?.order_no ??
    o?.orderNumber ??
    o?.order_number ??
    o?.number ??
    o?.publicNo ??
    o?.public_no ??
    o?.humanNo ??
    o?.human_no ??
    o?.displayNo ??
    o?.display_no ??
    ""
  );
}
function displayOrderNo(o) {
  const n = orderNoOf(o);
  // humanOrderNo уже есть в проекте (как минимум в клиентском флоу),
  // если вдруг вернулся number строкой/числом — покажем его,
  // иначе упадём на id.
  try {
    return humanOrderNo(n || orderIdOf(o) || o?.id || "");
  } catch (e) {
    return String(n || orderIdOf(o) || o?.id || "");
  }
}
function canonStatus(o) {
  return String(o?.status || "").trim().toUpperCase() || null;
}
function toBearer(token) {
  const t = String(token || "").trim();
  if (!t) return "";
  return /^bearer\s+/i.test(t) ? t : `Bearer ${t}`;
}

// REISOR_DRIVER_REQUESTS_PRIORITY_SHORTCUT_V1
// Keep the compact Requests header score aligned with the Profile priority source.
function driverRequestsPriorityScore(source) {
  const trust = source?.driverTrust || source?.trust || source?.prioritySummary || source || {};
  const raw =
    trust?.priorityScore ??
    trust?.priority ??
    trust?.score ??
    trust?.trustPercent ??
    trust?.trustScore ??
    source?.priorityScore ??
    source?.priority ??
    source?.score ??
    source?.trustPercent ??
    source?.trustScore ??
    (typeof source === "number" || typeof source === "string" ? source : NaN);

  const n = Math.round(Number(raw));
  return Number.isFinite(n) ? Math.max(0, n) : null;
}
function cleanErr(e) {
  const msg = String(e?.message || e || "").trim();
  if (!msg) return i18next.t("driverFallback.errorRetry");
  const low = msg.toLowerCase();

  if (
    low.includes("failed to fetch") ||
    low.includes("networkerror") ||
    low.includes("load failed") ||
    low.includes("err_network") ||
    low == "fetch"
  ) {
    return i18next.t("driverFallback.network");
  }

  if (low.includes("<html") || low.includes("<!doctype") || low.includes("504 gateway")) {
    return i18next.t("driverFallback.server504");
  }

  return msg.length > 140 ? msg.slice(0, 140) + "…" : msg;
}
// === SOUND SETTINGS (driver) ===
const SOUND_SETTINGS_KEY = "driver_sound_settings_v1";
const SOUND_CUSTOM_PREFIX = "driver_sound_custom_";
const SOUND_PRESETS = [
  { id: "off", name: t("sounds.preset.off") },
  { id: "reisor_order", name: i18next.t("driverSound.order") },
  { id: "reisor_assign", name: i18next.t("driverSound.assign") },
  { id: "reisor_chat", name: i18next.t("driverSound.chat") },
  { id: "happy", name: t("sounds.preset.happy") },
  { id: "ding", name: t("sounds.preset.ding") },
  { id: "double", name: t("sounds.preset.double") },
  { id: "buzz", name: t("sounds.preset.buzz") },
  { id: "chime", name: i18next.t("driverSound.chime") },
  { id: "pop", name: i18next.t("driverSound.pop") },
  { id: "alarm", name: i18next.t("driverSound.alarm") },
  { id: "sonar", name: i18next.t("driverSound.sonar") },
  { id: "notify", name: i18next.t("driverSound.notify") },
  { id: "custom", name: t("sounds.preset.custom") },
];
function loadSoundSettings() {
  try {
    const raw = localStorage.getItem(SOUND_SETTINGS_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    return obj && typeof obj === "object" ? obj : null;
  } catch {
    return null;
  }
}
function saveSoundSettings(next) {
  try {
    localStorage.setItem(SOUND_SETTINGS_KEY, JSON.stringify(next || {}));
  } catch {}
}
function loadCustomSound(eventKey) {
  try {
    return localStorage.getItem(SOUND_CUSTOM_PREFIX + eventKey) || "";
  } catch {
    return "";
  }
}
function saveCustomSound(eventKey, dataUrl) {
  try {
    if (dataUrl) localStorage.setItem(SOUND_CUSTOM_PREFIX + eventKey, String(dataUrl));
    else localStorage.removeItem(SOUND_CUSTOM_PREFIX + eventKey);
  } catch {}
}
function getAudioCtx() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    if (!window.__cargoDriverAudioCtx) window.__cargoDriverAudioCtx = new AC();
    return window.__cargoDriverAudioCtx;
  } catch {
    return null;
  }
}
function playWebAudioPreset(presetId, volume = 0.8) {
  const ctx = getAudioCtx();
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
  } catch {}
  const t0 = ctx.currentTime + 0.01;
  const master = ctx.createGain();
  master.gain.setValueAtTime(0.0001, t0);
  master.gain.exponentialRampToValueAtTime(Math.max(0.0002, Math.min(1, volume)) * 0.22, t0 + 0.02);
  master.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.35);
  master.connect(ctx.destination);
  const ding = (freq, at, dur = 0.14, type = "sine", peak = 0.35) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, at);
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(peak, at + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    osc.connect(g);
    g.connect(master);
    osc.start(at);
    osc.stop(at + dur + 0.01);
  };
  if (presetId === "ding") {
    ding(880, t0, 0.16, "sine", 0.32);
    return;
  }
  if (presetId === "double") {
    ding(880, t0, 0.14, "sine", 0.28);
    ding(1174.66, t0 + 0.16, 0.14, "sine", 0.26);
    return;
  }
  if (presetId === "buzz") {
    ding(220, t0, 0.25, "sawtooth", 0.20);
    return;
  }
  // default: "happy"
  const notes = [523.25, 659.25, 783.99]; // C5 E5 G5
  notes.forEach((f, i) => ding(f, t0 + i * 0.08, 0.13, "triangle", 0.33));
  ding(988.0, t0 + 0.26, 0.10, "sine", 0.18);
}
function playCustomDataUrl(dataUrl, volume = 1) {
  try {
    const a = new Audio(String(dataUrl || ""));
    a.volume = Math.max(0, Math.min(1, volume));
    a.play().catch(() => {});
  } catch {}
}
async function postJson(url, body, token) {
  const headers = { "Content-Type": "application/json" };
  if (token) headers.Authorization = toBearer(token);
  const res = await fetch(url, {
    method: "POST",
    headers,
    credentials: "include",
    body: JSON.stringify(body || {}),
  });
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  let data;
  if (ct.includes("application/json")) {
    data = await res.json().catch(() => ({}));
  } else {
    data = await res.text().catch(() => "");
  }
  if (!res.ok) {
    const msg =
      (data && typeof data === "object" && (data.error || data.message)) ||
      (typeof data === "string" && data.slice(0, 160)) ||
      `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data;
}

// REISOR_STAGE97D4_DRIVER_SETTINGS_WORK_STATUS_INLINE_V1
const DRIVER_WORK_STATUS_OPTIONS = [
  { value: "ip", key: "driverCarrierStatus.ip", fallback: "ИП" },
  { value: "self_employed", key: "driverCarrierStatus.self_employed", fallback: "Самозанятый" },
  { value: "company", key: "driverCarrierStatus.company", fallback: "ООО / компания" },
  { value: "park_logist", key: "driverCarrierStatus.park_logist", fallback: "Парк / логист" },
];

function driverWorkStatusNeedsInn(status) {
  return ["ip", "self_employed", "company"].includes(String(status || ""));
}

function driverWorkStatusNeedsName(status) {
  return ["company", "park_logist"].includes(String(status || ""));
}

function driverWorkStatusSnapshot(source) {
  const d = source || {};
  return {
    carrierStatus: String(d.carrierStatus || "").trim(),
    inn: String(d.inn || "").replace(/\D/g, "").slice(0, 12),
    carrierName: String(d.carrierName || "").trim().slice(0, 160),
  };
}

function mergeDriverLocalStoragePatch(patch) {
  const keys = ["driver_data", "driver", "driver_profile", "driverAuth", "auth"];
  for (const k of keys) {
    try {
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      const obj = JSON.parse(raw);
      if (!obj || typeof obj !== "object") continue;
      localStorage.setItem(k, JSON.stringify({ ...obj, ...(patch || {}) }));
    } catch {}
  }
}
// ---------- statuses ----------
const STATUS_RU = {
  NEW: i18next.t("driverStatus.NEW"),
  SEARCH_DRIVER: i18next.t("driverStatus.SEARCH_DRIVER"),
  WAITING_DRIVER_ACCEPT: i18next.t("driverStatus.WAITING_DRIVER_ACCEPT"),
  // REISOR_DRIVER_WAITING_ACCEPT_I18N_V1
  DRIVER_DECLINED: i18next.t("driverStatus.DRIVER_DECLINED"),
  DRIVER_ACCEPT_EXPIRED: i18next.t("driverStatus.DRIVER_ACCEPT_EXPIRED"),
  ASSIGNED: i18next.t("driverStatus.ASSIGNED"),
  ON_ROUTE: i18next.t("driverStatus.ON_ROUTE"),
  AT_PICKUP: i18next.t("driverStatus.AT_PICKUP"),
  LOADED: i18next.t("driverStatus.LOADED"),
  COMPLETED: i18next.t("driverStatus.COMPLETED"),
  CANCELLED: i18next.t("driverStatus.CANCELLED"),
  CANCELED: i18next.t("driverStatus.CANCELED"),
  PROBLEM: i18next.t("driverStatus.PROBLEM"),
};

function statusColorClass(status) {
  const s = String(status || "").toUpperCase();
  if (["WAITING_DRIVER_ACCEPT"].includes(s)) return "gpt-status-yellow";
  if (["ASSIGNED", "ON_ROUTE", "LOADED"].includes(s)) return "gpt-status-green";
  if (["AT_PICKUP"].includes(s)) return "gpt-status-yellow";
  return "";
}

function isFixedOrder(o) {
  const m = String(
    o?.meta?.priceMode || o?.meta?.price_mode || o?.priceMode || o?.pricingMode || o?.mode || o?.priceType || ""
  ).trim().toLowerCase();

  // canonical values
  if (m === "fixed" || m === "fix" || m === "\u0444\u0438\u043a\u0441" || m === "\u0444\u0438\u043a\u0441\u0438\u0440\u043e\u0432\u0430\u043d\u043d\u0430\u044f") return true;

  // some payloads may use booleans
  if (o?.isAuction === false) return true;
  if (o?.auction === false) return true;

  // explicit auction markers
  if (m.includes("auction") || m.includes("\u0442\u043e\u0440\u0433")) return false;

  return false;
}

// REISOR_DRIVER_VISIBLE_UI_I18N_V1

// REISOR_STAGE97G2_LEGAL_UI_HUB_V2
function driverLegalLang(i18n) {
  const raw = String(i18n?.resolvedLanguage || i18n?.language || "ru").toLowerCase();
  if (raw.startsWith("uz")) return "uz";
  if (raw.startsWith("tr")) return "tr";
  return "ru";
}

const DRIVER_LEGAL_API_BASE = "https://backend.cargo-platform.online/api";

function absoluteDriverLegalV5Url(input) {
  const raw = String(input || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (/^\/api\/public\/legal\//i.test(raw)) {
    return `${DRIVER_LEGAL_API_BASE}${raw.replace(/^\/api/i, "")}`;
  }
  if (/^\/public\/legal\//i.test(raw)) return `${DRIVER_LEGAL_API_BASE}${raw}`;
  return raw;
}


function DriverSettingsRow({
  icon: Icon,
  title,
  description,
  onClick,
  tone = "slate",
}) {
  const toneClass = {
    slate:
      "border-slate-700/80 bg-slate-950/35 text-slate-100",
    sky:
      "border-sky-400/25 bg-sky-400/10 text-slate-100",
    emerald:
      "border-emerald-400/25 bg-emerald-400/10 text-slate-100",
    amber:
      "border-amber-400/25 bg-amber-400/10 text-slate-100",
    rose:
      "border-rose-400/30 bg-rose-500/10 text-rose-100",
  }[tone] ||
    "border-slate-700/80 bg-slate-950/35 text-slate-100";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`driver-settings-row flex w-full items-center gap-3 border-b
        border-slate-700/60 px-4 py-4 text-left
        last:border-b-0 active:bg-white/[0.04] ${toneClass}`}
    >
      <span
        className="driver-settings-row-icon flex h-11 w-11 shrink-0 items-center
          justify-center rounded-2xl border border-white/10
          bg-black/15"
      >
        {Icon ? <Icon size={21} strokeWidth={2.2} /> : null}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block text-base font-black leading-tight">
          {title}
        </span>

        {description ? (
          <span
            className="mt-1 block text-xs leading-5
              text-slate-300/85"
          >
            {description}
          </span>
        ) : null}
      </span>

      <ChevronRight
        className="shrink-0 text-slate-400"
        size={21}
        strokeWidth={2.3}
      />
    </button>
  );
}

export default function DriverWebApp({
  isRegisterFlow = false,
  onExitRegisterFlow,
  token: sessionToken = null,
  driver: sessionDriver = null,
}) {
  const { t, i18n } = useTranslation();
  const aiFloatingPill = useDraggableFloatingPill("reisor.ai.floating.driver.v1");
  const [driverLegalDoc, setDriverLegalDoc] = useState(null);
  const [driverLegalBundle, setDriverLegalBundle] = useState({ loading: false, error: "", version: "", documents: [] });
  const referralCopy = getDriverReferralPromoCopy(String(i18n.resolvedLanguage || i18n.language || "ru").split("-")[0]);
  
  // SOUND_EXTRACT_v1
  const sound = useSound();
  // SW_FOREGROUND_SOUND_v1: play sound only in foreground on SW postMessage
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMsg = (event) => {
      const msg = event?.data;
      // We listen only to SW -> page messages
      if (msg && msg.type === "push") {
        try { sound.playFromPush(msg.data); } catch {}
      }
    };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
  }, [sound]);
// REISOR_HUB_SESSION_PROP_BRIDGE_V1
  // App.jsx already owns the authenticated session and passes token/driver here.
  // Prefer the long-standing storage snapshot when present, but never downgrade an
  // authenticated parent session to guest merely because native/local storage lags.
  const [driverObj, setDriverObj] = useState(
    () => getDriverObjectFromStorage() || sessionDriver || null
  );
  useEffect(() => {
    const timer = setInterval(() => {
      const next = getDriverObjectFromStorage() || sessionDriver || null;
      if (JSON.stringify(next || null) !== JSON.stringify(driverObj || null)) {
        setDriverObj(next);
      }
    }, 1200);
    return () => clearInterval(timer);
  }, [driverObj, sessionDriver]);

  const driver = useMemo(
    () => getDriverFromStorage() || sessionDriver || null,
    [driverObj, sessionDriver]
  );
  const driverToken = useMemo(
    () => getDriverTokenFromStorage() || String(sessionToken || "").trim() || null,
    [driverObj, sessionToken]
  );
  const driverStatus = String(
    driverObj?.status ||
    sessionDriver?.status ||
    (isRegisterFlow ? "draft" : "pending")
  ).toLowerCase();
  const tabsAllowed = driverStatus === "approved";
  const showPendingBanner = driverStatus === "pending";

  // REISOR_STAGE22P2Q9A_PRIORITY_HISTORY_ABSORPTION_AND_DRIVER_TAB_STABILITY_V1
  // Session polling must not reset the current screen every few seconds.
  // Auto-open "Заявки" only once after real transition to approved.
  const autoOpenRequestsAfterApprovalRef = useRef(false);

  // REISOR_STAGE22P2Q8B_DRIVER_ACCESS_LIMITED_UI_I18N_V1
  const driverPlatformAccess =
    driverObj?.platformAccess ||
    driverObj?.driverPlatformAccess ||
    driverObj?.platform_access ||
    null;

  const driverPlatformStatus = String(
    driverPlatformAccess?.status ||
    driverObj?.accessStatus ||
    ""
  ).toLowerCase();

  const driverAccessLimited =
    driverPlatformStatus === "blocked" ||
    driverPlatformStatus === "review_required" ||
    driverPlatformAccess?.canTakeNewOrders === false;

  const driverAccessLimitedMessage = driverAccessLimited
    ? t("driverAccess.ordersLimitedMessage", {
        defaultValue: "Доступ к заказам ограничен. Обратитесь в поддержку REISOR.",
      })
    : "";

  // ✅ SYNC_STATUS_V1: если диспетчер одобрил водителя — подтягиваем статус с сервера,
  // чтобы вкладки "Заявки/Поездки/Баланс" открылись без ручного перелогина.
  useEffect(() => {
    if (!driverToken) return;
    let cancelled = false;

    const sync = async () => {
      try {
        const headers = {};
        const t = String(driverToken || "").trim();
        if (t) headers.Authorization = /^bearer\s+/i.test(t) ? t : `Bearer ${t}`;

        const res = await fetch("/api/drivers/session", {
          method: "GET",
          headers,
          credentials: "include",
          cache: "no-store",
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return;

        const s = data?.driver || data?.driverData || data?.profile || null;
        const newStatus = String(s?.status || "").toLowerCase();
        if (!newStatus) return;

        // обновляем локальный driver_data, чтобы setInterval на driverObj увидел изменения
        try {
          const raw = localStorage.getItem("driver_data");
          const cur = raw ? JSON.parse(raw) : {};
          const next = { ...(cur || {}), ...(s || {}), status: newStatus };
          localStorage.setItem("driver_data", JSON.stringify(next));
        } catch {}

        if (
          !cancelled &&
          newStatus === "approved" &&
          driverStatus !== "approved" &&
          !autoOpenRequestsAfterApprovalRef.current
        ) {
          // Open requests only once after approval, not on every session poll.
          autoOpenRequestsAfterApprovalRef.current = true;
          setMainTab("requests");
          setRequestsTab("new");
        }
      } catch {}
    };

    // сразу и затем периодически
    sync();
    const t = setInterval(sync, 6000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverToken, driverStatus]);
  const [mainTab, setMainTab] = useState(isRegisterFlow ? "profile" : "requests");
  const [requestsTab, setRequestsTab] = useState("new"); // new | my_offers
  const [requestsSource, setRequestsSource] = useState("market"); // reisor | market — REISOR_MARKET_DEFAULT_HOME_V1
  const [aiOpen, setAiOpen] = useState(false); // REISOR_AI_V03B_DRIVER_UX
  const [aiWelcomeOpen, setAiWelcomeOpen] = useState(false); // REISOR_STAGE163M5C2_DRIVER_AI_ENTRY_V1
  const [aiEntryIntent, setAiEntryIntent] = useState(""); // REISOR_STAGE163M5C3_DRIVER_AI_ACTIVATION_V1
  const [vehicleSearchPreset, setVehicleSearchPreset] = useState(null); // REISOR_1_0_102_PHONE_VEHICLE_AI_HANDOFF
  const [requestsPriorityScore, setRequestsPriorityScore] = useState(() =>
    driverRequestsPriorityScore(driverObj)
  );
  const [routeMapOpenByOrder, setRouteMapOpenByOrder] = useState({}); // REISOR_DRIVER_COMPACT_MAP_FEED_V3
  const [tripsTab, setTripsTab] = useState("scheduled"); // scheduled | past
  // DEEP_FOCUS_V1: open specific order after push click (?orderId=...)
  const [deepFocusId, setDeepFocusId] = useState(() => {
    try { return String(localStorage.getItem("deep_order_id") || "").trim(); } catch { return ""; }
  });
  // REISOR_MATCH_STAGE51_MARKET_FREIGHT_DEEP_LINK_V1
  const [deepMarketFreightId, setDeepMarketFreightId] = useState(() => {
    try { return String(localStorage.getItem("deep_market_freight_id") || "").trim(); } catch { return ""; }
  });
  const deepFocusHandledRef = useRef(false);

  function openAiFreight(freightId) {
    const fid = String(freightId || "").trim();
    if (!fid) return;
    setAiOpen(false);
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent("reisor_driver_open_market_freight", { detail: { freightId: fid } }));
    }, 0);
  }

  function showAiSearchInMarket(searchSpec = {}) {
    const localIso = (dayOffset = 0) => {
      const d = new Date();
      d.setHours(12, 0, 0, 0);
      d.setDate(d.getDate() + dayOffset);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    };
    const loadingDate = String(searchSpec?.loadingDate || "").trim();
    const nextFilters = {
      from: String(searchSpec?.from || "").trim(),
      to: String(searchSpec?.to || "").trim(),
      truck: String(searchSpec?.truck || "").trim().toUpperCase(),
      dateMode: loadingDate === localIso(0) ? "today" : loadingDate === localIso(1) ? "tomorrow" : "all",
    };
    try {
      localStorage.setItem(
        `reisor_driver_market_filters_${String(driver?.id || driver?._id || "anon")}`,
        JSON.stringify(nextFilters)
      );
    } catch {}
    setAiOpen(false);
    setSettingsOpen(false);
    setChatOpen(false);
    setMainTab("requests");
    setRequestsSource("market");
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent("reisor_driver_apply_market_filters", { detail: { filters: nextFilters } }));
    }, 0);
  }

  function prepareAiAvailableVehicle(vehicle = null) {
    setAiOpen(false);
    setSettingsOpen(false);
    setChatOpen(false);
    setAvailableVehicleDraft(vehicle && typeof vehicle === "object" ? vehicle : null);
    setAvailableVehicleOpen(true);
  }

  // TDZ FIX: эти state нужны выше первого useEffect, где они уже есть в deps
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState("");

  const [myOrders, setMyOrders] = useState([]);

  // REISOR_DRIVER_REQUESTS_PRIORITY_SHORTCUT_V1
  // Prefer locally refreshed session/profile data when it already carries driverTrust.
  useEffect(() => {
    const next = driverRequestsPriorityScore(driverObj);
    if (next !== null) setRequestsPriorityScore(next);
  }, [driverObj]);

  // Requests-only lightweight refresh. No backend changes and no client-side score formula:
  // the server profile remains the source of truth, including valid 3-digit scores (e.g. 130).
  useEffect(() => {
    if (!tabsAllowed || mainTab !== "requests" || !driver?.id) return;

    let cancelled = false;
    let controller = null;

    const syncPriority = async () => {
      try {
        controller?.abort?.();
        controller = new AbortController();

        const headers = {};
        const bearer = toBearer(driverToken);
        if (bearer) headers.Authorization = bearer;

        const res = await fetch(`/api/drivers/${encodeURIComponent(driver.id)}/profile`, {
          method: "GET",
          headers,
          credentials: "include",
          cache: "no-store",
          signal: controller.signal,
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok || cancelled) return;

        const profile = data?.driver || data?.profile || data || {};
        const next = driverRequestsPriorityScore(
          profile?.driverTrust ||
          profile?.trust ||
          data?.driverTrust ||
          data?.trust ||
          profile
        );

        if (!cancelled && next !== null) setRequestsPriorityScore(next);
      } catch (e) {
        if (String(e?.name || "") !== "AbortError") {
          // Header score is non-blocking: keep the last known value on network errors.
        }
      }
    };

    syncPriority();
    const timer = setInterval(syncPriority, 60000);

    return () => {
      cancelled = true;
      clearInterval(timer);
      try { controller?.abort?.(); } catch {}
    };
  }, [tabsAllowed, mainTab, driver?.id, driverToken]);


  useEffect(() => {
    const id = String(deepFocusId || "").trim();
    if (!id) return;
    if (!tabsAllowed) return;
    if (deepFocusHandledRef.current) return;

    const sameId = (o) => String(orderIdOf(o) || o?.id || o?._id || "") === id;
    const isPastStatus = (st) => ["COMPLETED", "CANCELLED", "CANCELED"].includes(String(st || "").toUpperCase());

    const finish = () => {
      deepFocusHandledRef.current = true;
      try { localStorage.removeItem("deep_order_id"); } catch {}
    };

    const openDeepLinkedOrder = async () => {
      try {
        // 1) уже есть среди поездок
        try {
          const trip = (myOrders || []).find(sameId);
          if (trip) {
            const st = canonStatus(trip) || trip?.status || "";
            setMainTab("trips");
            setTripsTab(isPastStatus(st) ? "past" : "scheduled");
            await openTripById(id);
            finish();
            return;
          }
        } catch {}

        // 2) уже есть среди заявок
        try {
          const req = (orders || []).find(sameId);
          if (req) {
            setMainTab("requests");
            setRequestsSource("reisor");
            setRequestsTab(hasMyOffer(req) ? "my_offers" : "new");
            finish();
            return;
          }
        } catch {}

        // 3) fallback: грузим напрямую по id
        const headers = {};
        if (driverToken) headers.Authorization = toBearer(driverToken);

        const res = await fetch(`/api/orders/${encodeURIComponent(id)}`, {
          headers,
          credentials: "include",
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.error) {
          throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
        }

        const loaded = { ...data, id: String(orderIdOf(data) || data?.id || data?._id || id) };
        const st = canonStatus(loaded) || loaded?.status || "";

        // trip-like заказ
        if (st && !["NEW", "SEARCH_DRIVER"].includes(String(st).toUpperCase())) {
          setMainTab("trips");
          setTripsTab(isPastStatus(st) ? "past" : "scheduled");
          await openTripById(id);
          finish();
          return;
        }

        // обычная заявка
        setOrders((prev) => {
          const arr = Array.isArray(prev) ? prev.slice() : [];
          const ix = arr.findIndex((x) => String(orderIdOf(x) || x?.id || x?._id || "") === id);
          if (ix >= 0) arr[ix] = { ...arr[ix], ...loaded };
          else arr.unshift(loaded);
          return arr;
        });
        setMainTab("requests");
        setRequestsSource("reisor");
        setRequestsTab(hasMyOffer(loaded) ? "my_offers" : "new");
        finish();
      } catch {}
    };

    openDeepLinkedOrder();
  }, [deepFocusId, tabsAllowed, orders, myOrders, driverToken]);
  useEffect(() => { if (!tabsAllowed) setMainTab("profile"); }, [tabsAllowed]);
  // ---- toast (no random popups) ----
  const [toast, setToast] = useState(null); 
  const toastTimerRef = useRef(null);
  const [fatalError, setFatalError] = useState("");
  useEffect(() => {
    const onErr = (e) => {
      try {
        const msg =
          String(e?.error?.stack || e?.error?.message || e?.message || e || "Unknown error");
        setFatalError(msg);
      } catch {
        setFatalError("Unknown error");
      }
    };
    const onRej = (e) => {
      try {
        const msg = String(e?.reason?.stack || e?.reason?.message || e?.reason || "Unhandled rejection");
        setFatalError(msg);
      } catch {
        setFatalError("Unhandled rejection");
      }
    };
    window.addEventListener("error", onErr);
    window.addEventListener("unhandledrejection", onRej);
    return () => {
      try { window.removeEventListener("error", onErr); } catch {}
      try { window.removeEventListener("unhandledrejection", onRej); } catch {}
    };
  }, []);
// {type:'ok'|'err', text, actionText?, onAction?, duration?}
  const showToast = (type, text, opts = {}) => {
    try { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); } catch {}
    const toastObj = { type, text, ...(opts || {}) };
    setToast(toastObj);
    // По ТЗ: если есть кнопка (actionText) — уведомление держим до клика
    let dur;
    if (typeof toastObj.duration === "number") dur = toastObj.duration;
    else if (toastObj.actionText) dur = 0;
    else dur = 3500;
    if (dur > 0) toastTimerRef.current = setTimeout(() => setToast(null), dur);
  };
  // expose toast globally (avoids TDZ after minify)
  try { globalThis.__driverToast = showToast; } catch {}

  // REISOR_STAGE_P9_DRIVER_DURABLE_INBOX_UI_V1
  // A targeted freight is accepted by backend storage first. Native push only wakes this
  // inbox faster; focus/resume polling guarantees the driver still sees it without push.
  // This uses dedicated UI state (not the generic toast), so unrelated app toasts cannot
  // overwrite or silently hide a commercially relevant incoming freight.
  const [incomingFreightInvite, setIncomingFreightInvite] = useState(null);
  const driverFreightInviteInbox = useDriverFreightInviteInbox({
    enabled: tabsAllowed && Boolean(driverToken),
    token: String(driverToken || "").trim(),
    onInvite: (invite) => {
      setIncomingFreightInvite(invite || null);
      try { sound.play("order"); } catch {}
    },
  });
  // REISOR_CLIENT_TRUST_REVIEW_V1
  // After completed order, driver can review the client. Internal-only for dispetcher/admin.
  const [clientTrustReviewOrder, setClientTrustReviewOrder] = useState(null);
  const clientTrustPendingCheckedRef = useRef(false);

// SOUND_UI_V1: настройки звуков (водитель сам выбирает)
const [settingsOpen, setSettingsOpen] = useState(false);
const [settingsTab, setSettingsTab] = useState("main");

// REISOR_LEGAL_V5_POSTLOGIN_HUB_V1
useEffect(() => {
  if (!settingsOpen || settingsTab !== "legal") return undefined;
  let alive = true;
  const controller = new AbortController();
  const legalLocale = driverLegalLang(i18n);
  setDriverLegalBundle({ loading: true, error: "", version: "", documents: [] });

  fetch(`${DRIVER_LEGAL_API_BASE}/public/legal/v5/bundle?countryCode=UZ&accountType=driver&locale=${encodeURIComponent(legalLocale)}`, {
    signal: controller.signal,
    headers: { Accept: "application/json" },
  })
    .then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok || !data?.bundle) throw new Error(data?.code || `HTTP_${res.status}`);
      return data;
    })
    .then((data) => {
      if (!alive) return;
      setDriverLegalBundle({
        loading: false,
        error: "",
        version: String(data.bundle?.version || ""),
        documents: Array.isArray(data.documents) ? data.documents.filter((d) => d?.printUrl && d?.title) : [],
      });
    })
    .catch((err) => {
      if (!alive || err?.name === "AbortError") return;
      setDriverLegalBundle({ loading: false, error: String(err?.message || "LEGAL_BUNDLE_LOAD_FAILED"), version: "", documents: [] });
    });

  return () => { alive = false; controller.abort(); };
}, [settingsOpen, settingsTab, i18n.resolvedLanguage, i18n.language]);
const [availableVehicleOpen, setAvailableVehicleOpen] = useState(false); // REISOR_STAGE160A1_DRIVER_AVAILABLE_VEHICLE_UI_V1
const [availableVehicleDraft, setAvailableVehicleDraft] = useState(null); // REISOR_M5C_AI_RETURN_VEHICLE_DRAFT_V1
const [myAdsOpen, setMyAdsOpen] = useState(false); // REISOR_STAGE160C3_MY_ADS_NAV_V1
const [myVehiclesOpen, setMyVehiclesOpen] = useState(false); // REISOR_STAGE160C3_MY_ADS_NAV_V1
const [myFreightsOpen, setMyFreightsOpen] = useState(false); // REISOR_STAGE161B2_DRIVER_MY_FREIGHTS_V1
const [targetedVehicleFreight, setTargetedVehicleFreight] = useState(null); // REISOR_VEHICLE_SEND_P3_DRIVER_TARGET_V1
const [targetedVehicleCreateKey, setTargetedVehicleCreateKey] = useState(""); // REISOR_VEHICLE_SEND_P3_DRIVER_TARGET_V1

const [quickAccessOpen, setQuickAccessOpen] = useState(false); // REISOR_DRIVER_QUICK_ACCESS_STAR_V1
const [marketFavoritesSignal, setMarketFavoritesSignal] = useState(0);

function beginDriverFreightForVehicle(vehicle) {
  const id = String(vehicle?.id || "").trim();
  const ownerRole = String(vehicle?.ownerRole || "").trim().toUpperCase();
  if (!id || !["DRIVER", "EXPEDITOR"].includes(ownerRole)) return;

  const label = [String(vehicle?.from || "").trim(), String(vehicle?.to || "").trim()]
    .filter(Boolean)
    .join(" → ") || "Машина";

  setTargetedVehicleFreight({ targetKind: "VEHICLE", id, ownerRole, label });
  setTargetedVehicleCreateKey(`vehicle:${id}:${Date.now()}`);
  setMyFreightsOpen(true);
}

function beginDriverFreightForDriver(driver) {
  const id = String(driver?.id || "").trim();
  if (!id) return;

  const label = String(driver?.fullName || "").trim() || "Водитель";
  setTargetedVehicleFreight({ targetKind: "DRIVER_DIRECTORY", id, ownerRole: "DRIVER", label });
  setTargetedVehicleCreateKey(`driver:${id}:${Date.now()}`);
  setMyFreightsOpen(true);
}

async function handleDriverTargetedFreightCreated(post) {
  const context = targetedVehicleFreight;
  const postId = String(post?.id || "").trim();
  const targetKind = String(context?.targetKind || "VEHICLE").trim().toUpperCase();
  const isDriverDirectory = targetKind === "DRIVER_DIRECTORY";
  if (!context?.id || !postId || (!isDriverDirectory && !context?.ownerRole)) {
    showToast("error", "Груз создан, но адресная отправка машине не выполнена.", { duration: 5000 });
    setTargetedVehicleFreight(null);
    return;
  }

  try {
    const { response, data } = await reisorProDriverRequest(
      isDriverDirectory
        ? `/api/vehicle-market/drivers/${encodeURIComponent(context.id)}/invite`
        : `/api/vehicle-market/items/${encodeURIComponent(context.id)}/invite`,
      {
        token: driverToken,
        method: "POST",
        body: isDriverDirectory
          ? { postId }
          : {
              postId,
              vehicleOwnerRole: context.ownerRole,
            },
      }
    );

    if (!response?.ok || !data?.ok) {
      throw new Error(data?.error || "VEHICLE_INVITE_FAILED");
    }

    const sent = Number(data?.sent ?? data?.delivery?.sent ?? 0);
    const accepted = data?.accepted === true || Boolean(data?.invite?.id);
    showToast(
      sent > 0 ? "ok" : accepted ? "ok" : "warn",
      sent > 0
        ? "Груз создан и отправлен выбранной машине."
        : accepted
          ? "Груз создан и сохранён водителю в REISOR. Push-канал сейчас недоступен."
          : "Груз создан. У выбранной машины сейчас нет активного push-канала.",
      { duration: 5000 }
    );
  } catch (error) {
    console.warn("[REISOR driver targeted vehicle invite]", error?.message || error);
    showToast("warn", "Груз создан, но адресная отправка машине не подтверждена. Повторите отправку из раздела «Машины».", { duration: 5500 });
  } finally {
    setTargetedVehicleFreight(null);
    setMyFreightsOpen(false);
  }
}

function driverAiFirstName(profile) {
  const explicit = String(profile?.firstName || "").trim();
  if (explicit) return explicit.split(/\s+/)[0];
  const legacy = String(profile?.name || profile?.fullName || "").trim();
  return legacy ? legacy.split(/\s+/)[0] : "";
}

// REISOR_DRIVER_AI_WELCOME_CADENCE_V2
// Do not interrupt a new driver on first launch. Show the welcome only on
// authenticated app sessions #4 and #7, once per session; never auto-open after that.
useEffect(() => {
  if (!tabsAllowed || driverAccessLimited || isRegisterFlow) return;

  const did = String(driver?.id || driver?._id || "anon").trim();
  const countKey = `reisor_driver_ai_launch_count_${did}`;
  const countedSessionKey = `reisor_driver_ai_launch_counted_${did}`;

  try {
    let count = Number.parseInt(localStorage.getItem(countKey) || "0", 10);
    if (!Number.isFinite(count) || count < 0) count = 0;

    const countedThisSession = sessionStorage.getItem(countedSessionKey) === "1";
    if (!countedThisSession) {
      count += 1;
      localStorage.setItem(countKey, String(count));
      sessionStorage.setItem(countedSessionKey, "1");
    }

    const shouldShow = count === 4 || count === 7;
    const shownKey = `reisor_driver_ai_welcome_shown_${did}_${count}`;
    const shownThisSession = sessionStorage.getItem(shownKey) === "1";

    if (shouldShow && !shownThisSession) {
      sessionStorage.setItem(shownKey, "1");
      setAiWelcomeOpen(true);
    }
  } catch {
    // Storage may be unavailable in hardened WebViews; fail closed (no popup).
  }
}, [tabsAllowed, driverAccessLimited, isRegisterFlow, driver?.id, driver?._id]);

const closeAiWelcomeForSession = () => {
  setAiWelcomeOpen(false);
};

// REISOR_STAGE163M5C2_DRIVER_MY_VEHICLES_PUSH_DEEP_LINK_V1
useEffect(() => {
  if (!tabsAllowed) return;
  const openMyVehiclesFromPush = () => {
    try { localStorage.removeItem("reisor_driver_open_my_vehicles"); } catch {}
    setAiWelcomeOpen(false);
    setAiOpen(false);
    setSettingsOpen(false);
    setAvailableVehicleOpen(false);
    setMyAdsOpen(false);
    setMyFreightsOpen(false);
    setMyVehiclesOpen(true);
  };
  const consumePending = () => {
    let fromUrl = false;
    try {
      const url = new URL(window.location.href);
      fromUrl = String(url.searchParams.get("openMyVehicles") || "").trim() === "1";
      if (fromUrl) {
        url.searchParams.delete("openMyVehicles");
        window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
      }
    } catch {}
    let fromStorage = false;
    try { fromStorage = String(localStorage.getItem("reisor_driver_open_my_vehicles") || "").trim() === "1"; } catch {}
    if (fromUrl || fromStorage) openMyVehiclesFromPush();
  };
  window.addEventListener("reisor_driver_open_my_vehicles", openMyVehiclesFromPush);
  consumePending();
  const retry = window.setTimeout(consumePending, 400);
  return () => {
    window.clearTimeout(retry);
    window.removeEventListener("reisor_driver_open_my_vehicles", openMyVehiclesFromPush);
  };
}, [tabsAllowed]);

// REISOR_STAGE161C1_DRIVER_MY_FREIGHTS_PUSH_DEEP_LINK_V1
useEffect(() => {
  if (!tabsAllowed) return;

  const openMyFreightsFromPush = () => {
    try { localStorage.removeItem("reisor_driver_open_my_freights"); } catch {}
    setAiOpen(false);
    setSettingsOpen(false);
    setAvailableVehicleOpen(false);
    setMyAdsOpen(false);
    setMyVehiclesOpen(false);
    setMyFreightsOpen(true);
  };

  const consumePending = () => {
    let fromUrl = false;
    try {
      const url = new URL(window.location.href);
      fromUrl = String(url.searchParams.get("openMyFreights") || "").trim() === "1";
      if (fromUrl) {
        url.searchParams.delete("openMyFreights");
        window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
      }
    } catch {}

    try {
      const fromStorage =
        String(localStorage.getItem("reisor_driver_open_my_freights") || "").trim() === "1";
      if (fromUrl || fromStorage) openMyFreightsFromPush();
    } catch {
      if (fromUrl) openMyFreightsFromPush();
    }
  };

  window.addEventListener("reisor_driver_open_my_freights", openMyFreightsFromPush);
  consumePending();
  const retry = window.setTimeout(consumePending, 400);

  return () => {
    window.clearTimeout(retry);
    window.removeEventListener("reisor_driver_open_my_freights", openMyFreightsFromPush);
  };
}, [tabsAllowed]);

// REISOR_STAGE97D4_DRIVER_SETTINGS_WORK_STATUS_INLINE_V1
const [settingsWorkStatusForm, setSettingsWorkStatusForm] = useState(() => driverWorkStatusSnapshot(driverObj || {}));
const [settingsWorkStatusLoading, setSettingsWorkStatusLoading] = useState(false);
const [settingsWorkStatusSaving, setSettingsWorkStatusSaving] = useState(false);
const [settingsWorkStatusError, setSettingsWorkStatusError] = useState("");

const openSettingsWorkStatus = () => {
  setSettingsWorkStatusForm(driverWorkStatusSnapshot(driverObj || {}));
  setSettingsWorkStatusError("");
  setSettingsTab("work_status");
};

const syncSettingsWorkStatusFromServer = async () => {
  if (!driverToken || !driver?.id) return;
  setSettingsWorkStatusLoading(true);
  setSettingsWorkStatusError("");
  try {
    const headers = {};
    const bearer = toBearer(driverToken);
    if (bearer) headers.Authorization = bearer;
    const res = await fetch(`/api/drivers/${encodeURIComponent(driver.id)}/profile`, {
      method: "GET",
      headers,
      credentials: "include",
      cache: "no-store",
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
    const profile = data?.driver || data?.profile || data || {};
    const snap = driverWorkStatusSnapshot(profile);
    setSettingsWorkStatusForm(snap);
    setDriverObj((prev) => ({ ...(prev || {}), ...snap }));
    mergeDriverLocalStoragePatch(snap);
  } catch (e) {
    setSettingsWorkStatusError(String(e?.message || e));
  } finally {
    setSettingsWorkStatusLoading(false);
  }
};

const saveSettingsWorkStatus = async () => {
  const snap = driverWorkStatusSnapshot(settingsWorkStatusForm);
  if (!snap.carrierStatus) {
    showToast("err", t("profile.alerts.carrierStatusRequired", { defaultValue: "Выберите статус работы" }));
    return;
  }
  if (driverWorkStatusNeedsInn(snap.carrierStatus) && !snap.inn) {
    showToast("err", t("profile.alerts.innRequired", { defaultValue: "Укажите ИНН" }));
    return;
  }
  if (driverWorkStatusNeedsName(snap.carrierStatus) && !snap.carrierName) {
    showToast("err", t("profile.alerts.carrierNameRequired", { defaultValue: "Укажите название компании, парка или логиста" }));
    return;
  }
  if (!driverToken || !driver?.id) {
    showToast("err", t("auth.no_token"));
    return;
  }

  setSettingsWorkStatusSaving(true);
  setSettingsWorkStatusError("");
  try {
    const data = await postJson(`/api/drivers/${encodeURIComponent(driver.id)}/work-status`, snap, driverToken);
    const saved = driverWorkStatusSnapshot({ ...snap, ...(data || {}) });
    setSettingsWorkStatusForm(saved);
    setDriverObj((prev) => ({ ...(prev || {}), ...saved }));
    mergeDriverLocalStoragePatch(saved);
    showToast("ok", t("profile.alerts.workStatusSaved", { defaultValue: "Статус работы сохранён" }));
    setSettingsTab("main");
  } catch (e) {
    const msg = String(e?.message || e);
    setSettingsWorkStatusError(msg);
    showToast("err", `${t("profile.alerts.workStatusSaveError", { defaultValue: "Не удалось сохранить статус работы" })}: ${msg}`);
  } finally {
    setSettingsWorkStatusSaving(false);
  }
};

useEffect(() => {
  if (settingsTab !== "work_status") return;
  setSettingsWorkStatusForm(driverWorkStatusSnapshot(driverObj || {}));
  syncSettingsWorkStatusFromServer().catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [settingsTab, driverToken, driver?.id]);

const [uiTheme, setUiTheme] = useState(() => {
  try { return localStorage.getItem("uiTheme") || "light"; } catch { return "light"; }
});
useEffect(() => {
  try {
    const theme =
      uiTheme === "dark"
        ? "dark"
        : "light";

    const root =
      document.documentElement;

    root.dataset.theme =
      theme;

    root.classList.toggle(
      "dark",
      theme === "dark"
    );

    root.style.colorScheme =
      theme;

    localStorage.setItem(
      "uiTheme",
      theme
    );

    window.dispatchEvent(
      new CustomEvent(
        "reisor-theme-change",
        {
          detail: theme,
        }
      )
    );
  } catch {}
}, [uiTheme]);

useEffect(() => {
  const onReisorThemeChange = (
    event
  ) => {
    const next =
      event?.detail;

    if (
      next !== "light" &&
      next !== "dark"
    ) {
      return;
    }

    setUiTheme(
      next
    );
  };

  const onThemeStorage = (
    event
  ) => {
    if (
      event?.key !== "uiTheme"
    ) {
      return;
    }

    if (
      event?.newValue !== "light" &&
      event?.newValue !== "dark"
    ) {
      return;
    }

    setUiTheme(
      event.newValue
    );
  };

  window.addEventListener(
    "reisor-theme-change",
    onReisorThemeChange
  );

  window.addEventListener(
    "storage",
    onThemeStorage
  );

  return () => {
    window.removeEventListener(
      "reisor-theme-change",
      onReisorThemeChange
    );

    window.removeEventListener(
      "storage",
      onThemeStorage
    );
  };
}, []);

useEffect(() => {
  if (!tabsAllowed || !driverToken) return;
  if (clientTrustPendingCheckedRef.current) return;
  clientTrustPendingCheckedRef.current = true;

  let cancelled = false;
  const loadPendingClientTrustReview = async () => {
    try {
      const headers = {};
      const bearer = toBearer(driverToken);
      if (bearer) headers.Authorization = bearer;

      const res = await fetch("/api/client-trust/driver/pending", {
        headers,
        credentials: "include",
        cache: "no-store",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.error) return;
      const item = Array.isArray(data?.items) ? data.items[0] : null;
      if (!cancelled && item) setClientTrustReviewOrder(item);
    } catch {}
  };

  loadPendingClientTrustReview();
  return () => { cancelled = true; };
}, [tabsAllowed, driverToken]);

// PUSH_UI_V1: Web Push (фоновые уведомления для TWA/PWA)
const pushSupported =
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;
const isNativeAndroidRuntime = (() => {
  try {
    return Capacitor?.isNativePlatform?.() === true && String(Capacitor?.getPlatform?.() || "").toLowerCase() === "android";
  } catch {
    return false;
  }
})();

// REISOR_DRIVER_BOTTOM_NAV_NATIVE_SAFE_AREA_V1
// REISOR_DRIVER_BOTTOM_NAV_LOWER_2MM_V1
// Keep the verified native safe-area rule, but lower the floating nav by ~8 CSS px (~2 mm)
// on runtimes where env(safe-area-inset-bottom) does not report a larger system inset.
const bottomNavOffset = isNativeAndroidRuntime
  ? "max(14px, calc(env(safe-area-inset-bottom, 0px) + 6px))"
  : 14;

const bottomNavBackdropHeight = isNativeAndroidRuntime
  ? "calc(72px + max(env(safe-area-inset-bottom, 0px), 20px) + 20px)"
  : 112;
const [pushPermission, setPushPermission] = useState(() =>
  pushSupported ? Notification.permission : "unsupported"
);
const [nativeNotificationPermission, setNativeNotificationPermission] = useState(() =>
  isNativeAndroidRuntime ? "checking" : "unsupported"
);
const [pushSubscribed, setPushSubscribed] = useState(false);
const [pushChannelStatus, setPushChannelStatus] = useState({
  loaded: false,
  ready: false,
  webSubscriptions: 0,
  nativeTokens: 0,
});
const [pushBusy, setPushBusy] = useState(false);
const [pushErr, setPushErr] = useState("");
const pushAutoPromptRef = useRef(false);

// PUSH_REQUIRED_V3:
// В Android APK Web Push/Service Worker может не создать подписку стабильно внутри WebView.
// Поэтому для нативного Android-релиза блокируем "На линии" только по системному Android-разрешению.
// Web Push всё равно пытаемся создать и синхронизировать в фоне, но не держим водителя в вечной блокировке.
const nativeNotificationsReady = !isNativeAndroidRuntime || nativeNotificationPermission === "granted";
const webPushReady = pushSupported && pushPermission === "granted" && pushSubscribed;
const pushRequiredReady = isNativeAndroidRuntime ? nativeNotificationsReady : webPushReady;
const driverCanWorkOnline = tabsAllowed && pushRequiredReady;

async function refreshNativeNotificationPermission() {
  if (!isNativeAndroidRuntime) return "unsupported";
  try {
    const res = await DriverNativeBridge.checkNotificationPermission();
    const status = res?.granted || res?.status === "granted" ? "granted" : "denied";
    setNativeNotificationPermission(status);
    return status;
  } catch {
    setNativeNotificationPermission("unsupported");
    return "unsupported";
  }
}

async function refreshPushChannelStatus() {
  if (!driverToken) {
    setPushChannelStatus({ loaded: true, ready: false, webSubscriptions: 0, nativeTokens: 0 });
    return null;
  }
  try {
    const res = await fetch("/api/drivers/push/status", {
      method: "GET",
      headers: { Authorization: toBearer(driverToken) },
      credentials: "include",
      cache: "no-store",
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.ok) throw new Error(data?.error || `HTTP ${res.status}`);
    const next = {
      loaded: true,
      ready: Boolean(data?.ready),
      webSubscriptions: Math.max(0, Number(data?.webSubscriptions || 0)),
      nativeTokens: Math.max(0, Number(data?.nativeTokens || 0)),
    };
    setPushChannelStatus(next);
    return next;
  } catch {
    setPushChannelStatus({ loaded: true, ready: false, webSubscriptions: 0, nativeTokens: 0 });
    return null;
  }
}
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
async function getSwReg() {
  if (!("serviceWorker" in navigator)) throw new Error("NO_SW");
  // 1) Сначала пробуем существующую регистрацию — это НЕ висит
  let reg = null;
  try {
    reg = await navigator.serviceWorker.getRegistration("/");
  } catch {}
  // 2) Если нет — регистрируем
  if (!reg) {
    try {
      reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
    } catch (e) {
      // не валим тут — ниже будет fallback
    }
  }
  if (reg) return reg;
  // 3) Fallback: ready, но с таймаутом (чтобы не зависать навсегда в TWA)
  const readyPromise = navigator.serviceWorker.ready;
  const timeoutPromise = new Promise((_, rej) =>
    setTimeout(() => rej(new Error("SW_READY_TIMEOUT")), 4000)
  );
  return await Promise.race([readyPromise, timeoutPromise]);
}
async function syncPushSubscription({ upsert = false } = {}) {
  if (!pushSupported) return;
  try { await refreshNativeNotificationPermission(); } catch {}
  try {
    setPushPermission(Notification.permission);
  } catch {}
  try {
    const reg = await getSwReg();
    const sub = await reg.pushManager.getSubscription();
    const has = !!sub;
    setPushSubscribed(has);
    if (upsert && has) {
      await postJson("/api/drivers/push/subscribe", { subscription: sub }, driverToken);
    }
    await refreshPushChannelStatus().catch(() => {});
  } catch (e) {
    setPushSubscribed(false);
    setPushErr(e?.message || String(e));
  }
}
// при старте: если разрешение уже выдано — синкаем подписку на бэк (для восстановления после апдейтов)
useEffect(() => {
  if (!pushSupported) return;
  if (Notification.permission === "granted") {
    syncPushSubscription({ upsert: true }).catch(() => {});
  } else {
    syncPushSubscription({ upsert: false }).catch(() => {});
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [driverToken]);

useEffect(() => {
  refreshPushChannelStatus().catch(() => {});
  const timer = window.setInterval(() => {
    refreshPushChannelStatus().catch(() => {});
  }, 30000);
  return () => window.clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [driverToken]);

useEffect(() => {
  const refresh = () => refreshPushChannelStatus().catch(() => {});
  window.addEventListener("reisor:push-channel-changed", refresh);
  return () => window.removeEventListener("reisor:push-channel-changed", refresh);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [driverToken]);

useEffect(() => {
  const refresh = () => {
    try { refreshNativeNotificationPermission().catch(() => {}); } catch {}
    try {
      if (pushSupported && Notification.permission === "granted") {
        syncPushSubscription({ upsert: true }).catch(() => {});
      } else if (pushSupported) {
        setPushPermission(Notification.permission);
      }
    } catch {}
  };

  refresh();

  let appResumeSub = null;
  try {
    Promise.resolve(CapApp.addListener("resume", refresh)).then((handle) => {
      appResumeSub = handle;
    }).catch(() => {});
  } catch {}

  try { window.addEventListener("focus", refresh); } catch {}
  const onVisibility = () => {
    try {
      if (document.visibilityState === "visible") refresh();
    } catch {}
  };
  try { document.addEventListener("visibilitychange", onVisibility); } catch {}

  return () => {
    try { window.removeEventListener("focus", refresh); } catch {}
    try { document.removeEventListener("visibilitychange", onVisibility); } catch {}
    try { appResumeSub?.remove?.(); } catch {}
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [driverToken, pushSupported]);

useEffect(() => {
  if (!pushSupported) return;
  if (!driverToken) return;
  if (pushBusy) return;
  if (pushAutoPromptRef.current) return;
  if (pushSubscribed && pushPermission === "granted") return;

  const ask = async () => {
    if (pushAutoPromptRef.current) return;
    pushAutoPromptRef.current = true;
    try {
      await enablePush();
    } catch {}
  };

  const onFirstGesture = () => {
    ask().catch(() => {});
  };

  window.addEventListener("pointerdown", onFirstGesture, { once: true, passive: true });
  window.addEventListener("touchstart", onFirstGesture, { once: true, passive: true });
  window.addEventListener("keydown", onFirstGesture, { once: true });

  return () => {
    try { window.removeEventListener("pointerdown", onFirstGesture); } catch {}
    try { window.removeEventListener("touchstart", onFirstGesture); } catch {}
    try { window.removeEventListener("keydown", onFirstGesture); } catch {}
  };
}, [driverToken, pushSupported, pushSubscribed, pushPermission, pushBusy]);

async function enablePush() {
  if (!pushSupported) {
    showToast("err", t("push.not_supported"));
    return false;
  }
  if (!driverToken) {
    showToast("err", t("auth.no_token"));
    return false;
  }
  setPushBusy(true);
  setPushErr("");
  try {
    // 0) native Android permission. Without it app notifications can be enabled in WebView but blocked by OS.
    const nativeStatus = await refreshNativeNotificationPermission();
    if (isNativeAndroidRuntime && nativeStatus !== "granted") {
      showToast(
        "err",
        t("driverPush.allowInSettings"),
        { duration: 0, actionText: t("driverPush.openSettings"), onAction: () => openNotificationSettings().catch(() => {}) }
      );
      return false;
    }

    // 1) permission
    if (Notification.permission !== "granted") {
      const perm = await Notification.requestPermission();
      setPushPermission(perm);
      if (perm !== "granted") {
        showToast("err", t("push.need_permission"));
        return false;
      }
    }
    // 2) get VAPID key
    const pkRes = await fetch("/api/drivers/push/public-key", { cache: "no-store", credentials: "include" });
    const pkJson = await pkRes.json().catch(() => ({}));
    if (!pkRes.ok || !pkJson?.key) {
      throw new Error(pkJson?.error || "NO_VAPID_KEY");
    }
    const appKey = urlBase64ToUint8Array(String(pkJson.key));
    // 3) subscribe
    const reg = await getSwReg();
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: appKey,
      });
    }
    // 4) save on backend
    await postJson("/api/drivers/push/subscribe", { subscription: sub }, driverToken);
    setPushSubscribed(true);
    showToast("ok", t("push.enabled"));
    return true;
  } catch (e) {
    setPushErr(e?.message || String(e));
    showToast("err", `${t("push.enable_failed")}: ${cleanErr(e)}`);
    return false;
  } finally {
    setPushBusy(false);
  }
}
async function disablePush() {
  if (!pushSupported) return;
  if (!driverToken) {
    showToast("err", t("auth.no_token"));
    return;
  }
  setPushBusy(true);
  setPushErr("");
  try {
    const reg = await getSwReg();
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      try {
        await postJson("/api/drivers/push/unsubscribe", { endpoint: sub.endpoint }, driverToken);
      } catch {}
      await sub.unsubscribe();
    }
    setPushSubscribed(false);
    showToast("ok", t("push.disabled"));
  } catch (e) {
    setPushErr(e?.message || String(e));
    showToast("err", `${t("push.disable_failed")}: ${cleanErr(e)}`);
  } finally {
    setPushBusy(false);
  }
}
async function testPush() {
  if (!driverToken) {
    showToast("err", t("auth.no_token"));
    return;
  }
  try {
    const result = await postJson("/api/drivers/push/test", {}, driverToken);
    const sent = Math.max(0, Number(result?.sent || 0));
    await refreshPushChannelStatus().catch(() => {});
    if (sent > 0) {
      showToast("ok", t("push.test_sent"));
      return;
    }
    showToast("err", t("push.test_no_channel"));
  } catch (e) {
    showToast("err", cleanErr(e));
  }
}

async function openNotificationSettings() {
  if (isNativeAndroidRuntime) {
    const nativeStatus = await refreshNativeNotificationPermission();
    if (nativeStatus !== "granted") {
      try {
        await DriverNativeBridge.openNotificationSettings();
      } catch {
        try {
          window.location.href = "app-settings:";
        } catch {}
      }

      showToast(
        "err",
        t("driverPush.enableInSettings"),
        { duration: 0, actionText: t("driverPush.understood"), onAction: () => {} }
      );
      return;
    }

    // Android-разрешение уже включено. Не блокируем водителя из-за Web Push,
    // потому что в Capacitor WebView подписка может создаваться не сразу.
    // Пытаемся создать/синхронизировать её в фоне.
    setPushPermission("granted");
    setPushSubscribed(true);
    showToast("ok", t("driverPush.androidEnabled"));
    setTimeout(() => {
      enablePush().then(() => syncPushSubscription({ upsert: true })).catch(() => {});
    }, 250);
    return;
  }

  const granted = await enablePush();
  if (granted) {
    await syncPushSubscription({ upsert: true }).catch(() => {});
    return;
  }

  const denied = pushSupported && Notification.permission === "denied";
  showToast(
    "err",
    denied
      ? t("driverPush.blocked")
      : t("driverPush.allowSystem"),
    { duration: 0, actionText: t("driverPush.understood"), onAction: () => {} }
  );
}

function showPushRequiredWarning() {
  showToast(
    "err",
    t("driverPush.disabledLong"),
    {
      duration: 0,
      actionText: t("driverPush.openNotificationSettings"),
      onAction: () => { openNotificationSettings().catch(() => {}); },
    }
  );
}
// FORCE_UPDATE_V1: разово очищаем SW/CacheStorage, чтобы TWA не держал старые ассеты
useEffect(() => {
  const key = "driver_force_update_done_v1";
  try {
    if (localStorage.getItem(key) === "1") return;
    localStorage.setItem(key, "1");
  } catch {}
  (async () => {
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch {}
    try {
      if (window.caches?.keys) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch {}
    try {
      setTimeout(() => location.reload(), 250);
    } catch {}
  })();
}, []);
const [soundCfg, setSoundCfg] = useState(() => {
  const saved = loadSoundSettings() || {};
  return {
    enabled: saved.enabled !== false,
    volume: typeof saved.volume === "number" ? saved.volume : 0.9,
    vibrate: saved.vibrate !== false,
    newOrder: saved.newOrder || "reisor_order",
    assignedOrder: saved.assignedOrder || "reisor_assign",
    chat: saved.chat || "reisor_chat",
  };
});
useEffect(() => {
  saveSoundSettings(soundCfg);
}, [soundCfg]);

useEffect(() => {
  const key = "driver_force_reisor_sounds_v107";
  try {
    if (localStorage.getItem(key) === "1") return;
    localStorage.setItem(key, "1");
  } catch {}

  setSoundCfg((v) => ({
    ...v,
    enabled: true,
    newOrder: "reisor_order",
    assignedOrder: "reisor_assign",
    chat: "reisor_chat",
  }));
}, []);
// ---- SOUND runtime (driver) ----
// Важно: playEventSound/handleUploadSound используются UI "Управление звуками" и поллингом.
// Chrome/TWA блокирует аудио до первого жеста пользователя — поэтому делаем priming.
const __soundCfgRef = useRef(soundCfg);
useEffect(() => { __soundCfgRef.current = soundCfg; }, [soundCfg]);
const __primeAudioOnceRef = useRef(false);
useEffect(() => {
  const prime = () => {
    if (__primeAudioOnceRef.current) return;
    __primeAudioOnceRef.current = true;
    try {
      // если в файле есть getAudioCtx() — разбудим AudioContext
      if (typeof getAudioCtx === "function") {
        const ctx = getAudioCtx();
        if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
      }
    } catch {}
  };
  window.addEventListener("pointerdown", prime, { passive: true });
  window.addEventListener("touchstart", prime, { passive: true });
  window.addEventListener("keydown", prime);
  return () => {
    try { window.removeEventListener("pointerdown", prime); } catch {}
    try { window.removeEventListener("touchstart", prime); } catch {}
    try { window.removeEventListener("keydown", prime); } catch {}
  };
}, []);
const __toast = (type, msg) => {
  try {
    if (typeof showToast === "function") showToast(type, msg);
    else console.log(`[${type}]`, msg);
  } catch {}
};
const __loadCustom = (eventKey) => {
  try {
    if (typeof loadCustomSound === "function") return loadCustomSound(eventKey);
    return window.localStorage.getItem(`driver_custom_sound_${eventKey}`) || "";
  } catch { return ""; }
};
const __saveCustom = (eventKey, dataUrl) => {
  try {
    if (typeof saveCustomSound === "function") return saveCustomSound(eventKey, dataUrl);
    window.localStorage.setItem(`driver_custom_sound_${eventKey}`, dataUrl);
  } catch {}
};
const __playDataUrl = (dataUrl, volume, opts = {}) => {
  const preview = !!opts.preview;
  const previewKey = String(opts.previewKey || "");
  try {
    if (!dataUrl) return;
    if (preview) __stopPreviewSound();
    const a = new Audio(dataUrl);
    a.volume = Math.max(0, Math.min(1, Number(volume ?? 0.8)));
    if (preview) {
      __previewAudioRef.current = a;
      __previewCtxRef.current = null;
      __previewPlayingKeyRef.current = previewKey;
      setPreviewPlayingKey(previewKey);
      a.onended = () => {
        if (__previewAudioRef.current === a) __stopPreviewSound();
      };
    }
    a.play().catch(() => {
      if (preview && __previewAudioRef.current === a) __stopPreviewSound();
    });
  } catch {}
};
const __playPreset = (presetId, volume, opts = {}) => {
  try {
    if (
      typeof playWebAudioPreset === "function" &&
      !["reisor_order", "reisor_assign", "reisor_chat"].includes(String(presetId || ""))
    ) {
      return playWebAudioPreset(presetId, volume);
    }

    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const vol = Math.max(0, Math.min(1, Number(volume ?? 0.8)));
    const preview = !!opts.preview;
    const previewKey = String(opts.previewKey || "");
    if (preview) {
      __stopPreviewSound();
      __previewCtxRef.current = ctx;
      __previewAudioRef.current = null;
      __previewPlayingKeyRef.current = previewKey;
      setPreviewPlayingKey(previewKey);
      setTimeout(() => {
        if (__previewCtxRef.current === ctx && !__previewAudioRef.current) {
          __stopPreviewSound();
        }
      }, 1600);
    }

    const beep = (freq, startAt, dur, gain = 1, type = "sine") => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = type;
      o.frequency.setValueAtTime(freq, ctx.currentTime + startAt);
      g.gain.setValueAtTime(0.0001, ctx.currentTime + startAt);
      g.gain.exponentialRampToValueAtTime(Math.max(0.0001, vol * 0.18 * gain), ctx.currentTime + startAt + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + startAt + dur);
      o.connect(g);
      g.connect(ctx.destination);
      o.start(ctx.currentTime + startAt);
      o.stop(ctx.currentTime + startAt + dur + 0.03);
    };

    const playBundledPreset = (fileName) => {
      try {
        const src = (() => {
          try {
            return new URL(`/sounds/${fileName}`, window.location.origin).toString();
          } catch {
            return `/sounds/${fileName}`;
          }
        })();
        const a = new Audio(src);
        a.preload = "auto";
        a.volume = Math.max(0, Math.min(1, Number(vol || 0.8)));
        if (preview) {
          __previewAudioRef.current = a;
          __previewCtxRef.current = null;
          setPreviewPlayingKey(previewKey);
          a.onended = () => {
            if (__previewAudioRef.current === a) __stopPreviewSound();
          };
        }
        a.play().catch(() => {
          if (preview && __previewAudioRef.current === a) __stopPreviewSound();
        });
        setTimeout(() => { try { ctx.close().catch(() => {}); } catch {} }, 120);
        return true;
      } catch {
        return false;
      }
    };
    if (presetId === "reisor_order") {
      if (playBundledPreset("reisor_order.mp3")) return;
      beep(660, 0.00, 0.11, 1.0, "triangle");
      beep(880, 0.12, 0.13, 1.0, "triangle");
      beep(1175, 0.27, 0.18, 1.0, "triangle");
      setTimeout(() => { try { ctx.close().catch(() => {}); } catch {} }, 800);
      return;
    }
    if (presetId === "reisor_assign") {
      if (playBundledPreset("reisor_assign.mp3")) return;
      beep(740, 0.00, 0.10, 1.0, "sine");
      beep(988, 0.10, 0.12, 1.0, "sine");
      setTimeout(() => { try { ctx.close().catch(() => {}); } catch {} }, 500);
      return;
    }
    if (presetId === "reisor_chat") {
      if (playBundledPreset("reisor_chat.mp3")) return;
      beep(988, 0.00, 0.07, 0.9, "triangle");
      beep(1318, 0.08, 0.09, 0.8, "triangle");
      setTimeout(() => { try { ctx.close().catch(() => {}); } catch {} }, 400);
      return;
    }

    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.value = presetId === "chat" ? 880 : 660;
    g.gain.value = vol * 0.15;
    o.connect(g); g.connect(ctx.destination);
    o.start();
    setTimeout(() => { try { o.stop(); ctx.close().catch(() => {}); } catch {} }, 140);
  } catch {}
};
const __vibrate = (eventKey) => {
  try {
    if (!navigator.vibrate) return;
    const patterns = {
      newOrder: [120, 60, 120],
      assignedOrder: [90, 40, 90],
      chat: [50],
    };
    navigator.vibrate(patterns[eventKey] || [50]);
  } catch {}
};
// === FIX: these functions were called but not defined ===
const playEventSound = (eventKey, { force = false, preview = false } = {}) => {
  try {
    const cfg = __soundCfgRef.current || {};
    if (!force && cfg.enabled === false) return;
    if (preview && __previewPlayingKeyRef.current === eventKey) {
      __stopPreviewSound();
      return;
    }
    const volume = typeof cfg.volume === "number" ? cfg.volume : 0.8;
    const presetId = String(cfg[eventKey] || "off");
    if (cfg.vibrate !== false) __vibrate(eventKey);
    if (presetId === "off") return;
    if (presetId === "custom") {
      const dataUrl = __loadCustom(eventKey);
      if (dataUrl) __playDataUrl(dataUrl, volume, { preview, previewKey: eventKey });
      else __playPreset("ding", volume, { preview, previewKey: eventKey });
      return;
    }
    __playPreset(presetId, volume, { preview, previewKey: eventKey });
  } catch {}
};
const handleUploadSound = (eventKey, file) => {
  try {
    if (!file) return;
    const maxBytes = 900 * 1024;
    if (file.size > maxBytes) {
      try { if (typeof showToast === "function") showToast("err", t("sound.file_too_big")); } catch {}
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const dataUrl = String(reader.result || "");
        if (!dataUrl.startsWith("data:audio/")) {
          try { if (typeof showToast === "function") showToast("err", t("sound.not_audio")); } catch {}
          return;
        }
        try {
          if (typeof saveCustomSound === "function") saveCustomSound(eventKey, dataUrl);
          else window.localStorage.setItem(`driver_custom_sound_${eventKey}`, dataUrl);
        } catch {}
        setSoundCfg((v) => ({ ...v, [eventKey]: "custom" }));
        try { if (typeof showToast === "function") showToast("ok", t("sound.saved")); } catch {}
        // immediate test (user gesture context)
        try {
          const vol = typeof (soundCfgRef?.current?.volume) === "number" ? soundCfgRef.current.volume : 0.8;
          if (typeof playCustomDataUrl === "function") playCustomDataUrl(dataUrl, vol);
          else {
            const a = new Audio(dataUrl);
            a.volume = Math.max(0, Math.min(1, Number(vol)));
            a.play().catch(() => {});
          }
        } catch {}
      } catch {
        try { if (typeof showToast === "function") showToast("err", t("sound.save_failed")); } catch {}
      }
    };
    reader.onerror = () => {
      try { if (typeof showToast === "function") showToast("err", t("sound.read_failed")); } catch {}
    };
    reader.readAsDataURL(file);
  } catch {}
};

const __previewAudioRef = useRef(null);
const __previewCtxRef = useRef(null);
const [previewPlayingKey, setPreviewPlayingKey] = useState("");
const __previewPlayingKeyRef = useRef("");
const __stopPreviewSound = () => {
  try {
    const a = __previewAudioRef.current;
    if (a) {
      try { a.pause(); } catch {}
      try { a.currentTime = 0; } catch {}
    }
  } catch {}
  try {
    const ctx = __previewCtxRef.current;
    if (ctx && typeof ctx.close === "function") {
      try { ctx.close().catch(() => {}); } catch {}
    }
  } catch {}
  __previewAudioRef.current = null;
  __previewCtxRef.current = null;
  __previewPlayingKeyRef.current = "";
  setPreviewPlayingKey("");
};

const primeAudioRef = useRef(false);
const prevRequestIdsRef = useRef(null);
const prevAssignedIdsRef = useRef(null);
const watchOrdersRef = useRef([]); // assigned trips for background chat notify
const lastChatByOrderRef = useRef({}); // orderId -> lastMessageId
  const lastChatSigRef = useRef(""); // chat dedupe signature (fix ReferenceError)
useEffect(() => {
  const prime = () => {
    if (primeAudioRef.current) return;
    primeAudioRef.current = true;
  };
}, []);
  // ---- ORDERS (pool) ----
// SOUND_POLL_NEW_ORDERS_V1: следим за новыми заказами в фоне и играем звук
useEffect(() => {
  if (!tabsAllowed) return;
  let cancelled = false;
  const tick = async () => {
    try {
      const headers = {};
      if (driverToken) headers.Authorization = toBearer(driverToken);
      const res = await fetch("/api/driver/orders", { headers, credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return;
      const list = extractList(data)
        .map((o) => ({ ...o, id: orderIdOf(o) || o.id }))
        .filter((o) => {
          const st = canonStatus(o);
          return st === "NEW" || st === "SEARCH_DRIVER";
        });
      const nextIds = new Set(list.map((o) => String(o.id)));
      if (prevRequestIdsRef.current) {
        let hasNew = false;
        for (const id of nextIds) {
          if (!prevRequestIdsRef.current.has(id)) { hasNew = true; break; }
        }
        // (ТЗ) автозвук "новый заказ" не нужен
        // if (hasNew) playEventSound("newOrder");
      }
      prevRequestIdsRef.current = nextIds;
      if (!cancelled && mainTab === "requests") setOrders(list);
    } catch {}
  };
  tick();
  const t = setInterval(tick, 20000);
  return () => {
    cancelled = true;
    clearInterval(t);
  };
}, [tabsAllowed, driverToken, mainTab]);
  const loadOrders = async () => {
    if (!tabsAllowed || mainTab !== "requests") return;
    setOrdersLoading(true);
    setOrdersError("");
    try {
      const headers = {};
      if (driverToken) headers.Authorization = toBearer(driverToken);
      const res = await fetch("/api/driver/orders", { headers, credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
      const list = extractList(data)
        .map((o) => ({ ...o, id: orderIdOf(o) || o.id }))
        .filter((o) => {
          const st = canonStatus(o);
          return st === "NEW" || st === "SEARCH_DRIVER";
        });
      setOrders(list);
    } catch (e) {
      setOrdersError(cleanErr(e));
    } finally {
      setOrdersLoading(false);
    }
  };
  useEffect(() => {
    if (!tabsAllowed || mainTab !== "requests") return;
    // список обновляется фоновым polling-ом (SOUND_POLL_NEW_ORDERS_V1)
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabsAllowed, mainTab]);// ---- OFFERS cache (per-driver) + skipped orders + requests filters ----
const offersStorageKey = useMemo(() => `driver_offers_${String(driver?.id || "anon")}`, [driver?.id]);
const [offers, setOffers] = useState(() => {
  // migrate from legacy key if exists
  const raw = localStorage.getItem(offersStorageKey) || localStorage.getItem("driver_offers");
  return raw ? safeJson(raw) || {} : {};
});
useEffect(() => {
  // reload when driver changes
  try {
    const raw = localStorage.getItem(offersStorageKey) || localStorage.getItem("driver_offers");
    setOffers(raw ? safeJson(raw) || {} : {});
  } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [offersStorageKey]);
useEffect(() => {
  try { localStorage.setItem(offersStorageKey, JSON.stringify(offers || {})); } catch {}
}, [offersStorageKey, offers]);

const skippedStorageKey = useMemo(() => `driver_skipped_${String(driver?.id || "anon")}`, [driver?.id]);
const [skippedOrders, setSkippedOrders] = useState(() => {
  const raw = localStorage.getItem(skippedStorageKey);
  return raw ? safeJson(raw) || {} : {};
});
useEffect(() => {
  // reload when driver changes
  try {
    const raw = localStorage.getItem(skippedStorageKey);
    setSkippedOrders(raw ? safeJson(raw) || {} : {});
  } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [skippedStorageKey]);
useEffect(() => {
  try { localStorage.setItem(skippedStorageKey, JSON.stringify(skippedOrders || {})); } catch {}
}, [skippedStorageKey, skippedOrders]);

const skipOrderId = (oid) => {
  const id = String(oid || "").trim();
  if (!id) return;
  setSkippedOrders((prev) => ({ ...(prev || {}), [id]: Date.now() }));
};
const unskipOrderId = (oid) => {
  const id = String(oid || "").trim();
  if (!id) return;
  setSkippedOrders((prev) => {
    const src = prev || {};
    if (!src[id]) return src;
    const next = { ...src };
    delete next[id];
    return next;
  });
};
const isSkippedOrderId = (oid) => {
  const id = String(oid || "").trim();
  return !!(id && (skippedOrders || {})[id]);
};
const isSkippedOrder = (order) => isSkippedOrderId(orderIdOf(order));

const [requestsSort, setRequestsSort] = useState(() => {
  try { return localStorage.getItem("driver_requests_sort") || "newest"; } catch { return "newest"; }
});
useEffect(() => {
  try { localStorage.setItem("driver_requests_sort", String(requestsSort || "newest")); } catch {}
}, [requestsSort]);

const [requestsShowSkipped, setRequestsShowSkipped] = useState(() => {
  try { return localStorage.getItem("driver_requests_show_skipped") === "1"; } catch { return false; }
});
  const [requestsFiltersOpen, setRequestsFiltersOpen] = useState(false);

  useEffect(() => {
    // close filter panel when switching tabs
    setRequestsFiltersOpen(false);
  }, [requestsTab]);

useEffect(() => {
  try { localStorage.setItem("driver_requests_show_skipped", requestsShowSkipped ? "1" : "0"); } catch {}
}, [requestsShowSkipped]);

const isExpiredMs = (ms) => {
  const n = Number(ms || 0);
  return Number.isFinite(n) && n > 0 && n <= Date.now();
};
const bidExpiresAtMs = (b) => {
  const ex = b?.expiresAt || b?.expires_at || null;
  if (!ex) return null;
  const ms = new Date(ex).getTime();
  return Number.isFinite(ms) ? ms : null;
};
const isBidExpired = (b) => {
  const ms = bidExpiresAtMs(b);
  return ms != null && ms <= Date.now();
};

// REISOR_DRIVER_ACTIVE_BID_CANCEL_GUARD_V2
// REISOR_STAGE22P2C6_DRIVER_MY_OFFERS_FEED_HOTFIX_V1
const currentDriverIdForOffers = () =>
  String(
    driver?.id ||
    driverObj?.id ||
    driverObj?._id ||
    driverObj?.driverId ||
    driverObj?.driver_id ||
    ""
  ).trim();

const bidDriverIdForOffers = (bid) =>
  String(
    bid?.driverId ||
    bid?.driver ||
    bid?.driver_id ||
    bid?.driverObjectId ||
    bid?.driverObjectID ||
    ""
  ).trim();

const isServerMyOfferActive = (order) => {
  if (!order || typeof order !== "object") return false;

  const flag = order?.hasMyOffer ?? order?.has_my_offer;
  const state = String(order?.driverBidState || order?.driver_bid_state || "").trim().toLowerCase();

  if (flag === true && (!state || state === "active")) return true;
  if (state === "active") return true;

  const mb = order?.myBid || order?.my_bid || null;
  if (mb && typeof mb === "object") {
    const st = String(mb?.status || "active").toLowerCase();
    if (st === "active" && !mb?.chosen && !isBidExpired(mb)) return true;
  }

  return false;
};

const myRawBidFromServer = (order) => {
  const direct = order?.myBid || order?.my_bid || null;
  if (direct && typeof direct === "object") return direct;

  const did = currentDriverIdForOffers();
  const bids = Array.isArray(order?.bids) ? order.bids : [];
  return bids.find((x) => bidDriverIdForOffers(x) === did) || null;
};

const myBidFromServer = (order) => {
  const direct = order?.myBid || order?.my_bid || null;
  const b = (direct && typeof direct === "object") ? direct : myRawBidFromServer(order);
  if (!b) return null;

  const st = String(b?.status || "active").toLowerCase();

  // Отменять можно только живое предложение.
  // assigned/chosen уже не предложение, а назначенная поездка.
  if (st && st !== "active") return null;
  if (b?.chosen) return null;
  if (isBidExpired(b)) return null;

  return b;
};

const hasMyOffer = (order) => {
  const oid = orderIdOf(order);

  if (isServerMyOfferActive(order)) return true;

  const rawServerBid = myRawBidFromServer(order);
  const rawStatus = String(rawServerBid?.status || "").toLowerCase();

  if (rawServerBid && rawStatus && rawStatus !== "active") return false;
  if (rawServerBid?.chosen) return false;

  const local = offers?.[oid];
  if (local && local.state !== "cancelled") {
    if (isExpiredMs(local.expiresAt)) return false;
    return true;
  }

  return !!myBidFromServer(order);
};

// AUTO: prune expired local offers periodically (so "New" gets them back)
useEffect(() => {
  const t = setInterval(() => {
    setOffers((prev) => {
      const now = Date.now();
      const src = prev || {};
      let changed = false;
      const next = { ...src };
      for (const oid of Object.keys(next)) {
        const off = next[oid];
        const ex = Number(off?.expiresAt || 0);
        if (off && off.state !== "cancelled" && Number.isFinite(ex) && ex > 0 && ex <= now) {
          delete next[oid];
          changed = true;
        }
      }
      return changed ? next : src;
    });
  }, 30000);
  return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [offersStorageKey]);
  const getInitialPrice = (order) => {
    const pm = priceModeOf(order);
    const fixedBase = basePriceAmountOf(order) ?? basePriceRubOf(order) ?? fixedOrderPriceOf(order);
    if (isFixedPriceMode(pm) && fixedBase != null) return fixedBase;

    const oid = orderIdOf(order);
    if (offers?.[oid]?.price != null) return offers[oid].price;
    const srv = myBidFromServer(order);
    if (srv?.bidAmount != null) return srv.bidAmount;
    return fixedBase ?? "";
  };
  // ---- Inline bid form (no modal) ----
  const [bidOpenId, setBidOpenId] = useState(null);
  const toggleBid = (orderId) => {
    const id = String(orderId || "").trim();
    if (!id) return;
    setBidOpenId((prev) => (prev === id ? null : id));
  };
const handleSubmitBid = async (orderId, amount, expiration, extra = {}) => {
    const oid = String(orderId || "").trim();
    if (!tabsAllowed || !oid) return;
    if (driverAccessLimited) {
      showToast("err", driverAccessLimitedMessage);
      return;
    }
    try {
      const result = await placeBid(oid, {
        driverId: driver.id,
        driverName: driver.name,
        amount,
        bidAmount: amount,
        expiration,
        loadType: extra?.loadType,
        payment: extra?.payment || null,
        currency: extra?.currency || null,
        bidCurrency: extra?.currency || null,
      });
      const expiresAtMs = result?.bid?.expiresAt
        ? new Date(result.bid.expiresAt).getTime()
        : Date.now() + 3600 * 1000;
      setOffers((prev) => ({
        ...(prev || {}),
        [oid]: {
          price: amount,
          period: expiration,
          loadType: extra?.loadType || null,
          payment: extra?.payment || null,
        currency: extra?.currency || null,
        bidCurrency: extra?.currency || null,
          expiresAt: expiresAtMs,
          state: "active",
        },
      }));

      // REISOR_STAGE22P2C6_DRIVER_MY_OFFERS_FEED_HOTFIX_V1:
      // Move the card immediately from "Заявки" to "Мои предложения" for this driver.
      const optimisticBid = {
        ...(result?.bid && typeof result.bid === "object" ? result.bid : {}),
        driverId: driver.id,
        driverName: driver.name,
        bidAmount: amount,
        amount,
        currency: extra?.currency || result?.bid?.currency || result?.bid?.bidCurrency || "RUB",
        bidCurrency: extra?.currency || result?.bid?.bidCurrency || result?.bid?.currency || "RUB",
        status: "active",
        expiresAt: result?.bid?.expiresAt || new Date(expiresAtMs).toISOString(),
      };
      setOrders((prev) => (prev || []).map((o) => (
        String(orderIdOf(o) || o?.id || o?._id || "") === oid
          ? { ...o, hasMyOffer: true, driverBidState: "active", myBid: optimisticBid }
          : o
      )));
      setRouteMapOpenByOrder((previous) => {
        if (!Object.prototype.hasOwnProperty.call(previous, oid)) return previous;
        const next = { ...previous };
        delete next[oid];
        return next;
      });
      setRequestsTab("my_offers");

      showToast("ok", t("driverToast.bidSent"));
      await loadOrders();
      // закрываем inline-панель только при успехе
      setBidOpenId(null);
    } catch (e) {
      const errorCode = String(
        e?.code ||
        e?.data?.code ||
        e?.data?.error ||
        ""
      ).trim().toUpperCase();

      if (errorCode === "DRIVER_ACCESS_REQUIRED") {
        const accessReason = String(
          e?.data?.driverAccess?.reason || ""
        ).trim().toLowerCase();

        const pendingStates = new Set([
          "pending",
          "processing",
          "authorized",
          "self_declared_paid",
        ]);

        let paymentPending = pendingStates.has(
          String(
            e?.data?.driverAccess?.paymentStatus || ""
          ).trim().toLowerCase()
        );

        if (!paymentPending && driverToken) {
          try {
            const headers = {};
            const bearer = toBearer(driverToken);

            if (bearer) {
              headers.Authorization = bearer;
            }

            const response = await fetch(
              "/api/driver/subscription/status",
              {
                method: "GET",
                headers,
                credentials: "include",
                cache: "no-store",
              }
            );

            const statusData =
              await response
                .json()
                .catch(() => ({}));

            const payments =
              Array.isArray(statusData?.lastPayments)
                ? statusData.lastPayments
                : [];

            paymentPending = payments.some(
              (payment) => {
                const status = String(
                  payment?.status || ""
                ).trim().toLowerCase();

                const paymentStatus = String(
                  payment?.paymentStatus || ""
                ).trim().toLowerCase();

                return (
                  pendingStates.has(status) ||
                  pendingStates.has(paymentStatus)
                );
              }
            );
          } catch {}
        }

        let message;

        if (paymentPending) {
          message = t(
            "driverSubscription.bidPaymentPending",
            {
              defaultValue:
                "Платёж за подписку уже обрабатывается. После подтверждения доступ к заказам восстановится.",
            }
          );
        } else if (
          accessReason === "trial_first_order_used"
        ) {
          message = t(
            "driverSubscription.bidTrialUsed",
            {
              defaultValue:
                "Заказы временно недоступны. Бесплатный первый заказ уже использован. Оформите подписку на 30 дней — после активации сможете предлагать цену и брать заказы. Количество заказов со стороны подписки не ограничено.",
            }
          );
        } else {
          message = t(
            "driverSubscription.bidAccessRequired",
            {
              defaultValue:
                "Заказы временно недоступны. Активной подписки нет. Оформите подписку на 30 дней — после активации сможете предлагать цену и брать заказы. Количество заказов со стороны подписки не ограничено.",
            }
          );
        }

        showToast("err", message, {
          duration: 0,
          actionText: paymentPending
            ? t(
                "driverSubscription.openSubscription",
                {
                  defaultValue:
                    "Открыть подписку",
                }
              )
            : t(
                "driverSubscription.subscribe30Days",
                {
                  defaultValue:
                    "Оформить подписку",
                }
              ),
          onAction: () => {
            setBidOpenId(null);
            setMainTab("balance");
          },
        });

        return;
      }

      showToast("err", cleanErr(e));
    }
  };
// ✅ Cancel offer (robust: tries both endpoints)
    const handleCancelOffer = async (orderId) => {
    if (!tabsAllowed) return;
    const oid = String(orderId || "").trim();
    if (!oid) return;
    try {
      // primary (canon)
      await cancelBid(oid, {});
      setOffers((prev) => {
        const next = { ...(prev || {}) };
        delete next[oid];
        return next;
      });
      showToast("ok", t("driverToast.bidCanceled"));
      await loadOrders();
      return;
    } catch (e1) {
      const m1 = String(e1?.message || "");
      // если сервер говорит bid_not_found — считаем, что предложение уже не активно
      if (m1.includes("bid_not_found") || m1.includes("not_found") || m1.includes("inactive") || m1.includes("not active")) {
        setOffers((prev) => {
          const next = { ...(prev || {}) };
          delete next[oid];
          return next;
        });
        showToast("ok", t("driverToast.bidInactive"));
        await loadOrders();
        return;
      }
      // fallback alias
      try {
        await cancelBid(oid, {}); // backend accepts /bids/cancel
        setOffers((prev) => {
          const next = { ...(prev || {}) };
          delete next[oid];
          return next;
        });
        showToast("ok", t("driverToast.bidCanceled"));
        await loadOrders();
        return;
      } catch (e2) {
        showToast("err", cleanErr(e2?.message ? e2 : e1));
      }
    }
  };
  // ---- MY TRIPS ----
  // DEEP_SCROLL_V1: scroll to focused order when list loaded
  useEffect(() => {
    const id = String(deepFocusId || "").trim();
    if (!id) return;
    if (mainTab !== "requests") return;
    const t = setTimeout(() => {
      const el = document.getElementById(`order-${id}`);
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 250);
    return () => clearTimeout(t);
  }, [deepFocusId, mainTab, requestsTab, orders, myOrders]);
  // REISOR_MATCH_STAGE51_MARKET_FREIGHT_DEEP_LINK_V1
  useEffect(() => {
    if (!tabsAllowed) return undefined;

    const openMarketFreight = (freightId) => {
      const fid = String(freightId || "").trim();
      if (!fid) return;

      try { localStorage.setItem("deep_market_freight_id", fid); } catch {}
      setDeepMarketFreightId(fid);
      setSettingsOpen(false);
      setSettingsTab("main");
      setChatOpen(false);
      setTripDetails(null);
      setBidOpenId(null);
      setMainTab("requests");
      setRequestsSource("market");
    };

    const consumePending = () => {
      try {
        const fid = String(localStorage.getItem("deep_market_freight_id") || "").trim();
        if (fid) openMarketFreight(fid);
      } catch {}
    };

    const onOpenMarketFreight = (event) => {
      openMarketFreight(event?.detail?.freightId);
    };

    window.addEventListener("reisor_driver_open_market_freight", onOpenMarketFreight);
    consumePending();
    const retry = window.setTimeout(consumePending, 400);

    return () => {
      window.clearTimeout(retry);
      window.removeEventListener("reisor_driver_open_market_freight", onOpenMarketFreight);
    };
  }, [tabsAllowed]);

  useEffect(() => {
    const onOpenOrder = (event) => {
      try {
        const oid = String(event?.detail?.orderId || "").trim();
        // REISOR_DRIVER_OPEN_ORDER_EVENT_RESET_V2
        if (oid) {
          deepFocusHandledRef.current = false;
          try { localStorage.setItem("deep_order_id", oid); } catch {}
          setDeepFocusId(oid);
        }
        setMainTab("trips");
        setTripsTab("scheduled");
        loadMyOrders({ silent: false }).catch(() => {});
      } catch {}
    };

    window.addEventListener("reisor_driver_open_order", onOpenOrder);
    return () => window.removeEventListener("reisor_driver_open_order", onOpenOrder);
  }, [driverToken, tabsAllowed]);

  const [myOrdersLoading, setMyOrdersLoading] = useState(false);
  const [myOrdersError, setMyOrdersError] = useState("");
// SOUND_POLL_ASSIGNED_V1: звук, когда водителю назначили заказ (в фоне)
useEffect(() => {
  if (!tabsAllowed) return;
  if (!driverToken) return;
  // если водитель на вкладке "Поездки", там уже есть свой polling
  // (sound) разрешаем polling даже на вкладке "Поездки"
  const tick = async () => {
    try {
      const headers = { Authorization: toBearer(driverToken) };
      const res = await fetch("/api/driver/my-orders?scope=active&limit=50", { headers, credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return;
      const list = extractList(data).map((o) => ({ ...o, id: orderIdOf(o) || o.id }));
      // keep for background chat notifications
      watchOrdersRef.current = list;
      // REISOR_DRIVER_MYORDERS_BACKGROUND_STATE_V1: assigned trip must appear in "Поездки" without manual refresh.
      // REISOR_DRIVER_TRIPS_STATE_SINGLE_WRITER_V1:
      // while Trips is open, its own scope-aware poll is the only writer of myOrders.
      // Background active polling remains alive for assignment sounds/notifications.
      if (mainTab !== "trips") {
        try { setMyOrders(list); } catch {}
      }
      const nextIds = new Set(list.map((o) => String(o.id)));
      if (prevAssignedIdsRef.current) {
        const newlyAssigned = list.filter((o) => !prevAssignedIdsRef.current.has(String(o.id)));
        if (newlyAssigned.length) {
          playEventSound("assignedOrder");
          try {
            showToast("ok", t("stage.bid_won_toast", { defaultValue: "🏆 Ваша ставка выиграла. Клиент выбрал вас — нажмите «Принять заказ»." }), {
              actionText: t("tabs.trips"),
              onAction: () => {
                try { setMainTab("trips"); setTripsTab("scheduled"); } catch {}
              },
              });
          } catch {}
        }
      }
      prevAssignedIdsRef.current = nextIds;
    } catch {}
  };
  tick();
  const t = setInterval(tick, 15000);
  return () => clearInterval(t);
}, [tabsAllowed, driverToken, mainTab]);
  const [tripDetails, setTripDetails] = useState(null);
  // OPEN_TRIP_BY_ID_V1
  const openTripById = async (orderId) => {
    const oid = String(orderId || "");
    if (!oid) return;
    // 1) попробуем найти в уже загруженных myOrders
    try {
      const list = (myOrders || []);
      const found = list.find((x) => String(orderIdOf(x) || x?.id || x?._id || "") === oid);
      if (found) {
        setTripDetails(found);
        return;
      }
    } catch {}
    // 2) если не нашли — грузим напрямую по id
    setTripDetails({ id: oid, _loading: true });
    try {
      const headers = {};
      if (driverToken) headers.Authorization = toBearer(driverToken);
      const res = await fetch(`/api/orders/${encodeURIComponent(oid)}`, { headers, credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.error) {
        throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
      }
      setTripDetails({ ...data, id: String(orderIdOf(data) || data?.id || data?._id || oid) });
    } catch (e) {
      setTripDetails({ id: oid, _error: cleanErr(e) || t("errors.load_order_failed") });
    }
  };
  const loadMyOrders = async ({ silent = false } = {}) => {
    if (!tabsAllowed || mainTab !== "trips") return [];
    if (!driverToken) {
      if (!silent) setMyOrdersError(t("errors.no_driver_token"));
      return [];
    }
    if (!silent) setMyOrdersLoading(true);
    if (!silent) setMyOrdersError("");
    try {
      const headers = { Authorization: toBearer(driverToken) };
      const scopeQuery = tripsTab === "past" ? "scope=history&limit=20" : "scope=active&limit=50";
      const res = await fetch(`/api/driver/my-orders?${scopeQuery}`, { headers, credentials: "include" });
      const data = await res.json().catch(() => ([]));
      if (!res.ok) throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
      const list = extractList(data).map((o) => ({ ...o, id: orderIdOf(o) || o.id }));
      watchOrdersRef.current = list;
      setMyOrders(list);
      try { syncNativeTrackingForOrders(list, driverToken, 30); } catch (e) {}
      try { syncPeriodicOrderGpsForOrders(list, driverToken, 30); } catch (e) {}
      return list;
    } catch (e) {
      if (!silent) setMyOrdersError(cleanErr(e));
      return [];
    } finally {
      if (!silent) setMyOrdersLoading(false);
    }
  };
  useEffect(() => {
    if (!tabsAllowed || mainTab !== "trips") return;
    loadMyOrders({ silent: false });
    const t = setInterval(() => loadMyOrders({ silent: true }), 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabsAllowed, mainTab, driverToken, tripsTab]);
  useEffect(() => {
    if (mainTab !== "trips") {
      try { syncPeriodicOrderGpsForOrders([], driverToken, 30); } catch {}
    }
    return () => {
      try { syncPeriodicOrderGpsForOrders([], driverToken, 30); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainTab, driverToken]);
  const doStage = async (orderId, action) => {
    if (!tabsAllowed || !driverToken) {
      showToast("err", t("auth.no_token"));
      return;
    }
    const id = String(orderId || "").trim();
    if (!id) return;
    try {
      pingOrderGps(id, driverToken, action);
      await postJson(`/api/driver/order/${encodeURIComponent(id)}/${encodeURIComponent(action)}`, {}, driverToken);
      showToast("ok", t("driverToast.actionDone"));
      const list = await loadMyOrders({ silent: false });
      if (String(action || "").toLowerCase() === "complete") {
        // REISOR_CLIENT_TRUST_ELIGIBILITY_GATE_V1:
        // completion stays independent; open review only when backend confirms eligibility.
        try {
          const headers = {};
          const bearer = toBearer(driverToken);
          if (bearer) headers.Authorization = bearer;
          const reviewRes = await fetch("/api/client-trust/driver/pending", {
            headers,
            credentials: "include",
            cache: "no-store",
          });
          const reviewData = await reviewRes.json().catch(() => ({}));
          if (reviewRes.ok && !reviewData?.error) {
            const reviewItems = Array.isArray(reviewData?.items) ? reviewData.items : [];
            const eligible = reviewItems.find((item) => {
              const oid = String(item?.orderId || "").trim();
              const aid = String(item?.activeOrderId || "").trim();
              return oid === id || aid === id;
            }) || null;
            if (eligible) setClientTrustReviewOrder(eligible);
          }
        } catch {}
      }
      if (tripDetails) {
        const upd = list.find((x) => orderIdOf(x) === id) || null;
        setTripDetails(upd);
      }
    } catch (e) {
      showToast("err", cleanErr(e));
    }
  };
  // ---- CHAT ----
  const [chatOpen, setChatOpen] = useState(false);
  const [chatOrder, setChatOrder] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState("");
  const [chatText, setChatText] = useState("");

  // ANDROID_BACK_NAV_V1: аппаратная кнопка "Назад" должна закрывать внутренние экраны (чат/детали/настройки), а не сворачивать WebView
  const backTrapArmedRef = useRef(false);
  const backNavRef = useRef({});

  // DOUBLE_BACK_EXIT_V1: на корневом экране первый Back показывает подсказку, второй — выходит
  const backExitLastRef = useRef(0);
  const showBackToast = (msg) => {
    try {
      const elId = "__gptcargo_back_toast";
      let el = document.getElementById(elId);
      if (!el) {
        el = document.createElement("div");
        el.id = elId;
        el.style.position = "fixed";
        el.style.left = "50%";
        el.style.bottom = "18px";
        el.style.transform = "translateX(-50%)";
        el.style.background = "rgba(0,0,0,0.8)";
        el.style.color = "#fff";
        el.style.padding = "10px 14px";
        el.style.borderRadius = "10px";
        el.style.fontSize = "14px";
        el.style.zIndex = "99999";
        el.style.maxWidth = "85vw";
        el.style.textAlign = "center";
        el.style.pointerEvents = "none";
        document.body.appendChild(el);
      }
      el.textContent = msg;
      el.style.opacity = "1";
      clearTimeout(window.__gptcargoToastTimer || 0);
      window.__gptcargoToastTimer = setTimeout(() => {
        try { el.style.opacity = "0"; } catch {}
      }, 1400);
    } catch {}
  };

  useEffect(() => {
    backNavRef.current = {
      isRegisterFlow,
      mainTab,
      settingsOpen,
      availableVehicleOpen,
      myAdsOpen,
      myVehiclesOpen,
      myFreightsOpen,
      quickAccessOpen,
      bidOpenId,
      tripDetails,
      chatOpen,
    };
  }, [isRegisterFlow, mainTab, settingsOpen, availableVehicleOpen, myAdsOpen, myVehiclesOpen, myFreightsOpen, quickAccessOpen, bidOpenId, tripDetails, chatOpen]);

  useEffect(() => {
    try {
      const ua = navigator?.userAgent || "";
      const isAndroid = /Android/i.test(ua);
      if (!isAndroid) return;

      const pushTrap = () => {
        try { window.history.pushState({ __gptcargo_back_trap: true, t: Date.now() }, ""); } catch {}
      };

      if (!backTrapArmedRef.current) {
        backTrapArmedRef.current = true;
        pushTrap();
      }

      const onPop = () => {
        const s = backNavRef.current || {};
        let handled = false;

        try {
          if (s.availableVehicleOpen) {
            setAvailableVehicleOpen(false);
            handled = true;
          } else if (s.myVehiclesOpen) {
            setMyVehiclesOpen(false);
            handled = true;
          } else if (s.myFreightsOpen) {
            setMyFreightsOpen(false);
            handled = true;
          } else if (s.quickAccessOpen) {
            setQuickAccessOpen(false);
            handled = true;
          } else if (s.myAdsOpen) {
            setMyAdsOpen(false);
            handled = true;
          } else if (s.chatOpen) {
            setChatOpen(false);
            setChatOrder(null);
            setChatMessages([]);
            handled = true;
          } else if (s.tripDetails) {
            setTripDetails(null);
            handled = true;
          } else if (s.bidOpenId) {
            setBidOpenId(null);
            handled = true;
          } else if (s.settingsOpen) {
            setSettingsOpen(false);
            handled = true;
          } else if (s.mainTab && s.mainTab !== (s.isRegisterFlow ? "profile" : "requests")) {
            setMainTab(s.isRegisterFlow ? "profile" : "requests");
            handled = true;
          } else if (s.isRegisterFlow && typeof onExitRegisterFlow === "function") {
            onExitRegisterFlow();
            handled = true;
          }
        } catch {}

        // Всегда возвращаем ловушку: Back не сворачивает приложение
        pushTrap();
      };

      window.addEventListener("popstate", onPop);
      return () => window.removeEventListener("popstate", onPop);
    } catch {}
  }, []);

  const loadChat = async (order, { silent = false } = {}) => {
    const oid = orderIdOf(order);
    if (!oid) return;
    if (!silent) setChatLoading(true);
    setChatError("");
    try {
      const headers = {};
      if (driverToken) headers.Authorization = toBearer(driverToken);
      const res = await fetch(`/api/orders/${encodeURIComponent(oid)}/chat?limit=60&by=driver&userId=${encodeURIComponent(driver.id)}`, {
        headers,
        credentials: "include",
      });
      const data = await res.json().catch(() => ([]));
      if (!res.ok) throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
      const arr = Array.isArray(data) ? data : (Array.isArray(data?.messages) ? data.messages : (Array.isArray(data?.items) ? data.items : extractList(data)));
      setChatMessages(arr);
// SOUND_CHAT_V1: если пришло новое сообщение (не от водителя) — звук
try {
  const arr = Array.isArray(data) ? data : (Array.isArray(data?.messages) ? data.messages : (Array.isArray(data?.items) ? data.items : extractList(data)));
  const last = arr && arr.length ? arr[arr.length - 1] : null;
  const lastId = String(last?.id || last?._id || last?.messageId || last?.createdAt || last?.ts || "");
  if (lastId) {
    const driverId = String(driver?.id || driver?._id || "").trim();
    const fromId = String(last?.authorId || last?.senderId || last?.driverId || last?.userId || "").trim();
    const isMe = driverId && fromId && driverId === fromId;
    if (lastChatSigRef.current && lastChatSigRef.current !== lastId && !isMe) {
      playEventSound("chat");
      try {
        showToast("ok", t("driverToast.chatNew"), { duration: 4500 });
      } catch {}
    }
    lastChatSigRef.current = lastId;
  }
} catch {}
    } catch (e) {
      setChatError(cleanErr(e));
    } finally {
      if (!silent) setChatLoading(false);
    }
  };
  const openChat = async (order) => {
    setChatOrder(order || null);
    setChatMessages([]);
    setChatText("");
    setChatOpen(true);
    await loadChat(order);
  };
  const closeChat = () => {
    setChatOpen(false);
    setChatOrder(null);
    setChatMessages([]);
    setChatError("");
    setChatText("");
  };
// CHAT_NOTIFY_POLL_V1: уведомление о новом сообщении (звук+тост) даже когда чат закрыт
useEffect(() => {
  if (!tabsAllowed) return;
  if (!driverToken) return;
  const driverId = String(driver?.id || driver?._id || "").trim();
  if (!driverId) return;
  let cancelled = false;
  const isActiveForChat = (o) => {
    const st = canonStatus(o);
    return st && !["COMPLETED", "CANCELLED", "CANCELED"].includes(st);
  };
  const tick = async () => {
    if (cancelled) return;
    try {
      const list = Array.isArray(watchOrdersRef.current) ? watchOrdersRef.current : [];
      const active = list.filter(isActiveForChat).slice(0, 6);
      if (!active.length) return;
      const headers = { Authorization: toBearer(driverToken) };
      for (const o of active) {
        const oid = orderIdOf(o) || o?.id;
        if (!oid) continue;
        // если этот чат открыт — не дублируем (там есть SOUND_CHAT_POLL_V1)
        try {
          const opened = chatOpen && chatOrder ? String(orderIdOf(chatOrder) || chatOrder.id || "") : "";
          if (opened && opened === String(oid)) continue;
        } catch {}
        const res = await fetch(
          `/api/orders/${encodeURIComponent(String(oid))}/chat?limit=10&by=driver&userId=${encodeURIComponent(driverId)}`,
          { headers, credentials: "include" }
        );
        const data = await res.json().catch(() => ([]));
        if (!res.ok) continue;
        const arr = Array.isArray(data)
          ? data
          : (Array.isArray(data?.messages) ? data.messages : (Array.isArray(data?.items) ? data.items : extractList(data)));
        const last = arr && arr.length ? arr[arr.length - 1] : null;
        const lastId = String(last?.id || last?._id || last?.messageId || last?.createdAt || last?.ts || "");
        if (!lastId) continue;
        const key = String(oid);
        const prev = lastChatByOrderRef.current[key];
        lastChatByOrderRef.current[key] = lastId;
        // первый проход — только запоминаем, без уведомления
        if (!prev) continue;
        if (prev !== lastId) {
          const by = String(last?.by || "").toLowerCase();
          const fromUserId = String(last?.userId || last?.authorId || last?.senderId || last?.driverId || "").trim();
          const isMe = (by === "driver") || (fromUserId && fromUserId === driverId);
          if (isMe) continue;
          playEventSound("chat");
          try {
            showToast("ok", t("driverToast.chatNew"), {
              actionText: t("driverChat.open"),
              onAction: () => openChat(o),
              });
          } catch {}
          break; // avoid toast spam
        }
      }
    } catch {}
  };
  tick();
  const t = setInterval(tick, 10000);
  return () => {
    cancelled = true;
    clearInterval(t);
  };
}, [tabsAllowed, driverToken, driver?.id, driver?._id, chatOpen, chatOrder]);
// SOUND_CHAT_POLL_V1: обновляем чат в фоне и играем звук при новых сообщениях
useEffect(() => {
  if (!chatOpen || !chatOrder) return;
  lastChatSigRef.current = "";
  const t = setInterval(() => {
    loadChat(chatOrder, { silent: true }).catch(() => {});
  }, 6000);
  return () => clearInterval(t);
}, [chatOpen, chatOrder, driverToken]);
  // Открыть маршрут на карте (Google Maps). Работает и в Telegram WebApp (openLink), и в обычном браузере.
  const buildMapsUrl = (o) => {
    const norm = (v) => {
      const s = String(v || "").trim();
      return s === "—" ? "" : s;
    };
  // ---- maps provider (Google/Yandex) + remember ----
  const MAP_PROVIDER_KEY = "gptcargo_driver_map_provider_v1";
  const MAP_PROVIDER_REMEMBER_KEY = "gptcargo_driver_map_provider_remember_v1";
  const readMapPref = () => {
    try {
      const remember = localStorage.getItem(MAP_PROVIDER_REMEMBER_KEY) === "1";
      const provider = String(localStorage.getItem(MAP_PROVIDER_KEY) || "");
      if (!remember) return { provider: "", remember: false };
      if (provider !== "yandex" && provider !== "google") return { provider: "", remember: false };
      return { provider, remember: true };
    } catch {
      return { provider: "", remember: false };
    }
  };
  const saveMapPref = (provider, remember) => {
    try {
      if (remember) {
        localStorage.setItem(MAP_PROVIDER_KEY, provider);
        localStorage.setItem(MAP_PROVIDER_REMEMBER_KEY, "1");
      } else {
        localStorage.removeItem(MAP_PROVIDER_KEY);
        localStorage.removeItem(MAP_PROVIDER_REMEMBER_KEY);
      }
    } catch {}
  };
  const buildYandexMapsUrl = (o) => {
    const norm = (v) => {
      const s = String(v || "").trim();
      return s === "—" ? "" : s;
    };
    const from = norm(o?.from || o?.fromAddress || o?.pickupAddress || o?.loadingAddress);
    const to = norm(o?.to || o?.toAddress || o?.dropoffAddress || o?.unloadingAddress);
    const fromLat = o?.fromLat ?? o?.pickupLat ?? o?.loadingLat;
    const fromLng = o?.fromLng ?? o?.pickupLng ?? o?.loadingLng;
    const toLat = o?.toLat ?? o?.dropoffLat ?? o?.unloadingLat;
    const toLng = o?.toLng ?? o?.dropoffLng ?? o?.unloadingLng;
    const has = (v) => v !== undefined && v !== null && String(v).trim() !== "" && !Number.isNaN(Number(v));
    const n = (v) => (typeof v === "number" ? v : Number(v));
    if (has(fromLat) && has(fromLng) && has(toLat) && has(toLng)) {
      const origin = `${n(fromLat)},${n(fromLng)}`;
      const dest = `${n(toLat)},${n(toLng)}`;
      return `https://yandex.ru/maps/?mode=routes&rtext=${encodeURIComponent(origin)}~${encodeURIComponent(dest)}&rtt=auto`;
    }
    if (from && to) {
      return `https://yandex.ru/maps/?mode=routes&rtext=${encodeURIComponent(from)}~${encodeURIComponent(to)}&rtt=auto`;
    }
    if (from) return `https://yandex.ru/maps/?text=${encodeURIComponent(from)}`;
    if (to) return `https://yandex.ru/maps/?text=${encodeURIComponent(to)}`;
    return null;
  };
    const from = norm(o?.from || o?.fromAddress || o?.pickupAddress || o?.loadingAddress);
    const to = norm(o?.to || o?.toAddress || o?.dropoffAddress || o?.unloadingAddress);
    // Пробуем координаты, если они есть в объекте
    const fromLat = o?.fromLat ?? o?.pickupLat ?? o?.loadingLat;
    const fromLng = o?.fromLng ?? o?.pickupLng ?? o?.loadingLng;
    const toLat = o?.toLat ?? o?.dropoffLat ?? o?.unloadingLat;
    const toLng = o?.toLng ?? o?.dropoffLng ?? o?.unloadingLng;
    const has = (v) => v !== undefined && v !== null && String(v).trim() !== "" && !Number.isNaN(Number(v));
    const n = (v) => (typeof v === "number" ? v : Number(v));
    if (has(fromLat) && has(fromLng) && has(toLat) && has(toLng)) {
      const origin = `${n(fromLat)},${n(fromLng)}`;
      const dest = `${n(toLat)},${n(toLng)}`;
      return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(dest)}&travelmode=driving`;
    }
    if (from && to) {
      return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(from)}&destination=${encodeURIComponent(to)}&travelmode=driving`;
    }
    if (from) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(from)}`;
    if (to) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(to)}`;
    return null;
  };
  /**
   * ВАЖНО (канон проекта):
   * - НЕ переводить проект на 100% платные маршрутизаторы (Yandex Router / Google Directions API) без ЖЁСТКОГО лимитера.
   * - Если кто-то когда-то решит считать маршруты через API: ставим hard-stop на ~700-800/сутки (из 1000 free) и уходим в fallback.
   * Сейчас тут НЕТ платного API: это встроенный iframe Google Maps (public embed) + fallback на Google Maps URL.
   */
  const buildGoogleEmbedUrl = (o) => {
    const from = String(o?.from || o?.fromAddress || o?.pickupAddress || o?.loadingAddress || "").trim();
    const to = String(o?.to || o?.toAddress || o?.dropoffAddress || o?.unloadingAddress || "").trim();
    const fromLat = o?.fromLat ?? o?.pickupLat ?? o?.loadingLat;
    const fromLng = o?.fromLng ?? o?.pickupLng ?? o?.loadingLng;
    const toLat = o?.toLat ?? o?.dropoffLat ?? o?.unloadingLat;
    const toLng = o?.toLng ?? o?.dropoffLng ?? o?.unloadingLng;
    const has = (v) => v !== undefined && v !== null && String(v).trim() !== "" && !Number.isNaN(Number(v));
    const n = (v) => (typeof v === "number" ? v : Number(v));
    // Google public embed: без ключа (НЕ Directions API)
    if (has(fromLat) && has(fromLng) && has(toLat) && has(toLng)) {
      const saddr = `${n(fromLat)},${n(fromLng)}`;
      const daddr = `${n(toLat)},${n(toLng)}`;
      return `https://www.google.com/maps?output=embed&f=d&dirflg=d&saddr=${encodeURIComponent(saddr)}&daddr=${encodeURIComponent(daddr)}&hl=ru`;
    }
    if (from && to) {
      return `https://www.google.com/maps?output=embed&f=d&dirflg=d&saddr=${encodeURIComponent(from)}&daddr=${encodeURIComponent(to)}&hl=ru`;
    }
    if (from) return `https://www.google.com/maps?output=embed&q=${encodeURIComponent(from)}&hl=ru`;
    if (to) return `https://www.google.com/maps?output=embed&q=${encodeURIComponent(to)}&hl=ru`;
    return null;
  };
  // Адреса кликабельны: открываем Яндекс.Карты (внешнее приложение/браузер)
  const openYandexText = (q) => {
    const text = String(q || "").trim();
    if (!text || text === "—") {
      showToast("err", t("driverToast.noAddress"));
      return;
    }
    const url = `https://yandex.ru/maps/?text=${encodeURIComponent(text)}`;
    try {
      const tg = window?.Telegram?.WebApp;
      if (tg?.openLink) return tg.openLink(url);
    } catch {}
    window.open(url, "_blank", "noopener,noreferrer");
  };
  
  
  
  
  
  const openMap = (o) => {
    const norm = (v) => {
      const s = String(v || "").trim();
      return s === "—" ? "" : s;
    };
    const from = norm(o?.from || o?.fromAddress || o?.pickupAddress || o?.loadingAddress);
    const to = norm(o?.to || o?.toAddress || o?.dropoffAddress || o?.unloadingAddress);
    const fromLat = o?.fromLat ?? o?.pickupLat ?? o?.loadingLat;
    const fromLng = o?.fromLng ?? o?.pickupLng ?? o?.loadingLng;
    const toLat = o?.toLat ?? o?.dropoffLat ?? o?.unloadingLat;
    const toLng = o?.toLng ?? o?.dropoffLng ?? o?.unloadingLng;
    const has = (v) =>
      v !== undefined && v !== null && String(v).trim() !== "" && !Number.isNaN(Number(v));
    const n = (v) => (typeof v === "number" ? v : Number(v));
    const openWeb = (url) => {
      if (!url) return;
      try {
        const tg = window?.Telegram?.WebApp;
        if (tg?.openLink) return tg.openLink(url);
      } catch {}
      window.open(url, "_blank", "noopener,noreferrer");
    };
    const openDeepLinkWithFallback = (appUrl, webUrl) => {
      if (!appUrl) return openWeb(webUrl);
      let opened = false;
      const onVis = () => { if (document.hidden) opened = true; };
      document.addEventListener("visibilitychange", onVis, { once: true });
      try {
        window.location.href = appUrl;
      } catch {}
      setTimeout(() => {
        if (!opened) {
          // cache-buster на fallback
          const u = webUrl ? (webUrl + (webUrl.includes("?") ? "&" : "?") + "__ts=" + Date.now()) : null;
          openWeb(u);
        }
      }, 900);
    };
    // === 1) МАРШРУТ ЧЕРЕЗ КООРДИНАТЫ (самый надежный вариант) ===
    if (has(fromLat) && has(fromLng) && has(toLat) && has(toLng)) {
      const appUrl = `yandexmaps://build_route_on_map/?lat_from=${encodeURIComponent(n(fromLat))}&lon_from=${encodeURIComponent(n(fromLng))}&lat_to=${encodeURIComponent(n(toLat))}&lon_to=${encodeURIComponent(n(toLng))}`;
      const origin = `${n(fromLat)},${n(fromLng)}`;
      const dest = `${n(toLat)},${n(toLng)}`;
      const webUrl = `https://yandex.ru/maps/?mode=routes&rtext=${encodeURIComponent(origin)}~${encodeURIComponent(dest)}&rtt=auto`;
      return openDeepLinkWithFallback(appUrl, webUrl);
    }
    // === 2) МАРШРУТ ПО АДРЕСАМ (fallback, может быть капризным у Яндекса) ===
    if (from && to) {
      const appUrl = `https://yandex.ru/maps/?mode=routes&rtext=${encodeURIComponent(from)}~${encodeURIComponent(to)}&rtt=auto`;
      const webUrl = `https://yandex.ru/maps/?mode=routes&rtext=${encodeURIComponent(from)}~${encodeURIComponent(to)}&rtt=auto`;
      return openDeepLinkWithFallback(appUrl, webUrl);
    }
    // === 3) ПОКАЗАТЬ ОДНУ ТОЧКУ (клик по "Откуда"/"Куда") ===
    const q = from || to;
    if (!q) {
      try { showToast("err", t("map.no_coords")); } catch { alert(t("map.no_coords")); }
      return;
    }
    const appUrl = `https://yandex.ru/maps/?text=${encodeURIComponent(q)}`;
    const webUrl = `https://yandex.ru/maps/?text=${encodeURIComponent(q)}`;
    return openDeepLinkWithFallback(appUrl, webUrl);
  };
  const sendChat = async () => {
    const oid = orderIdOf(chatOrder);
    const text = String(chatText || "").trim();
    if (!oid || !text) return;
    try {
      await postJson(`/api/orders/${encodeURIComponent(oid)}/chat`, { by: "driver", userId: driver.id, text }, driverToken);
      setChatText("");
      await loadChat(chatOrder);
    } catch (e) {
      showToast("err", cleanErr(e));
    }
  };
  // ---- derived lists ----
  const myOfferOrders = useMemo(() => (orders || []).filter((o) => hasMyOffer(o)), [orders, offers, driver]);
  const newOrders = useMemo(() => (orders || []).filter((o) => !hasMyOffer(o)), [orders, offers, driver]);
  const tripsScheduled = useMemo(() => {
    return (myOrders || []).filter((o) => {
      const st = String(o?.status || "").toUpperCase();
      return !["COMPLETED", "CANCELLED", "CANCELED"].includes(st);
    });
  }, [myOrders]);
  const tripsPast = useMemo(() => {
    return (myOrders || []).filter((o) => {
      const st = String(o?.status || "").toUpperCase();
      return ["COMPLETED", "CANCELLED", "CANCELED"].includes(st);
    });
  }, [myOrders]);
  // AUTO: если у водителя ровно 1 запланированная поездка — сразу открываем детали (без экрана "Подробнее")
  useEffect(() => {
    if (!tabsAllowed) return;
    if (mainTab !== "trips") return;
    if (tripsTab !== "scheduled") return;
    if (tripDetails) return;
    if (tripsScheduled.length === 1) {
      setTripDetails(tripsScheduled[0]);
    }
  }, [tabsAllowed, mainTab, tripsTab, tripDetails, tripsScheduled]);
  // ---- UI pieces ----
  const Label = ({ children }) => <div className="text-sky-300 text-sm font-semibold">{children}</div>;
  const Value = ({ children, className = "" }) => (
    <div className={"text-white text-[15px] font-semibold leading-snug " + (className || "")}>{children}</div>
  );
  // CANON: distance/time берём из сохранённого order.routeMeta (кэш в заказе).
  // НЕ пересчитывать маршрут здесь — защита от платных лимитов.

// REISOR_DRIVER_MULTICAR_VISIBLE_UI_V1
function multiVehicleInfo(order) {
  const meta = (order && order.meta && typeof order.meta === "object") ? order.meta : {};
  const totalRaw = order?.vehicleCount ?? meta.vehicleCount ?? 1;
  const totalNum = Number(totalRaw);
  const total = Number.isFinite(totalNum) && totalNum > 0 ? Math.max(1, Math.floor(totalNum)) : 1;
  const assignedRaw = order?.assignedCount ?? meta.assignedCount ?? 0;
  const assignedNum = Number(assignedRaw);
  const assigned = Number.isFinite(assignedNum) && assignedNum >= 0 ? Math.max(0, Math.floor(assignedNum)) : 0;
  const remainingRaw = order?.remainingVehicleCount ?? meta.remainingVehicleCount;
  const remainingNum = Number(remainingRaw);
  const remaining = Number.isFinite(remainingNum) && remainingNum >= 0 ? Math.max(0, Math.floor(remainingNum)) : Math.max(0, total - assigned);
  return { isMulti: total > 1, total, assigned, remaining };
}

function MultiVehicleBadge({ order, compact = false }) {
  const m = multiVehicleInfo(order);
  if (!m.isMulti) return null;
  return (
    <div className={compact ? "rounded-2xl border border-blue-400/30 bg-blue-500/10 p-2 text-sm" : "rounded-2xl border border-blue-400/40 bg-blue-500/10 p-3"}>
      <div className="font-extrabold text-blue-200">{t("driverMulti.needed")}</div>
      <div className="text-white font-bold">{t("driverMulti.summary", { total: m.total, assigned: m.assigned, remaining: m.remaining })}</div>
      {m.remaining > 0 ? <div className="text-slate-200/80 text-sm">{t("driverMulti.canBidOne")}</div> : null}
    </div>
  );
}

  const formatRouteMetaLine = (order) => {
    // CANON: берём distance/time из кэша в заказе.
    // 1) order.routeMeta (новый канон)
    // 2) order.route (старый формат, тоже кэш в заказе)
    const rm = order?.routeMeta || order?.route || null;
    const d = Number(rm?.distanceKm);
    const mins = Number(rm?.durationMin);
    if (!Number.isFinite(d) || !Number.isFinite(mins)) return null;
    const distanceKm = Math.round(d);
    const total = Math.round(mins);
    const h = Math.floor(total / 60);
    const m = total % 60;
    // компактно: 12ч30м / 45м
    const hour = t("units.hourShort");
    const minute = t("units.minuteShort");
    const dur = h > 0 ? (m ? `${h}${hour}${m}${minute}` : `${h}${hour}`) : `${m}${minute}`;
    return `${distanceKm} ${t("units.km")} · ${dur}`;
  };
  const renderOrderCard = (order, opts = {}) => {
    const oid = orderIdOf(order);
    const {
      canSkip = false,
      isSkipped = false,
      onSkip,
      onUnskip,
      collapseMapsByDefault = false,
    } = opts || {};
    const date = formatWhen(order) || (order?.dateTime ? ruDateTime(order.dateTime) : ruDateTime(order?.createdAt));
    const from = order?.from || order?.fromAddress || order?.pickupAddress || "—";
    const to = order?.to || order?.toAddress || order?.dropoffAddress || "—";
    const weight = order?.weight ?? order?.cargoWeight ?? "—";
    const volume = order?.volume ?? order?.cargoVolume ?? "—";
    const comment = order?.comment ?? "—";

const reqBodyType = (() => {
  const btRaw = order?.bodyType ?? order?.truckBodyType ?? order?.body ?? "";
  const other = order?.bodyTypeOther ?? order?.bodyType_other ?? order?.truckBodyTypeOther ?? "";
  let body = String(btRaw || "").trim();
  if (!body) return "";
  const low = body.toLowerCase();
  if (low === "\u0434\u0440\u0443\u0433\u043e\u0435" && String(other || "").trim()) return String(other || "").trim();
  return body;
})();

const multiVehicle = multiVehicleInfo(order);

const reqLoadingText = (() => {
  const raw = order?.loadingTypes ?? order?.loadingType ?? order?.loadTypes ?? order?.loadType ?? [];
  const arr = Array.isArray(raw) ? raw : typeof raw === "string" ? raw.split(",") : [];
  const clean = Array.from(new Set(arr.map((x) => String(x || "").trim()).filter(Boolean)));
  return clean.length ? clean.join(", ") : "";
})();

const requirementsBlock = (compact = false) => {
  if (!reqBodyType && !reqLoadingText) return null;
  if (compact) {
    const parts = [];
    if (reqBodyType) parts.push(`${t("order.fields.bodyType", { defaultValue: "Тип кузова" })}: ${reqBodyType}`);
    if (reqLoadingText) parts.push(`${t("order.fields.loadingTypes", { defaultValue: "Тип загрузки" })}: ${reqLoadingText}`);
    return (
      <div className="rounded-2xl border border-slate-700/40 bg-slate-900/40 p-2 text-sm text-white">
        <div className="text-slate-200/70 text-xs font-semibold mb-1">
          {t("order.requirements", { defaultValue: "Требования заказа" })}
        </div>
        <div className="space-y-1">
          {parts.map((x, i) => (
            <div key={i}>{x}</div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-slate-700/40 bg-slate-900/40 p-3">
      <div className="text-slate-200/70 text-xs font-semibold">
        {t("order.requirements", { defaultValue: "Требования заказа" })}
      </div>
      {reqBodyType ? (
        <div className="text-white text-sm">
          <span className="text-slate-200/70">
            {t("order.fields.bodyType", { defaultValue: "Тип кузова" })}:
          </span>{" "}
          {reqBodyType}
        </div>
      ) : null}
      {reqLoadingText ? (
        <div className="text-white text-sm">
          <span className="text-slate-200/70">
            {t("order.fields.loadingTypes", { defaultValue: "Тип загрузки" })}:
          </span>{" "}
          {reqLoadingText}
        </div>
      ) : null}
    </div>
  );
};
    const routeLine = formatRouteMetaLine(order);
    const routeEmbedUrl = buildGoogleEmbedUrl({ ...order, from, to });
    const routeMapDefaultOpen =
      !collapseMapsByDefault && !hasMyOffer(order);
    const routeMapOpen = Object.prototype.hasOwnProperty.call(
      routeMapOpenByOrder,
      oid
    )
      ? Boolean(routeMapOpenByOrder[oid])
      : routeMapDefaultOpen;
    const toggleRouteMap = () => {
      if (!oid) return;
      setRouteMapOpenByOrder((previous) => {
        const current = Object.prototype.hasOwnProperty.call(previous, oid)
          ? Boolean(previous[oid])
          : routeMapDefaultOpen;
        return { ...previous, [oid]: !current };
      });
    };
    const localRaw = offers?.[oid] || null;
    const local =
      localRaw && localRaw.state !== "cancelled" && !isExpiredMs(localRaw.expiresAt)
        ? localRaw
        : null;
    const srvBid = myBidFromServer(order);
    const offerText = local?.price != null ? local.price : srvBid?.bidAmount;
    const offerCurrency = String(
      local?.currency || local?.bidCurrency || local?.currencyCode || local?.priceCurrency ||
      srvBid?.currency || srvBid?.bidCurrency || srvBid?.priceCurrency ||
      order?.meta?.selectedBid?.currency || order?.selectedBid?.currency ||
      order?.meta?.priceCurrency || order?.meta?.price_currency ||
      "RUB"
    ).toUpperCase();
    const offerCurrencySym =
      offerCurrency === "USD" ? "$" :
      offerCurrency === "EUR" ? "€" :
      offerCurrency === "UZS" ? t("units.uzs") : "₽";

    // BEST_BID_APP_V3: the best price shown to drivers is a competition signal.
    // Dogruz/add_load is a separate "on the way" offer and must never become best price.
    // Compare full-trip bids by RUB-equivalent from backend; display the original bid amount/currency.
    const bestBidInfo = (() => {
      const backendBest = order?.bestBid || order?.bestOffer || order?.meta?.bestBid || order?.meta?.bestOffer || null;
      if (backendBest && !isDogruzBidForBestPrice(backendBest)) {
        const amount = Number(backendBest?.amount ?? backendBest?.bidAmount ?? backendBest?.price ?? 0) || 0;
        const currency = String(backendBest?.currency || backendBest?.bidCurrency || backendBest?.bid_currency || "RUB").toUpperCase();
        const rub = Number(backendBest?.rub ?? backendBest?.bidAmountRub ?? backendBest?.bid_amount_rub ?? 0) || bidRubForBestPrice(backendBest);
        if (amount > 0 && rub > 0) return { rub, amount, currency };
      }

      const bids = Array.isArray(order?.bids) ? order.bids : [];
      const now = Date.now();
      let best = null;

      for (const b of bids) {
        if (!b) continue;
        if (isDogruzBidForBestPrice(b)) continue;
        if (String(b?.status || "active").toLowerCase() !== "active") continue;

        const exp = b?.expiresAt ? new Date(b.expiresAt).getTime() : NaN;
        if (Number.isFinite(exp) && exp <= now) continue;

        const currency = String(
          b?.bidCurrency || b?.currency || b?.bid_currency || "RUB"
        ).toUpperCase();

        const amount = Number(b?.bidAmount ?? b?.amount ?? b?.price ?? 0) || 0;
        if (!(amount > 0)) continue;

        const rub = bidRubForBestPrice(b);
        if (!(rub > 0)) continue;

        if (!best || rub < best.rub) {
          best = { rub, amount, currency };
        }
      }

      return best;
    })();
    const offerLabel = hasMyOffer(order)
      ? t("orders.actions.edit_offer")
      : t("orders.actions.offer_price");
    return (
      <Card id={`order-${oid}`} key={oid} className={`${deepFocusId === oid ? "ring-2 ring-violet-400" : ""}`}>
        <CardHeader className="pb-1">
          <CardTitle className="text-white font-extrabold">{date}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
        {isSkipped ? (
          <div className="text-red-300 font-bold text-sm">⛔ {t("requests.skipped")}</div>
        ) : null}
        <MultiVehicleBadge order={order} />
          <div>
            <Label>{t("order.fields.order")}:</Label>
            <div className="text-white/90">
              {(() => {
                const ref =
                  (typeof o !== "undefined" ? o :
                  (typeof order !== "undefined" ? order :
                  (typeof orderInfo !== "undefined" ? orderInfo :
                  (typeof chatOrder !== "undefined" ? chatOrder : null))));
                const id = ref ? (((typeof orderIdOf === "function") ? orderIdOf(ref) : null) || ref.id || ref.orderId || ref._id) : null;
                if (!id) return "—";
                const h = (typeof humanOrderNo === "function" ? humanOrderNo(id) : String(id));
                return "№" + h;
              })()}
            </div>
            <Label>{t("order.fields.from")}:</Label>
            <Value>
              <button
                type="button"
                className="text-left underline decoration-dotted underline-offset-2 hover:text-sky-100"
                onClick={() => openYandexText(from)}
              >
                {from}
              </button>
            </Value>
          </div>
          <div>
            <Label>{t("order.fields.to")}:</Label>
            <Value>
              <button
                type="button"
                className="text-left underline decoration-dotted underline-offset-2 hover:text-sky-100"
                onClick={() => openYandexText(to)}
              >
                {to}
              </button>
            </Value>
          </div>
          {routeLine ? (
            <div className="text-orange-500 font-extrabold text-base mt-1">{t("order.fields.route")}: {routeLine}</div>
          ) : null}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("order.fields.weight")}:</Label>
              <Value>{weight} {t("units.kg")}</Value>
            </div>
            <div>
              <Label>{t("order.fields.volume")}: </Label>
              <Value>{volume} {t("units.m3")}</Value>
            </div>
          </div>
          {requirementsBlock()}
          <div>
            {/* PAYMENT_CHECKS_V1 */}
            <PaymentChecks title={paymentTitleFromOrder(order)} items={paymentItemsFromOrder(order)} bordered />
            <Label>{t("order.fields.comment")}:</Label>
            <Value>{comment}</Value>
          </div>
          {routeEmbedUrl ? (
            <div className="space-y-2">
              <button
                type="button"
                onClick={toggleRouteMap}
                aria-expanded={routeMapOpen}
                className="w-full rounded-xl border border-slate-700 bg-slate-900/80 px-3 py-2.5 text-sm font-bold text-sky-200 transition hover:bg-slate-800"
              >
                {routeMapOpen
                  ? t("buttons.hide_map")
                  : t("buttons.show_map")}
              </button>
              {routeMapOpen ? (
                <div className="rounded-2xl overflow-hidden border border-slate-700 bg-slate-900/40">
                  <iframe
                    title={`route_${oid}`}
                    src={routeEmbedUrl}
                    className="w-full h-[260px]"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              ) : null}
            </div>
          ) : null}
          {offerText != null ? (
            <div>
              <div className="text-emerald-300 font-bold">{t("orders.labels.your_offer") + ":"}</div>
              <div className="text-white font-semibold">
                {t("orders.labels.your_price")}: <span className="font-extrabold">{offerText}</span> {offerCurrencySym}{" "}
                {local?.loadType ? <span className="text-slate-200/70">({local.loadType === "empty" ? t("requests.loadType.empty") : t("requests.loadType.dogruz")})</span> : null}
              </div>
              {/* BEST_BID_UI_V1 */}
              {bestBidInfo ? (
                <div className="text-violet-400 font-bold">
                  {t("orders.labels.best_price")}{" "}
                  {Number(bestBidInfo.amount).toLocaleString("ru-RU")}{" "}
                  {currencySymbolOf(bestBidInfo.currency)}
                </div>
              ) : null}
            </div>
          ) : null}
<div className="mt-3">
            <div className="rounded-2xl bg-slate-950/80 backdrop-blur border border-slate-700/40 p-2 shadow-lg">
              <div className="flex flex-col gap-2">
  {canSkip ? (
    isSkipped ? (
      <Button
        type="button"
        className="w-full rounded-2xl px-4 py-3 text-[15px] leading-tight font-bold bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-300"
        onClick={() => onUnskip?.(oid)}
      >
        {t("requests.unskip")}
      </Button>
    ) : (
      <Button
        type="button"
        className="w-full rounded-2xl px-4 py-3 text-[15px] leading-tight font-bold bg-red-600 text-white hover:bg-red-700 border border-red-500/40"
        onClick={() => onSkip?.(oid)}
      >
        {t("requests.skip")}
      </Button>
    )
  ) : null}
            {bidOpenId !== oid ? (
              <Button className="w-full" onClick={() => toggleBid(oid)}>
                {offerLabel}
              </Button>
            ) : null}
            {bidOpenId === oid ? (
              <>
                {requirementsBlock(true)}
                <BidInline
                isFixedPrice={isFixedOrder(order)} initialPrice={getInitialPrice(order)}
              priceLocked={isFixedPriceMode(priceModeOf(order)) && (basePriceAmountOf(order) ?? basePriceRubOf(order) ?? fixedOrderPriceOf(order)) != null}
                initialExpiration={(offers?.[oid]?.period) || "1h"}
                initialLoadType={(offers?.[oid]?.loadType) || (myBidFromServer(order)?.loadType) || "empty"}
                initialPayment={(offers?.[oid]?.payment) || (myBidFromServer(order)?.payment) || null}
                initialCurrency={isFixedPriceMode(priceModeOf(order)) ? (order?.meta?.priceCurrency || basePriceCurrencyOf(order) || "RUB") : ((offers?.[oid]?.currency) || (myBidFromServer(order)?.bidCurrency) || (myBidFromServer(order)?.currency) || (order?.meta?.priceCurrency) || basePriceCurrencyOf(order) || "RUB")}
                mode={hasMyOffer(order) ? "edit" : "create"}
                disabledReason={driverAccessLimitedMessage}
                onCancel={() => setBidOpenId(null)}
                onSubmit={(amount, expiration, extra) =>
                  handleSubmitBid(oid, amount, expiration, extra)
                }
              />
                          </>
            ) : null}
            {hasMyOffer(order) ? (
              <Button
                className="w-full rounded-2xl px-4 py-3 text-[15px] leading-tight font-bold bg-red-600 hover:bg-red-700 focus:ring-red-400"
                onClick={() => handleCancelOffer(oid)}
              >
                {t("orders.actions.cancel_offer")}
              </Button>
            ) : null}
          </div>
            </div>
          </div>
          <div className="text-xs text-slate-300/70">{t("order.no_prefix")}{humanOrderNo(oid)}</div>
        </CardContent>
      </Card>
    );
  };
  const renderTripCard = (o) => {
    const oid = orderIdOf(o);
    const date = formatWhen(o) || (o?.dateTime ? ruDateTime(o.dateTime) : ruDateTime(o?.createdAt));
    const from = o?.from || o?.fromAddress || o?.pickupAddress || "—";
    const to = o?.to || o?.toAddress || o?.dropoffAddress || "—";
    const st = canonStatus(o);
    const stLabel = t(`status.${st}`, STATUS_RU[st] || st || "—");
    const fixedPrice = fixedOrderPriceOf(o);
    const priceSym = currencySymbolOf(priceCurrencyOf(o));
    return (
      <Card key={oid}>
        <CardHeader className="pb-1">
          <CardTitle className="text-white font-extrabold">{date}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>{t("order.fields.from")}:</Label>
            <Value>
              <button
                type="button"
                className="text-left underline decoration-dotted underline-offset-2 hover:text-sky-100"
                onClick={() => openYandexText(from)}
              >
                {from}
              </button>
            </Value>
          </div>
          <div>
            <Label>{t("order.fields.to")}:</Label>
            <Value>
              <button
                type="button"
                className="text-left underline decoration-dotted underline-offset-2 hover:text-sky-100"
                onClick={() => openYandexText(to)}
              >
                {to}
              </button>
            </Value>
          </div>
          <div>
            <Label>{t("order.fields.order_no")}: </Label>
<div className="text-white/90 font-semibold">№{displayOrderNo( (typeof o!=="undefined" && o) || (typeof order!=="undefined" && order) || (typeof selectedOrder!=="undefined" && selectedOrder) || (typeof chatOrder!=="undefined" && chatOrder) || null )}</div>
<Label>{t("order.fields.status")}: </Label>
            <Value className={statusColorClass(st)}>{stLabel}</Value>
            <MultiVehicleBadge order={o} compact />

              
              {/* AGREED_TERMS_BLOCK_V1 */}
              {(() => {
                const oo =
                  (typeof order !== "undefined" ? order :
                  (typeof o !== "undefined" ? o :
                  (typeof trip !== "undefined" ? trip : null)));

                const sb = oo?.meta?.selectedBid || null;
                if (!sb) return null;

                const amtNum = Number(sb?.bidAmount);
                const hasAmt = Number.isFinite(amtNum) && amtNum > 0;

                const items = paymentItemsFromOrder(oo);
                const hasItems = Array.isArray(items) && items.length > 0;

                if (!hasAmt && !hasItems) return null;

                const amtSym = currencySymbolOf(priceCurrencyOf(oo));
                
                                const amtText = hasAmt ? (amtNum.toLocaleString("ru-RU") + " " + amtSym) : "—";

                return (
                  <div className="mt-3 p-3 rounded-xl border border-white/10">
                    <div className="text-white/70 text-sm mb-2">{paymentIsFinal(oo) ? t("payment.agreed") : t("payment.not_final")}</div>

                    <div className="flex items-center justify-between">
                      <div className="text-white/70">{t("common.price")}: </div>
                      <div className="text-white font-semibold">{amtText}</div>
                    </div>

                    {hasItems ? (
                      <div className="mt-2">
                        <PaymentChecks title={paymentIsFinal(oo) ? t("payment.final_title") : t("payment.offer_title")} items={items} bordered />
                      </div>
                    ) : null}
                  </div>
                );
              })()}


          </div>
                    {fixedPrice != null ? (
            <div>
              <Label>{t("order.price_label")}: </Label>
              <Value>
                <span className="text-emerald-200 font-extrabold">{moneyText(fixedPrice, { sym: priceSym })}</span>
              </Value>
            </div>
          ) : null}
<Button
            className="w-full bg-orange-500 hover:bg-orange-600 focus:ring-orange-300"
            onClick={() => openTripById(oid)}
          >{t("common.details")}</Button>
          {/* TRIPS_DETAILS_OPEN_BY_ID_V1 */}
          <div className="text-xs text-slate-300/70">{t("order.no_prefix")}{humanOrderNo(oid)}</div>
        </CardContent>
      </Card>
    );
  };
  const renderStageButtons = (o) => {
    const st = canonStatus(o);
    if (st === "WAITING_DRIVER_ACCEPT") {
      const exp = o?.meta?.pendingAssignment?.expiresAt;
      const acceptTime = exp ? new Date(exp).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }) : "";
      const expText = exp
        ? t("stage.accept_before", { time: acceptTime, defaultValue: `Примите до ${acceptTime}` })
        : t("stage.press_accept_order", { defaultValue: "Нажмите «Принять заказ»" });
      return (
        <div className="flex flex-col gap-2">
          <div className="rounded-xl border border-amber-300/30 bg-amber-400/10 px-3 py-2 text-sm text-amber-100">
            {t("stage.bid_won_client_selected", { timeText: expText, defaultValue: `Ваша ставка выиграла. Клиент выбрал вас. ${expText}, чтобы подтвердить назначение.` })}
          </div>
          <div className="flex gap-2">
            <Button className="flex-1" onClick={() => doStage(o.id, "accept")}>{t("stage.accept_order", { defaultValue: "✅ Принять заказ" })}</Button>
            <Button className="flex-1 bg-red-600 hover:bg-red-700 focus:ring-red-400" onClick={() => doStage(o.id, "reject")}>
              {t("stage.reject_order", { defaultValue: "Отказаться" })}
            </Button>
          </div>
        </div>
      );
    }
    if (st === "ASSIGNED") {
      return (
        <div className="flex gap-2">
          <Button className="flex-1" onClick={() => doStage(o.id, "accept")}>{t("driverStage.goPickup")}</Button>
          <Button className="flex-1 bg-red-600 hover:bg-red-700 focus:ring-red-400" onClick={() => doStage(o.id, "reject")}>
            {t("orders.actions.cancel_offer")}
          </Button>
        </div>
      );
    }
    if (st === "ON_ROUTE") {
      return (
        <div className="flex flex-col gap-2">
          <Button className="bg-purple-600 hover:bg-purple-700 focus:ring-purple-400" onClick={() => openChat(o)}>💬 {t(orderChatLabelKey(o))}</Button>
          <Button onClick={() => doStage(o.id, "arrived")}>{t("driverStage.arrived")}</Button>
        </div>
      );
    }
    if (st === "AT_PICKUP") {
      return (
        <div className="flex flex-col gap-2">
          <Button className="bg-purple-600 hover:bg-purple-700 focus:ring-purple-400" onClick={() => openChat(o)}>💬 {t(orderChatLabelKey(o))}</Button>
          <Button onClick={() => doStage(o.id, "loaded")}>{t("driverStage.loaded")}</Button>
        </div>
      );
    }
    if (st === "LOADED") {
      return (
        <div className="flex flex-col gap-2">
          <Button className="bg-purple-600 hover:bg-purple-700 focus:ring-purple-400" onClick={() => openChat(o)}>💬 {t(orderChatLabelKey(o))}</Button>
          <Button onClick={() => doStage(o.id, "complete")}>{t("driverStage.complete")}</Button>
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-2">
        <Button className="bg-purple-600 hover:bg-purple-700 focus:ring-purple-400" onClick={() => openChat(o)}>💬 {t(orderChatLabelKey(o))}</Button>
      </div>
    );
  };
  const renderTripDetails = () => {
    const o = tripDetails;
    if (!o) return null;
    // TRIP_DETAILS_LOADING_V1
    if (o?._loading) {
      return (
        <div className="w-full">
          <div className="flex items-center gap-3 mb-3">
            <button
              type="button"
              className="px-4 py-3 rounded-2xl bg-slate-700/60 hover:bg-slate-700 text-white font-bold"
              onClick={() => setTripDetails(null)}
              title={t("driverChat.back")}
            >
              ←
            </button>
            <div className="text-2xl font-black">{t("driverOrder.loading")}</div>
          </div>
          <div className="rounded-2xl border border-slate-700 bg-slate-800/30 p-4 text-slate-200/80">
            {t("driverOrder.loadingDetails")}
          </div>
        </div>
      );
    }
    if (o?._error) {
      return (
        <div className="w-full">
          <div className="flex items-center gap-3 mb-3">
            <button
              type="button"
              className="px-4 py-3 rounded-2xl bg-slate-700/60 hover:bg-slate-700 text-white font-bold"
              onClick={() => setTripDetails(null)}
              title={t("driverChat.back")}
            >
              ←
            </button>
            <div className="text-2xl font-black">{t("common.back")}</div>
          </div>
          <div className="rounded-2xl border border-slate-700 bg-slate-800/30 p-4 text-slate-200/80">
            {t("driverOrder.loadFailed")} <span className="font-semibold">{String(o._error)}</span>
          </div>
        </div>
      );
    }
    const oid = orderIdOf(o);
    const date = formatWhen(o) || (o?.dateTime ? ruDateTime(o.dateTime) : ruDateTime(o?.createdAt));
    const from = o?.from || o?.fromAddress || o?.pickupAddress || "—";
    const to = o?.to || o?.toAddress || o?.dropoffAddress || "—";
    const weight = o?.weight ?? o?.cargoWeight ?? "—";
    const volume = o?.volume ?? o?.cargoVolume ?? "—";
    const comment = o?.comment ?? "—";
    const st = canonStatus(o);
    const stLabel = t(`status.${st}`, STATUS_RU[st] || st || "—");
    const fixedPrice = fixedOrderPriceOf(o);
    const priceSym = currencySymbolOf(priceCurrencyOf(o));
    return (
      <div
        className="w-full"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 170px)" }}
      >
        {(tripsTab !== "scheduled" || tripsScheduled.length !== 1) && (
                <div className="flex items-center gap-2 mb-3">
          <button
            className="p-2 rounded-xl bg-slate-700/70 hover:bg-slate-700 text-white"
            onClick={() => setTripDetails(null)}
            title={t("driverChat.back")}
          >
            <ChevronLeft size={18} />
          </button>
          <div className="text-white font-bold">{t("common.back")}</div>
        </div>
        )}
        <Card>
          <CardHeader className="pb-1">
            <CardTitle className="text-white font-extrabold">{date}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>{t("order.fields.from")}:</Label>
              <Value>
              <button
                type="button"
                className="text-left underline decoration-dotted underline-offset-2 hover:text-sky-100"
                onClick={() => openYandexText(from)}
              >
                {from}
              </button>
            </Value>
            </div>
            <div>
              <Label>{t("order.fields.to")}:</Label>
              <Value>
              <button
                type="button"
                className="text-left underline decoration-dotted underline-offset-2 hover:text-sky-100"
                onClick={() => openYandexText(to)}
              >
                {to}
              </button>
            </Value>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("order.fields.weight")}:</Label>
                <Value>{weight} {t("units.kg")}</Value>
              </div>
              <div>
                <Label>{t("order.fields.volume")}: </Label>
                <Value>{volume} {t("units.m3")}</Value>
              </div>
            </div>
            <div>
                          {fixedPrice != null ? (
              <div>
                <Label>{t("order.price_label")}: </Label>
                <Value>
                  <span className="text-emerald-200 font-extrabold">{moneyText(fixedPrice, { sym: priceSym })}</span>
                </Value>
              </div>
            ) : null}
            {/* PAYMENT_CHECKS_TRIPS_DETAILS_V2 */}
            <PaymentChecks
              title={paymentTitleFromOrder(
                (typeof o !== "undefined" ? o : null) ||
                (typeof order !== "undefined" ? order : null) ||
                (typeof selected !== "undefined" ? selected : null) ||
                (typeof trip !== "undefined" ? trip : null)
              )}
              items={paymentItemsFromOrder(
                (typeof o !== "undefined" ? o : null) ||
                (typeof order !== "undefined" ? order : null) ||
                (typeof selected !== "undefined" ? selected : null) ||
                (typeof trip !== "undefined" ? trip : null)
              )}
              bordered
            />
<Label>{t("order.fields.comment")}:</Label>
              <Value>{comment}</Value>
            </div>
            <div>
              <Label>{t("order.fields.order_no")}: </Label>
<div className="text-white/90 font-semibold">№{displayOrderNo( (typeof o!=="undefined" && o) || (typeof order!=="undefined" && order) || (typeof selectedOrder!=="undefined" && selectedOrder) || (typeof chatOrder!=="undefined" && chatOrder) || null )}</div>
<Label>{t("order.fields.status")}: </Label>
              <Value className={statusColorClass(st)}>{stLabel}</Value>
            </div>
            {orderCounterpartyPhone(o) ? (
              <DriverContactActions
                phone={orderCounterpartyPhone(o)}
                callLabel={t(orderCallLabelKey(o))}
                className="pt-1"
              />
            ) : null}
            <div className="pt-1">{renderStageButtons(o)}</div>
          </CardContent>
        </Card>
      </div>
    );
  };
  const renderRequestsScreen = () => {
  const orderSortTs = (o) => {
    const d =
      o?.dateTime ||
      o?.when ||
      o?.createdAt ||
      o?.created_at ||
      o?.meta?.createdAt ||
      o?.meta?.created_at ||
      null;
    const ms = d ? new Date(d).getTime() : 0;
    return Number.isFinite(ms) ? ms : 0;
  };

  const base = requestsTab === "my_offers" ? myOfferOrders : newOrders;

  // New tab: hide orders with my active offer; allow skip/hide per driver
  let list = base;
  if (requestsTab === "new" && !requestsShowSkipped) {
    list = (list || []).filter((o) => !isSkippedOrder(o));
  }

  const dir = requestsSort === "oldest" ? 1 : -1;
  list = (list || []).slice().sort((a, b) => {
    // when showing skipped, keep skipped at bottom
    if (requestsTab === "new" && requestsShowSkipped) {
      const as = isSkippedOrder(a);
      const bs = isSkippedOrder(b);
      if (as !== bs) return as ? 1 : -1;
    }
    return (orderSortTs(a) - orderSortTs(b)) * dir;
  });

  const sourceSwitch = (
    <div className="mb-3 grid grid-cols-2 gap-2 rounded-2xl border border-slate-700/50 bg-slate-900/70 p-1.5">
      <button
        type="button"
        onClick={() => setRequestsSource("reisor")}
        className={`min-h-[48px] rounded-xl px-3 text-sm font-black transition ${
          requestsSource === "reisor"
            ? "bg-blue-600 text-white shadow-lg"
            : "bg-slate-800/70 text-slate-200"
        }`}
      >
        {t("freightMarket.reisorOrders", { defaultValue: "REISOR PRO" })}
      </button>
      <button
        type="button"
        onClick={() => setRequestsSource("market")}
        className={`min-h-[48px] rounded-xl px-3 text-sm font-black transition ${
          requestsSource === "market"
            ? "bg-emerald-600 text-white shadow-lg"
            : "bg-slate-800/70 text-slate-200"
        }`}
      >
        {t("freightMarket.title", { defaultValue: "REISOR HUB" })}
      </button>
    </div>
  );

  if (requestsSource === "market") {
    return (
      <>
        {sourceSwitch}
        <DriverFreightMarket
          driverToken={driverToken}
          driverId={driver?.id || driver?._id}
          active={tabsAllowed && mainTab === "requests" && requestsSource === "market"}
          openFavoritesSignal={marketFavoritesSignal}
          openFreightId={deepMarketFreightId}
          onFreightOpenHandled={(freightId) => {
            const fid = String(freightId || "").trim();
            if (!fid || fid !== String(deepMarketFreightId || "").trim()) return;
            try { localStorage.removeItem("deep_market_freight_id"); } catch {}
            setDeepMarketFreightId("");
          }}
          onOpenSubscription={() => setMainTab("balance")}
        />
      </>
    );
  }

  return (
      <>
        {sourceSwitch}
                <div className="driver-requests-toolbar sticky top-0 z-40 px-3 pt-2 pb-2 mb-3 bg-slate-950/85 backdrop-blur border-b border-slate-700/40">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <button
                className={`min-h-[52px] px-3 rounded-2xl text-[14px] leading-tight font-semibold flex items-center justify-center text-center ${requestsTab === "new" ? "bg-blue-600 text-white" : "bg-slate-800 text-white/80"}`}
                onClick={() => setRequestsTab("new")}
              >
                {t("requests.tabs.new")}
              </button>
              <button
                className={`min-h-[52px] px-2 sm:px-3 rounded-2xl text-[12px] sm:text-[14px] leading-tight font-semibold flex items-center justify-center text-center whitespace-normal ${requestsTab === "my_offers" ? "bg-blue-600 text-white" : "bg-slate-800 text-white/80"}`} // REISOR_DRIVER_MY_OFFERS_LABEL_HOTFIX_V1
                onClick={() => setRequestsTab("my_offers")}
              >
                {t("requests.tabs.my_offers_short")}
              </button>
              <button
                className="min-h-[52px] px-2 sm:px-3 rounded-2xl bg-slate-800 text-[12px] sm:text-[14px] leading-tight font-semibold text-white/80 flex items-center justify-center text-center whitespace-normal"
                onClick={() => { setTripDetails(null); setTripsTab("scheduled"); setMainTab("trips"); }}
              >
                {t("tabs.trips")}
              </button>
            </div>

            <div className="ml-auto flex items-center gap-2">
              <button
                className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-slate-800 hover:bg-slate-700 text-white"
                onClick={() => setRequestsFiltersOpen((v) => !v)}
                title={t("driverOrder.filter")}
              >
                <Search className="w-5 h-5" />
              </button>
            </div>
          </div>

          {requestsFiltersOpen && (
            <div className="mt-2 rounded-2xl bg-slate-900/80 border border-slate-700/40 p-3">
              <div className="flex items-center gap-2">
                <div className="text-white/70 text-xs w-24">{t("requests.sort_label")}</div>
                {/* REISOR_DRIVER_REQUESTS_SORT_NATIVE_SAFE_SELECT_V1 */}
                  <NativeSafeSelect
                    className="flex-1 min-w-0"
                    buttonClassName="w-full rounded-lg border border-slate-700/60 bg-slate-800 px-2 py-2 text-left text-sm font-semibold text-white outline-none"
                    menuClassName="absolute left-0 right-0 z-[99999] mt-2 overflow-hidden rounded-xl border border-slate-700 bg-slate-950 shadow-2xl"
                    value={requestsSort}
                    onChange={setRequestsSort}
                    ariaLabel={t("requests.sort_label")}
                    options={[
                      { value: "new_first", label: t("requests.sort_newest") },
                      { value: "old_first", label: t("requests.sort_oldest") },
                    ]}
                  />
              </div>

              {requestsTab === "new" && (
                <label className="mt-3 flex items-center gap-2 text-sm text-white/80 select-none">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-red-500"
                    checked={requestsShowSkipped}
                    onChange={(e) => setRequestsShowSkipped(e.target.checked)}
                  />
                  <span>{t("requests.show_skipped")}</span>
                </label>
              )}
            </div>
          )}
        </div>
        {ordersError ? <div className="text-red-300 text-sm mb-2">❌ {ordersError}</div> : null}
        {ordersLoading ? (
          <div className="text-slate-200/70">{t("common.loading")}</div>
        ) : list.length === 0 ? (
          <div className="text-slate-200/70">{requestsTab === "my_offers" ? t("requests.empty_my_offers") : t("requests.empty")}</div>
        ) : (
          <div
            className="flex flex-col gap-4"
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 140px)" }}
          >
            {list.map((o) =>
              renderOrderCard(o, {
                canSkip: requestsTab === "new",
                isSkipped: isSkippedOrder(o),
                onSkip: skipOrderId,
                onUnskip: unskipOrderId,
                collapseMapsByDefault: list.length >= 4,
              })
            )}
          </div>
        )}
      </>
    );
  };
  const renderTripsScreen = () => {
    if (tripDetails) return renderTripDetails();
    const list = tripsTab === "past" ? tripsPast : tripsScheduled;
    return (
      <>
        {/* ✅ header that never overflows */}
        <div className="flex flex-col gap-2 mb-3">
          <div className="flex gap-2">
            <button
              onClick={() => setTripsTab("scheduled")}
              className={`flex-1 min-w-0 px-2 py-2 rounded-xl text-[12px] sm:text-sm leading-tight font-bold transition ${
                tripsTab === "scheduled" ? "bg-blue-600 text-white" : "bg-slate-700/60 text-slate-200"
              }`}
            >{t("trips.tabs.planned")}</button>
            <button
              onClick={() => setTripsTab("past")}
              className={`flex-1 min-w-0 px-2 py-2 rounded-xl text-[12px] sm:text-sm leading-tight font-bold transition ${
                tripsTab === "past" ? "bg-blue-600 text-white" : "bg-slate-700/60 text-slate-200"
              }`}
            >{t("trips.tabs.past")}</button>
            <button
              className="px-3 py-2 rounded-xl bg-slate-700/60 hover:bg-slate-700 text-white"
              onClick={() => loadMyOrders({ silent: false })}
              title={t("common.refresh_title")}
            >
              <RefreshCw size={18} />
            </button>
          </div>
        </div>
        {myOrdersLoading ? (
          <div className="text-slate-200/70">{t("common.loading")}</div>
        ) : myOrdersError ? (
          <div className="text-red-300 text-sm">❌ {myOrdersError}</div>
        ) : list.length === 0 ? (
          <div className="text-slate-200/70">{tripsTab === "past" ? t("trips.empty_past") : t("trips.empty_planned")}</div>
        ) : (
          <div
            className="flex flex-col gap-4"
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 140px)" }}
          >
            {list.map((o) => renderTripCard(o))}

            {tripsTab === "past" && list.length >= 20 ? (
              <button
                type="button"
                onClick={() => {
                  try {
                    setSettingsOpen(true);
                    setSettingsTab("support");
                  } catch {}
                }}
                className="mt-1 rounded-2xl border border-slate-600 bg-slate-800/70 px-4 py-3 text-sm font-bold text-slate-100"
              >
                {t("trips.historySupportCta", {
                  defaultValue: "Нужна более старая история? Напишите в поддержку REISOR",
                })}
              </button>
            ) : null}
          </div>
        )}
      </>
    );
  };
  // REISOR_DRIVER_OFFLINE_SINGLE_GUARD_V1:
  // Connectivity gating belongs only to the root OfflineGuard in src/main.jsx.
  // Do not block DriverWebApp on a single transient navigator offline event.

  const toUserError = (err) => {
    const msg = String(err || "").trim();
    if (!msg) return i18next.t("driverFallback.errorRetryShort");
    if (/failed to fetch|networkerror|load failed|err_network|fetch/i.test(msg)) {
      return i18next.t("driverFallback.networkShort");
    }
    return msg;
  };

  const backStateRef = useRef(null);

  useEffect(() => {
    backStateRef.current = {
      chatOpen,
      settingsOpen,
      availableVehicleOpen,
      myAdsOpen,
      myVehiclesOpen,
      myFreightsOpen,
      quickAccessOpen,
      bidOpenId,
      tripDetails,
      mainTab,
      requestsTab,
    };
  }, [chatOpen, settingsOpen, availableVehicleOpen, myAdsOpen, myVehiclesOpen, myFreightsOpen, quickAccessOpen, bidOpenId, tripDetails, mainTab, requestsTab]);

  useEffect(() => {
    let listener = null;

    const installBackHandler = async () => {
      try {
        listener = await CapApp.addListener("backButton", () => {
          const st = backStateRef.current || {};

          if (st.availableVehicleOpen) {
            setAvailableVehicleOpen(false);
            return;
          }

          if (st.myVehiclesOpen) {
            setMyVehiclesOpen(false);
            return;
          }

          if (st.myFreightsOpen) {
            setMyFreightsOpen(false);
            return;
          }

          if (st.quickAccessOpen) {
            setQuickAccessOpen(false);
            return;
          }

          if (st.myAdsOpen) {
            setMyAdsOpen(false);
            return;
          }

          if (st.chatOpen) {
            setChatOpen(false);
            return;
          }

          if (st.settingsOpen) {
            setSettingsOpen(false);
            setSettingsTab("main");
            return;
          }

          if (st.bidOpenId) {
            setBidOpenId(null);
            return;
          }

          if (st.tripDetails) {
            setTripDetails(null);
            return;
          }

          if (st.mainTab === "profile" || st.mainTab === "balance" || st.mainTab === "trips" || st.mainTab === "vehicles") {
            setMainTab("requests");
            return;
          }

          if (st.requestsTab === "my_offers") {
            setRequestsTab("new");
            return;
          }

          // root screen:
          // keep app open, do not minimize, do not exit
        });
      } catch (e) {
        console.warn("install backButton listener failed", e);
      }
    };

    installBackHandler();

    return () => {
      try {
        listener?.remove?.();
      } catch {}
    };
  }, []);


  return (
    <div className={`app-shell w-full bg-slate-900 text-white ${(settingsOpen || chatOpen || aiOpen || availableVehicleOpen || myAdsOpen || myVehiclesOpen || myFreightsOpen || quickAccessOpen) ? "modal-open" : ""}`}>
      <div className="page-scroll" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 140px)" }}>
      {driverLegalDoc ? (
        <LegalDocsModal
          title={driverLegalDoc.title}
          url={driverLegalDoc.url}
          onClose={() => setDriverLegalDoc(null)}
        />
      ) : null}
      {fatalError ? (
        <div className="fixed inset-0 z-[2500] bg-black/90 p-4 overflow-auto">
          <div className="mx-auto max-w-3xl">
            <div className="text-red-300 font-extrabold text-lg mb-2">{t("driverError.title")}</div>
            <div className="text-red-200 text-sm mb-3">{t("driverError.copyHint")}</div>
            <pre className="whitespace-pre-wrap text-xs text-red-100 border border-red-500/30 rounded-xl p-3 bg-red-950/20">{fatalError}</pre>
            <div className="mt-3 flex gap-2">
              <button
                className="rounded-xl bg-slate-800 px-4 py-2 text-sm font-bold border border-slate-700"
                onClick={() => setFatalError("")}
              >
                {t("driverError.close")}
              </button>
              <button
                className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold"
                onClick={() => window.location.reload()}
              >
                {t("driverError.reload")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {/* REISOR_STAGE_P9_DRIVER_DURABLE_INBOX_PANEL_V1 */}
      {incomingFreightInvite ? (() => {
        const route = [
          String(incomingFreightInvite?.from || "").trim(),
          String(incomingFreightInvite?.to || "").trim(),
        ].filter(Boolean).join(" → ");
        const details = [
          String(incomingFreightInvite?.cargo || "").trim(),
          Number(incomingFreightInvite?.weightTons || 0) > 0
            ? `${Number(incomingFreightInvite.weightTons)} т`
            : "",
        ].filter(Boolean).join(" · ");
        const inviteId = String(incomingFreightInvite?.id || "").trim();
        const freightId = String(incomingFreightInvite?.marketFreightId || "").trim();

        return (
          <div className="fixed left-0 right-0 top-3 z-[10030] flex justify-center px-3">
            <div className="w-full max-w-md rounded-3xl border border-emerald-200 bg-emerald-600 px-5 py-4 text-white shadow-2xl">
              <div className="text-center text-lg font-extrabold leading-snug">
                {t("driverToast.freightInvite", {
                  route: route || "—",
                  details: details ? ` · ${details}` : "",
                  defaultValue: `📦 Новый груз REISOR: ${route || "—"}${details ? ` · ${details}` : ""}`,
                })}
              </div>
              <button
                type="button"
                onClick={() => {
                  setIncomingFreightInvite(null);
                  driverFreightInviteInbox.markOpened(inviteId).catch(() => {});
                  if (freightId) {
                    try { localStorage.setItem("deep_market_freight_id", freightId); } catch {}
                    try {
                      window.dispatchEvent(
                        new CustomEvent("reisor_driver_open_market_freight", { detail: { freightId } })
                      );
                    } catch {}
                  }
                }}
                className="mt-4 mx-auto block w-72 rounded-2xl border border-white bg-white px-6 py-4 text-lg font-extrabold text-slate-900"
              >
                {t("driverToast.freightInviteOpen", { defaultValue: "Открыть груз" })}
              </button>
            </div>
          </div>
        );
      })() : null}

      {/* toast */}
      {toast ? (
        <div className="driver-toast-host fixed left-0 right-0 z-[10020] flex justify-center px-3">
          <div
            className={`w-full max-w-md rounded-3xl px-6 py-5 text-lg font-extrabold shadow-2xl border ${
              toast.type === "ok"
                ? "bg-emerald-600 border-emerald-200 text-white"
                : "bg-red-600 border-red-200 text-white"
            }`}
          >
            <div className="text-center leading-snug">{toast.text}</div>
            {toast.actionText ? (
              <button
                type="button"
                onClick={() => {
                  try { if (typeof toast.onAction === "function") toast.onAction(); } catch {}
                  setToast(null);
                }}
                className="mt-5 mx-auto block w-72 rounded-2xl bg-white text-slate-900 border border-white px-6 py-4 text-lg font-extrabold"
              >
                {toast.actionText}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {driverAccessLimited ? (
        <div className="mx-auto max-w-md px-3 pt-3">
          <div className="rounded-2xl border border-red-400/40 bg-red-500/10 px-4 py-3 text-red-100 shadow-xl">
            <div className="text-sm font-black">
              {t("driverAccess.ordersLimitedTitle", { defaultValue: "Доступ к заказам ограничен" })}
            </div>
            <div className="mt-1 text-xs leading-relaxed">
              {driverAccessLimitedMessage}
            </div>
            <button
              type="button"
              onClick={() => { setSettingsOpen(true); setSettingsTab("support"); }}
              className="mt-3 rounded-xl border border-red-300/40 bg-red-500/20 px-3 py-2 text-xs font-bold text-red-50"
            >
              {t("driverAccess.contactSupport", { defaultValue: "Написать в поддержку" })}
            </button>
          </div>
        </div>
      ) : null}
{/* settings gear */}      {/* SOUND_SETTINGS_MODAL_V2 */}
      {settingsOpen ? (
        <div className="driver-settings-overlay fixed inset-0 z-[10050] bg-black/60 overflow-y-auto">
          <div
            className="driver-settings-shell min-h-[100dvh] flex items-start sm:items-center justify-center p-3 sm:p-4"
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 18px)" }}
          >
          <div
            className={`driver-settings-panel modal-scroll-panel w-full max-w-md overflow-hidden ${
              settingsTab === "support"
                ? "bg-slate-900"
                : "rounded-3xl border border-slate-700 bg-slate-900 shadow-2xl"
            }`}
            style={{ maxHeight: "calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 24px)" }}
          >
            <div className="driver-settings-header flex items-center justify-between px-4 py-3 border-b border-slate-700">
              <div className="text-lg font-black">
                {settingsTab === "sound"
                  ? t("sounds.manage")
                  : settingsTab === "referral"
                  ? referralCopy.modalTitle
                  : settingsTab === "support"
                  ? t("support.title")
                  : settingsTab === "work_status"
                  ? t("profile.workStatusTitle", { defaultValue: "Статус работы" })
                  : settingsTab === "legal"
                  ? t("legalHub.title")
                  : t("settings_ui.title")}
              </div>
              <button
                type="button"
                onClick={() => { setSettingsOpen(false); setSettingsTab("main"); }}
                className="driver-settings-close px-3 py-2 rounded-xl border border-slate-700 bg-slate-800/40"
              >
                ✕
              </button>
            </div>
            <div
              className={
                settingsTab === "support"
                  ? "driver-support-settings-body min-h-0 flex flex-col gap-1.5 overflow-hidden px-3 py-2"
                  : "driver-settings-body p-4 flex flex-col gap-3 overflow-y-auto"
              }
              style={{ maxHeight: "calc(100dvh - 120px)" }}
            >
              {settingsTab === "main" ? (
                <>
                  {/* REISOR_DRIVER_SETTINGS_FLAT_MOBILE_V2 */}
                  {/* REISOR_DRIVER_SETTINGS_LANGUAGE_SINGLE_HOME_V1 */}
                  {/* Language is the first control in Settings; Profile no longer owns language selection. */}
                  <div className="driver-settings-language-home">
                    <LanguageSelector />
                  </div>

                  <section className="driver-settings-section">
                    <div
                      className="driver-settings-section-label mb-2 px-1 text-xs font-black
                        uppercase tracking-[0.12em] text-slate-400"
                    >
                      {t("settings_ui.section_profile", {
                        defaultValue: "Профиль и работа",
                      })}
                    </div>

                    <div
                      className="driver-settings-group overflow-hidden rounded-3xl border
                        border-slate-700/80 bg-slate-950/25
                        shadow-lg shadow-black/10"
                    >
                      <DriverSettingsRow
                        icon={UserRound}
                        title={t("settings_ui.profile_link")}
                        description={t(
                          "settings_ui.profile_desc",
                          {
                            defaultValue:
                              "Личные данные, транспорт и приоритет.",
                          }
                        )}
                        onClick={() => {
                          setMainTab("profile");
                          setSettingsOpen(false);
                          setSettingsTab("main");
                        }}
                      />

                      <DriverSettingsRow
                        icon={BriefcaseBusiness}
                        title={t(
                          "settings_ui.work_status_link",
                          { defaultValue: "Статус работы" }
                        )}
                        description={t(
                          "settings_ui.work_status_desc",
                          {
                            defaultValue:
                              "ИП, самозанятый, ООО / компания или парк / логист.",
                          }
                        )}
                        onClick={openSettingsWorkStatus}
                        tone="sky"
                      />

                      <DriverSettingsRow
                        icon={Wallet}
                        title={t("tabs.balance", { defaultValue: "Баланс" })}
                        description={t("settings_ui.balance_desc", {
                          defaultValue: "Баланс, подписка и платежи.",
                        })}
                        onClick={() => {
                          setMainTab("balance");
                          setSettingsOpen(false);
                          setSettingsTab("main");
                        }}
                        tone="emerald"
                      />
                    </div>
                  </section>

                  <section className="driver-settings-section">
                    <div
                      className="driver-settings-section-label mb-2 px-1 text-xs font-black
                        uppercase tracking-[0.12em] text-slate-400"
                    >
                      {t("settings_ui.section_help", {
                        defaultValue: "Помощь и документы",
                      })}
                    </div>

                    <div
                      className="driver-settings-group overflow-hidden rounded-3xl border
                        border-slate-700/80 bg-slate-950/25
                        shadow-lg shadow-black/10"
                    >
                      <DriverSettingsRow
                        icon={FileText}
                        title={t("legalHub.settings_link")}
                        description={t("legalHub.settings_desc")}
                        onClick={() => setSettingsTab("legal")}
                        tone="emerald"
                      />

                      <DriverSettingsRow
                        icon={LifeBuoy}
                        title={t("support.settings_link")}
                        description={t(
                          "settings_ui.support_desc",
                          {
                            defaultValue:
                              "Чат со службой поддержки REISOR.",
                          }
                        )}
                        onClick={() => setSettingsTab("support")}
                        tone="amber"
                      />
                    </div>
                  </section>

                  <section className="driver-settings-section">
                    <div
                      className="driver-settings-section-label mb-2 px-1 text-xs font-black
                        uppercase tracking-[0.12em] text-slate-400"
                    >
                      {t("settings_ui.section_app", {
                        defaultValue: "Приложение",
                      })}
                    </div>

                    <div
                      className="driver-settings-group overflow-hidden rounded-3xl border
                        border-slate-700/80 bg-slate-950/25
                        shadow-lg shadow-black/10"
                    >
                      <DriverSettingsRow
                        icon={Share2}
                        title={referralCopy.cardTitle}
                        description={referralCopy.cardText}
                        onClick={() => setSettingsTab("referral")}
                      />

                      <DriverSettingsRow
                        icon={Volume2}
                        title={t("settings_ui.sound_link")}
                        description={t("settings_ui.sound_desc")}
                        onClick={() => setSettingsTab("sound")}
                      />

                      <div
                        className="driver-settings-theme-row flex items-center gap-3 border-t
                          border-slate-700/60 px-4 py-4"
                      >
                        <span
                          className="driver-settings-row-icon flex h-11 w-11 shrink-0
                            items-center justify-center rounded-2xl
                            border border-white/10 bg-black/15
                            text-slate-100"
                        >
                          <Sun size={21} strokeWidth={2.2} />
                        </span>

                        <div className="min-w-0 flex-1">
                          <div
                            className="text-base font-black
                              leading-tight"
                          >
                            {t("settings_ui.theme_title")}
                          </div>

                          <div
                            className="mt-1 text-xs leading-5
                              text-slate-300/85"
                          >
                            {t("settings_ui.theme_desc")}
                          </div>
                        </div>

                        <label
                          className="relative inline-flex shrink-0
                            cursor-pointer items-center select-none"
                        >
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={uiTheme === "light"}
                            onChange={(event) =>
                              setUiTheme(
                                event.target.checked
                                  ? "light"
                                  : "dark"
                              )
                            }
                          />

                          <div
                            className={`h-8 w-14 rounded-full border
                              border-slate-600 bg-slate-900/70
                              transition ${
                                uiTheme === "light"
                                  ? "ring-2 ring-emerald-400/40"
                                  : ""
                              }`}
                          >
                            <div
                              className={`h-7 w-7 translate-y-[1px]
                                rounded-full bg-white shadow
                                transition ${
                                  uiTheme === "light"
                                    ? "translate-x-6"
                                    : "translate-x-0.5"
                                }`}
                            />
                          </div>
                        </label>
                      </div>
                    </div>
                  </section>

                  {pushSupported ? (
                    <section
                      className="driver-settings-push rounded-3xl border
                        border-slate-700/80 bg-slate-950/25 p-4
                        shadow-lg shadow-black/10"
                    >
                      <div className="flex items-start gap-3">
                        <span
                          className="driver-settings-row-icon flex h-11 w-11 shrink-0
                            items-center justify-center rounded-2xl
                            border border-white/10 bg-black/15
                            text-slate-100"
                        >
                          <Bell size={21} strokeWidth={2.2} />
                        </span>

                        <div className="min-w-0 flex-1">
                          <div
                            className="flex items-center
                              justify-between gap-3"
                          >
                            <div className="text-base font-black">
                              {t("driverPush.title")}
                            </div>

                            <div
                              className={`rounded-full border px-3
                                py-1 text-xs font-black ${
                                  pushChannelStatus.ready &&
                                  pushRequiredReady
                                    ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-200"
                                    : "border-amber-500/30 bg-amber-500/15 text-amber-200"
                                }`}
                            >
                              {pushChannelStatus.ready &&
                              pushRequiredReady
                                ? t("driverPush.enabled")
                                : t("driverPush.disabled")}
                            </div>
                          </div>

                          <div
                            className="mt-1 text-xs leading-5
                              text-slate-300/85"
                          >
                            {t("driverPush.requiredOnline")}
                          </div>

                          {pushChannelStatus.loaded ? (
                            <div className="mt-1 text-[11px] font-bold text-slate-400">
                              {t("driverPush.channels", {
                                native: pushChannelStatus.nativeTokens,
                                web: pushChannelStatus.webSubscriptions,
                              })}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {pushErr ? (
                        <div className="mt-3 text-xs text-red-200/90">
                          {cleanErr(pushErr)}
                        </div>
                      ) : null}

                      {/* REISOR_DRIVER_PUSH_LOCK_WARNING_RESTORE_V1 */}
                      {!(
                        pushChannelStatus.ready &&
                        pushRequiredReady
                      ) ? (
                        <div
                          className="mt-3 rounded-2xl border
                            border-amber-500/30 bg-amber-500/10
                            px-4 py-3 text-xs leading-5
                            text-amber-100"
                        >
                          {t("driverPush.locked")}
                        </div>
                      ) : null}

                      <div
                        className="mt-3 grid grid-cols-1 gap-2
                          sm:grid-cols-2"
                      >
                        {!(
                          pushChannelStatus.ready &&
                          pushRequiredReady
                        ) ? (
                          <button
                            type="button"
                            onClick={enablePush}
                            disabled={pushBusy}
                            className="rounded-2xl bg-emerald-600
                              px-4 py-3 text-sm font-black
                              text-white disabled:opacity-60"
                          >
                            {pushBusy
                              ? t("driverPush.enabling")
                              : t("driverPush.enableButton")}
                          </button>
                        ) : null}

                        <button
                          type="button"
                          onClick={testPush}
                          disabled={
                            pushBusy ||
                            !driverToken ||
                            (isNativeAndroidRuntime
                              ? nativeNotificationPermission !== "granted"
                              : pushPermission !== "granted")
                          }
                          className="rounded-2xl border
                            border-slate-700 bg-slate-800/40
                            px-4 py-3 text-sm font-black
                            disabled:opacity-60"
                        >
                          {t("driverPush.testSound")}
                        </button>
                      </div>
                    </section>
                  ) : null}

                  {/* REISOR_DRIVER_SETTINGS_LOGOUT_V1 */}
                  <section className="driver-settings-section">
                    <div
                      className="driver-settings-section-label mb-2 px-1 text-xs font-black
                        uppercase tracking-[0.12em] text-slate-400"
                    >
                      {t("settings_ui.section_account", {
                        defaultValue: "Аккаунт",
                      })}
                    </div>

                    <div
                      className="driver-settings-group overflow-hidden rounded-3xl border
                        border-rose-400/25 bg-rose-500/[0.06]
                        shadow-lg shadow-black/10"
                    >
                      <DriverSettingsRow
                        icon={LogOut}
                        title={t("settings_ui.logout_title", {
                          defaultValue: "Выйти из аккаунта",
                        })}
                        description={t("settings_ui.logout_desc", {
                          defaultValue:
                            "Завершить текущую сессию на этом устройстве.",
                        })}
                        tone="rose"
                        onClick={() => {
                          const confirmed = window.confirm(
                            t("settings_ui.logout_confirm", {
                              defaultValue:
                                "Выйти из аккаунта на этом устройстве?",
                            })
                          );

                          if (!confirmed) return;

                          setSettingsOpen(false);
                          setSettingsTab("main");

                          window.dispatchEvent(
                            new Event("gptcargo_driver_force_login")
                          );
                        }}
                      />
                    </div>
                  </section>

                  <div
                    className="pt-1 text-center text-xs
                      font-semibold text-slate-400"
                  >
                    {t("settings_ui.version", {
                      version: DRIVER_APP_VERSION_NAME,
                      code: String(DRIVER_APP_VERSION_CODE),
                      defaultValue:
                        `Версия ${DRIVER_APP_VERSION_NAME} (${DRIVER_APP_VERSION_CODE})`,
                    })}
                  </div>
                </>

              ) : settingsTab === "legal" ? (
                <>
                  {/* REISOR_STAGE97G2_LEGAL_UI_HUB_V2 */}
                  <button
                    type="button"
                    onClick={() => setSettingsTab("main")}
                    className="w-fit rounded-2xl border border-slate-700 bg-slate-800/40 px-3 py-2 text-sm font-semibold"
                  >
                    ← {t("common.back")}
                  </button>

                  <div className="rounded-3xl border border-emerald-300/25 bg-emerald-300/10 p-4">
                    <div className="text-xl font-black">{t("legalHub.title")}</div>
                    <div className="mt-1 text-xs leading-5 text-emerald-100/85">
                      {t("legalHub.subtitle")}
                    </div>

                    <div className="mt-4 grid grid-cols-1 gap-3">
                      {driverLegalBundle.loading ? (
                        <div className="rounded-2xl border border-slate-700 bg-slate-950/40 px-4 py-3 text-sm text-slate-300">
                          {driverLegalLang(i18n) === "uz" ? "Ҳужжатлар юкланмоқда…" : driverLegalLang(i18n) === "tr" ? "Belgeler yükleniyor…" : "Загружаем актуальные документы…"}
                        </div>
                      ) : driverLegalBundle.error ? (
                        <div role="alert" className="rounded-2xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-slate-200">
                          {driverLegalLang(i18n) === "uz" ? "Ҳужжатларни юклаб бўлмади. Қайта кириб кўринг." : driverLegalLang(i18n) === "tr" ? "Belgeler yüklenemedi. Lütfen tekrar deneyin." : "Не удалось загрузить актуальные документы. Повторите попытку."}
                        </div>
                      ) : driverLegalBundle.documents.map((doc) => (
                        <button
                          key={`${doc.documentNumber}:${doc.version}`}
                          type="button"
                          onClick={(event) => {
                            try { event.preventDefault(); event.stopPropagation(); } catch {}
                            setDriverLegalDoc({ title: doc.title, url: absoluteDriverLegalV5Url(doc.printUrl) });
                          }}
                          className="w-full rounded-2xl border border-slate-700 bg-slate-950/40 px-4 py-3 text-left"
                        >
                          <div className="font-bold text-slate-50">{doc.title}</div>
                          <div className="mt-1 text-xs leading-5 text-slate-300/85">
                            {driverLegalLang(i18n) === "uz" ? "Амалдаги" : driverLegalLang(i18n) === "tr" ? "Güncel" : "Действующий"}
                            {doc.version ? ` • v${doc.version}` : ""}
                          </div>
                        </button>
                      ))}
                    </div>

                    <div className="mt-4 rounded-2xl border border-slate-700 bg-slate-950/40 p-3 text-xs leading-5 text-slate-300/85">
                      {t("legalHub.notice")}
                    </div>
                  </div>
                </>

              ) : settingsTab === "work_status" ? (
                <>
                  {/* REISOR_STAGE97D4_DRIVER_SETTINGS_WORK_STATUS_INLINE_V1 */}
                  <button
                    type="button"
                    onClick={() => setSettingsTab("main")}
                    className="w-fit rounded-2xl border border-slate-700 bg-slate-800/40 px-3 py-2 text-sm font-semibold"
                  >
                    ← {t("common.back")}
                  </button>

                  <div className="rounded-3xl border border-sky-300/25 bg-sky-300/10 p-4">
                    <div className="text-xl font-black">
                      {t("profile.workStatusTitle", { defaultValue: "Статус работы" })}
                    </div>
                    <div className="mt-1 text-xs leading-5 text-sky-100/85">
                      {t("profile.workStatusSubtitle", {
                        defaultValue: "Укажите, от чьего имени вы направляете предложения по заказам.",
                      })}
                    </div>

                    {settingsWorkStatusLoading ? (
                      <div className="mt-3 text-xs text-slate-300">
                        {t("profile.loading", { defaultValue: "Загрузка…" })}
                      </div>
                    ) : null}

                    {settingsWorkStatusError ? (
                      <div className="mt-3 rounded-2xl border border-red-400/30 bg-red-500/10 p-3 text-xs text-red-100">
                        {settingsWorkStatusError}
                      </div>
                    ) : null}

                    <div className="mt-4 grid grid-cols-1 gap-3">
                      {/* REISOR_DRIVER_WORK_STATUS_NATIVE_SAFE_SELECT_V1 */}
                        <NativeSafeSelect
                          className="w-full"
                          buttonClassName="w-full rounded-2xl border border-slate-700 bg-slate-950/60 px-4 py-3 text-left text-sm font-semibold text-white outline-none disabled:opacity-60"
                          menuClassName="absolute left-0 right-0 z-[99999] mt-2 overflow-hidden rounded-2xl border border-slate-700 bg-slate-950 shadow-2xl"
                          value={settingsWorkStatusForm.carrierStatus || ""}
                          disabled={settingsWorkStatusSaving || settingsWorkStatusLoading}
                          ariaLabel={t("profile.workStatusHint", {
                            defaultValue: "Выберите, от чьего имени вы направляете предложения по заказам.",
                          })}
                          onChange={(next) => {
                            setSettingsWorkStatusForm((p) => ({
                              ...p,
                              carrierStatus: next,
                              inn: driverWorkStatusNeedsInn(next) ? p.inn : "",
                              carrierName: driverWorkStatusNeedsName(next) ? p.carrierName : "",
                            }));
                          }}
                          options={[
                            {
                              value: "",
                              label: `${t("profile.carrierStatus", { defaultValue: "Статус работы" })}…`,
                            },
                            ...DRIVER_WORK_STATUS_OPTIONS.map((x) => ({
                              value: x.value,
                              label: t(x.key, { defaultValue: x.fallback }),
                            })),
                          ]}
                        />

                      {driverWorkStatusNeedsName(settingsWorkStatusForm.carrierStatus) ? (
                        <input
                          className="w-full rounded-2xl border border-slate-700 bg-slate-950/60 px-4 py-3 text-sm font-semibold text-white outline-none placeholder:text-slate-400"
                          value={settingsWorkStatusForm.carrierName || ""}
                          disabled={settingsWorkStatusSaving || settingsWorkStatusLoading}
                          onChange={(e) =>
                            setSettingsWorkStatusForm((p) => ({
                              ...p,
                              carrierName: String(e.target.value || "").slice(0, 160),
                            }))
                          }
                          placeholder={
                            settingsWorkStatusForm.carrierStatus === "company"
                              ? t("profile.carrierCompanyName", { defaultValue: "Название ООО / компании" })
                              : t("profile.carrierParkName", { defaultValue: "Название парка / логиста" })
                          }
                          title={t("profile.carrierNameHint", {
                            defaultValue: "Название указывается со слов водителя. Подтверждение можно будет добавить позже.",
                          })}
                        />
                      ) : null}

                      {driverWorkStatusNeedsInn(settingsWorkStatusForm.carrierStatus) ? (
                        <input
                          className="w-full rounded-2xl border border-slate-700 bg-slate-950/60 px-4 py-3 text-sm font-semibold text-white outline-none placeholder:text-slate-400"
                          inputMode="numeric"
                          value={settingsWorkStatusForm.inn || ""}
                          disabled={settingsWorkStatusSaving || settingsWorkStatusLoading}
                          onChange={(e) =>
                            setSettingsWorkStatusForm((p) => ({
                              ...p,
                              inn: String(e.target.value || "").replace(/\D/g, "").slice(0, 12),
                            }))
                          }
                          placeholder={
                            settingsWorkStatusForm.carrierStatus === "company"
                              ? t("profile.companyInn", { defaultValue: "ИНН компании" })
                              : t("profile.inn", { defaultValue: "ИНН" })
                          }
                          title={t("profile.innHint", {
                            defaultValue: "ИНН используется как заявленный водителем правовой статус работы.",
                          })}
                        />
                      ) : null}
                    </div>

                    {settingsWorkStatusForm.carrierStatus ? (
                      <div className="mt-3 rounded-2xl border border-sky-300/20 bg-black/15 px-3 py-2 text-xs leading-5 text-sky-50">
                        {t("profile.workStatusDisclaimer", {
                          defaultValue:
                            "Статус указан водителем. REISOR не является перевозчиком и не оформляет перевозку от своего имени.",
                        })}
                      </div>
                    ) : null}

                    <button
                      type="button"
                      disabled={settingsWorkStatusSaving || settingsWorkStatusLoading}
                      onClick={saveSettingsWorkStatus}
                      className="mt-4 w-full rounded-2xl bg-sky-600 px-5 py-4 text-base font-black text-white disabled:opacity-60"
                    >
                      {settingsWorkStatusSaving
                        ? t("profile.workStatusSaving", { defaultValue: "Сохраняем статус…" })
                        : t("profile.workStatusSave", { defaultValue: "Сохранить статус работы" })}
                    </button>

                    <button
                      type="button"
                      onClick={() => { setMainTab("profile"); setSettingsOpen(false); setSettingsTab("main"); }}
                      className="mt-3 w-full rounded-2xl border border-slate-700 bg-slate-800/40 px-5 py-3 text-sm font-semibold text-slate-100"
                    >
                      {t("settings_ui.open_full_profile", { defaultValue: "Открыть полный профиль" })}
                    </button>
                  </div>
                </>
              ) : settingsTab === "support" ? (

                <>
                  {/* REISOR_SUPPORT_CHAT_COMPACT_UI_V31 — support branch only */}
                  <button
                    type="button"
                    onClick={() => setSettingsTab("main")}
                    className="w-fit px-1 py-1 text-sm font-semibold text-slate-300"
                  >
                    ← {t("common.back")}
                  </button>
                  <DriverSupportChat driverToken={driverToken} />
                </>
              ) : settingsTab === "referral" ? (

                <>
                  <button
                    type="button"
                    onClick={() => setSettingsTab("main")}
                    className="w-fit rounded-2xl border border-slate-700 bg-slate-800/40 px-3 py-2 text-sm font-semibold"
                  >
                    ← {t("common.back")}
                  </button>
                  <DriverReferralPromoCard token={driverToken} driver={driver} />
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setSettingsTab("main")}
                    className="w-fit rounded-2xl border border-slate-700 bg-slate-800/40 px-3 py-2 text-sm font-semibold"
                  >
                    ← {t("common.back")}
                  </button>
                  <label className="flex items-center gap-3 text-sm">
                    <input
                      type="checkbox"
                      checked={!!soundCfg.enabled}
                      onChange={(e) => setSoundCfg((v) => ({ ...v, enabled: e.target.checked }))}
                    />
                    {t("sounds.enabled")}
                  </label>
                  <label className="flex items-center gap-3 text-sm">
                    <input
                      type="checkbox"
                      checked={!!soundCfg.vibrate}
                      onChange={(e) => setSoundCfg((v) => ({ ...v, vibrate: e.target.checked }))}
                    />
                    {t("settings_ui.vibration")}
                  </label>
                  <div className="text-sm">
                    <div className="mb-2 font-semibold">{t("settings_ui.volume")}</div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={Math.round((Number(soundCfg.volume ?? 0.8)) * 100)}
                      onChange={(e) => setSoundCfg((v) => ({ ...v, volume: Number(e.target.value) / 100 }))}
                      className="w-full"
                    />
                  </div>
                  {[
                    { key: "newOrder", title: t("sounds.new_order") },
                    { key: "assignedOrder", title: t("sounds.assigned_order") },
                    { key: "chat", title: t("sounds.chat") },
                  ].map((row) => (
                    <div key={row.key} className="rounded-2xl border border-slate-700 bg-slate-800/30 p-3">
                      <div className="font-semibold mb-2">{row.title}</div>
                      <div className="flex items-center gap-2">
                        {/* REISOR_DRIVER_SOUND_NATIVE_SAFE_SELECT_V1 */}
                          <NativeSafeSelect
                            className="flex-1 min-w-0"
                            buttonClassName="sound-select w-full text-left"
                            menuClassName="absolute left-0 right-0 z-[99999] mt-2 overflow-hidden rounded-2xl border border-slate-700 bg-slate-950 shadow-2xl"
                            value={soundCfg[row.key] || "off"}
                            onChange={(next) => setSoundCfg((v) => ({ ...v, [row.key]: next }))}
                            ariaLabel={row.title}
                            options={[
                              { value: "off", label: t("sounds.preset.off") },
                              { value: "reisor_order", label: i18next.t("driverSound.order") },
                              { value: "reisor_assign", label: i18next.t("driverSound.assign") },
                              { value: "reisor_chat", label: i18next.t("driverSound.chat") },
                              { value: "happy", label: t("sounds.preset.happy") },
                              { value: "ding", label: t("sounds.preset.ding") },
                              { value: "double", label: t("sounds.preset.double") },
                              { value: "buzz", label: t("sounds.preset.buzz") },
                              { value: "custom", label: t("sounds.preset.custom") },
                            ]}
                          />
                        <button
                          type="button"
                          onClick={() => playEventSound(row.key, { force: true, preview: true })}
                          className="rounded-xl border border-emerald-500/40 bg-emerald-600/20 px-3 py-2 text-sm font-semibold"
                        >
                          {previewPlayingKey === row.key ? "■" : "▶"}
                        </button>
                      </div>
                      {soundCfg[row.key] === "custom" ? (
                        <div className="mt-2 text-sm">
                          <input
                            type="file"
                            accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac"
                            onChange={(e) => handleUploadSound(row.key, e.target.files?.[0])}
                          />
                          <div className="text-xs text-slate-300/80 mt-1">
                            {t("settings_ui.pick_file")}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
        </div>
      ) : null}
      {/* FULLSCREEN content (no wrapper) */}
      <div className="w-full px-3 pt-3">
        {tabsAllowed && !pushRequiredReady ? (
          <div className="mb-3 rounded-3xl border border-amber-500/40 bg-amber-950/40 p-5 text-amber-50 shadow-2xl">
            <div className="text-2xl font-black mb-3">{t("driverPush.disabledTitle")}</div>
            <div className="text-sm leading-6 text-amber-100/90">
              {t("driverPush.disabledDesc1")}
              <br />
              {t("driverPush.disabledDesc2")}
            </div>
            <div className="mt-4 rounded-2xl border border-amber-500/30 bg-slate-950/35 p-3 text-xs leading-5 text-amber-100/80">
              {t("driverPush.checkDesc1")}
              {t("driverPush.checkDesc2")}
            </div>
            <button
              type="button"
              onClick={() => openNotificationSettings().catch(() => {})}
              disabled={pushBusy}
              className="mt-4 w-full rounded-2xl bg-amber-400 px-5 py-4 text-base font-black text-slate-950 disabled:opacity-60"
            >
              {pushBusy ? t("driverPush.checking") : t("driverPush.openNotificationSettings")}
            </button>
          </div>
        ) : null}
        {showPendingBanner && (
          <div className="mb-3 p-4 rounded-2xl bg-yellow-900/30 text-yellow-200 border border-yellow-700/30">
            {t("profile.pending_banner")}
          </div>
        )}
        {tabsAllowed && (mainTab === "requests" || mainTab === "vehicles") ? <DriverMarketStatusQuickBar /> : null}
        {tabsAllowed && (mainTab === "requests" || mainTab === "vehicles") && (
          mainTab === "vehicles"
            ? (
              <ReisorVehicleMarket
                token={driverToken}
                showHeader={false}
                initialFilters={vehicleSearchPreset?.filters || null}
                initialFiltersKey={vehicleSearchPreset?.key || ""}
                actorRole="DRIVER"
                onCreateFreightForVehicle={beginDriverFreightForVehicle}
                onCreateFreightForDriver={beginDriverFreightForDriver}
              />
            )
            : renderRequestsScreen()
        )}

        {tabsAllowed && mainTab === "trips" && renderTripsScreen()}
        {mainTab === "balance" && (
          <DriverBalance
            driverToken={driverToken}
            onOpenOrder={(orderId) => {
              // BALANCE_OPEN_ORDER_V2
              if (!orderId) return;
              if (!driverCanWorkOnline) {
                showPushRequiredWarning();
                return;
              }
              const oid = String(orderId);
              setTripsTab("scheduled");
              setMainTab("trips");
              openTripById(oid);
            }}
          />
        )}
        {mainTab === "profile" && <DriverProfile />}
        </div>
      </div>
      {aiWelcomeOpen && tabsAllowed && !driverAccessLimited && !settingsOpen && !chatOpen && !aiOpen && !availableVehicleOpen && !myAdsOpen && !myVehiclesOpen && !myFreightsOpen && !quickAccessOpen ? (
        <div className="fixed inset-0 z-[10040] flex items-center justify-center bg-black/55 px-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-[420px] rounded-3xl border border-slate-700 bg-slate-950 p-5 text-white shadow-2xl">
            <button type="button" onClick={closeAiWelcomeForSession} className="float-right flex h-9 w-9 items-center justify-center rounded-xl bg-slate-800 text-slate-300" aria-label={t("reisorAi.close", { defaultValue: "Закрыть" })}>
              <X size={18} />
            </button>
            <div className="pr-10 text-xl font-black">
              {t("reisorAi.launcherGreeting", { defaultValue: "Добрый день, {{name}}. Я помогу быстрее найти работу. Что будем делать?", name: driverAiFirstName(driverObj) || t("reisorAi.launcherDriverFallback", { defaultValue: "водитель" }) })}
            </div>
            <div className="mt-5 grid gap-2">
              <button type="button" onClick={() => { closeAiWelcomeForSession(); setAiEntryIntent("freight"); setAiOpen(true); }} className="min-h-[48px] rounded-2xl bg-blue-600 px-4 text-sm font-black text-white">
                {t("reisorAi.launcherFindFreight", { defaultValue: "Найти груз" })}
              </button>
              <button type="button" onClick={() => { closeAiWelcomeForSession(); prepareAiAvailableVehicle(); }} className="min-h-[48px] rounded-2xl bg-emerald-600 px-4 text-sm font-black text-white">
                {t("reisorAi.launcherPublishVehicle", { defaultValue: "Оформить свободную машину" })}
              </button>
              <div className="pt-1 text-center text-xs font-semibold leading-relaxed text-slate-400">
                {t("reisorAi.launcherHint", { defaultValue: "Можно написать обычными словами: «Москва Ташкент реф 20т»" })}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <DriverAiAssistant
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        driverToken={driverToken}
        driverId={driver?.id || driver?._id}
        driver={driverObj || driver}
        onOpenFreight={openAiFreight}
        onShowInMarket={showAiSearchInMarket}
        onPrepareVehicle={prepareAiAvailableVehicle}
        onOpenVehicleMarket={(nextFilters = {}) => {
          setVehicleSearchPreset({
            key: `ai:${Date.now()}`,
            filters: {
              from: String(nextFilters?.from || "").trim(),
              to: String(nextFilters?.to || "").trim(),
              bodyType: String(
                nextFilters?.bodyType ||
                nextFilters?.truck ||
                ""
              ).trim(),
              capacityTons:
                nextFilters?.capacityTons ?? null,
              volumeM3:
                nextFilters?.volumeM3 ?? null,
              readyAt:
                nextFilters?.readyAt || "",
              partialLoad:
                Boolean(nextFilters?.partialLoad),
            },
          });
          setAiOpen(false);
          setMainTab("vehicles");
        }}
        entryIntent={aiEntryIntent}
      />

      {tabsAllowed && !driverAccessLimited && !aiWelcomeOpen && !settingsOpen && !chatOpen && !aiOpen && !availableVehicleOpen && !myAdsOpen && !myVehiclesOpen && !myFreightsOpen && !quickAccessOpen ? (
        <button
          ref={aiFloatingPill.elementRef}
          type="button"
          onClick={() => { setAiEntryIntent(""); setAiOpen(true); }}
          {...aiFloatingPill.pointerHandlers}
          className="reisor-ai-driver-launch-pill fixed inline-flex min-h-[48px] items-center gap-2 rounded-full border pr-4 text-sm font-black"
          style={{
            right: "max(12px, env(safe-area-inset-right, 0px))",
            bottom: isNativeAndroidRuntime
              ? "calc(env(safe-area-inset-bottom, 0px) + 96px)"
              : "92px",
            zIndex: 10020,
            ...aiFloatingPill.floatingStyle,
          }}
          aria-label="REISOR AI"
          aria-grabbed={aiFloatingPill.dragging ? "true" : "false"}
          data-reisor-ai-driver-pill="v03d4"
          data-reisor-ai-draggable="true"
        >
          <span className="reisor-ai-driver-avatar-wrap" aria-hidden="true">
            <img src="/icons/reisor_ai_avatar_v03d4.png" alt="" className="reisor-ai-driver-avatar" />
          </span>
          <Sparkles className="h-4 w-4" aria-hidden="true" />
          <span>REISOR AI</span>
          <ChevronRight className="h-4 w-4 opacity-80" aria-hidden="true" />
        </button>
      ) : null}

      {/* bottom nav */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          height: bottomNavBackdropHeight,
          background: uiTheme === "light" ? "rgba(243, 244, 246, 0.98)" : "rgba(2, 6, 23, 0.98)",
          zIndex: 9990,
          display: (settingsOpen || chatOpen || aiOpen || availableVehicleOpen || myAdsOpen || myVehiclesOpen || myFreightsOpen || quickAccessOpen) ? "none" : "block",
          pointerEvents: "none",
        }}
      />
      <div
        className="driver-bottom-nav fixed border-t border-slate-700 bg-slate-900/98 backdrop-blur rounded-2xl"
        style={{
          position: "fixed",
          left: "50%",
          right: "auto",
          width: "calc(min(760px, 100vw) - 16px)",
          bottom: bottomNavOffset,
          zIndex: 9999,
          display: (settingsOpen || chatOpen || aiOpen || availableVehicleOpen || myAdsOpen || myVehiclesOpen || myFreightsOpen || quickAccessOpen) ? "none" : "block",
          visibility: "visible",
          opacity: 1,
          transform: "translateX(-50%)",
        }}
      >
        <div className="w-full px-2">
          <div className="grid grid-cols-5 items-center gap-1 py-2" data-reisor-driver-bottom-nav-order="loads-vehicles-create-star-settings">
          <button
            disabled={!tabsAllowed}
            onClick={() => {
              if (!tabsAllowed) return;
              setMainTab("requests");
            }}
            className={`min-w-0 flex flex-col items-center gap-1 px-1 py-2 rounded-xl transition ${
              mainTab === "requests" ? "text-blue-300" : "text-slate-200/80"
            } ${!tabsAllowed ? "opacity-40" : ""}`}
          >
            <ClipboardList size={20} />
            <div className="text-[11px] font-semibold">{t("tabs.requests")}</div>
          </button>
          <button
            disabled={!tabsAllowed}
            onClick={() => {
              if (!tabsAllowed) return;
              setMainTab("vehicles");
            }}
            className={`min-w-0 flex flex-col items-center gap-1 px-1 py-2 rounded-xl transition ${
              mainTab === "vehicles" ? "text-blue-300" : "text-slate-200/80"
            } ${!tabsAllowed ? "opacity-40" : ""}`}
          >
            <Truck size={20} />
            <div className="text-[11px] font-semibold">
              {String(i18next.language || "").toLowerCase().startsWith("uz")
                ? "Машиналар"
                : String(i18next.language || "").toLowerCase().startsWith("tr")
                  ? "Araçlar"
                  : "Машины"}
            </div>
          </button>
          <button
            type="button"
            disabled={!tabsAllowed || driverAccessLimited}
            onClick={() => {
              if (!tabsAllowed || driverAccessLimited) return;
              setMyAdsOpen(true);
            }}
            className={`min-w-0 flex items-center justify-center rounded-xl transition ${
              !tabsAllowed || driverAccessLimited ? "opacity-40" : "text-blue-200"
            }`}
            aria-label={String(i18next.language || "").toLowerCase().startsWith("uz") ? "Менинг эълонларим" : String(i18next.language || "").toLowerCase().startsWith("tr") ? "İlanlarım" : "Мои объявления"}
            title={String(i18next.language || "").toLowerCase().startsWith("uz") ? "Менинг эълонларим" : String(i18next.language || "").toLowerCase().startsWith("tr") ? "İlanlarım" : "Мои объявления"}
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-full border border-emerald-400/40 bg-emerald-600 text-white shadow-lg shadow-emerald-950/30">
              <Plus size={24} strokeWidth={2.6} />
            </span>
          </button>
          <button
            type="button"
            disabled={!tabsAllowed || driverAccessLimited}
            onClick={() => {
              if (!tabsAllowed || driverAccessLimited) return;
              setQuickAccessOpen(true);
            }}
            className={`min-w-0 flex items-center justify-center rounded-xl ${
              !tabsAllowed || driverAccessLimited ? "opacity-40" : ""
            }`}
            aria-label={String(i18next.language || "").toLowerCase().startsWith("uz") ? "Сақланган" : String(i18next.language || "").toLowerCase().startsWith("tr") ? "Kaydedilenler" : "Сохранённые"}
            title={String(i18next.language || "").toLowerCase().startsWith("uz") ? "Сақланган" : String(i18next.language || "").toLowerCase().startsWith("tr") ? "Kaydedilenler" : "Сохранённые"}
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-amber-400/45 bg-amber-400/10 text-amber-300">
              <Star size={20} fill="currentColor" />
            </span>
          </button>
          <button
            type="button"
            onClick={() => { setSettingsTab("main"); setSettingsOpen(true); }}
            className="min-w-0 flex items-center justify-center rounded-xl"
            aria-label={t("settings.title")}
            title={t("settings.title")}
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-700 bg-slate-800/40 text-slate-200">
              <Settings size={18} />
            </span>
          </button>
          </div>
        </div>
      </div>
      <DriverQuickAccessSheet
        open={quickAccessOpen}
        onClose={() => setQuickAccessOpen(false)}
        onSaved={() => {
          setQuickAccessOpen(false);
          setRequestsSource("market");
          setMainTab("requests");
          setMarketFavoritesSignal((value) => value + 1);
        }}
        onVehicles={() => {
          setQuickAccessOpen(false);
          setMyVehiclesOpen(true);
        }}
      />
        <DriverAvailableVehicleModal
        open={availableVehicleOpen}
        onClose={() => { setAvailableVehicleOpen(false); setAvailableVehicleDraft(null); }}
        driverToken={driverToken}
        driver={driverObj || driver}
        vehicle={availableVehicleDraft}
        onCreated={() => {
          setAvailableVehicleOpen(false);
          setAvailableVehicleDraft(null);
          showToast("ok", t("reisorAi.vehiclePublished", { defaultValue: "Свободная машина опубликована" }), { duration: 4200 });
        }}
        onSaved={(_item, meta = {}) => {
          if (meta?.mode !== "edit") return;
          setAvailableVehicleOpen(false);
          setAvailableVehicleDraft(null);
        }}
      />

      <DriverMyAdsSheet
        open={myAdsOpen}
        onClose={() => setMyAdsOpen(false)}
        onFreight={() => {
          setMyAdsOpen(false);
          setMyFreightsOpen(true);
        }}
        onVehicles={() => {
          setMyAdsOpen(false);
          setMyVehiclesOpen(true);
        }}
      />

      <DriverMyVehicles
        open={myVehiclesOpen}
        onClose={() => setMyVehiclesOpen(false)}
        driverToken={driverToken}
        driver={driver}
      />

      <DriverMyFreights
        open={myFreightsOpen}
        onClose={() => {
          setMyFreightsOpen(false);
          setTargetedVehicleFreight(null);
        }}
        driverToken={driverToken}
        onChanged={() => {}}
        createRequestKey={targetedVehicleCreateKey}
        createContext={targetedVehicleFreight}
        onCreated={handleDriverTargetedFreightCreated}
        onTargetCreateCancelled={() => setTargetedVehicleFreight(null)}
      />

      <ClientTrustReviewModal
        open={!!clientTrustReviewOrder}
        order={clientTrustReviewOrder}
        token={driverToken}
        onClose={() => setClientTrustReviewOrder(null)}
        onSubmitted={(message) => {
          setClientTrustReviewOrder(null);
          showToast("ok", message || "Спасибо. Ваша оценка помогает REISOR защищать водителей от фейковых заказов.", { duration: 4500 });
        }}
      />

      {/* ============================================================================
   CHAT LOCK: do not edit chat without an explicit owner task.
   Any change requires reviewing this block first and a separate patch.
   ============================================================================ */}
{/* chat modal */}
      {chatOpen && chatOrder ? (
        <div
          className="fixed inset-0 bg-black/60 flex items-end"
          style={{ zIndex: 10050 }}
        >
          <div
            className="w-full bg-slate-900 border-t border-slate-700 rounded-t-3xl overflow-hidden flex flex-col"
            style={{ maxHeight: "calc(100dvh - env(safe-area-inset-top, 0px) - 8px)" }}
          >
            <div className="p-3 border-b border-slate-700 flex items-center justify-between">
              <button
                onClick={closeChat}
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-800 text-white"
              >
                <ChevronLeft size={18} /> {t("driverChat.back")}
              </button>
              <div className="text-sm text-slate-300">{t("driverChat.orderNo", { number: humanOrderNo(orderIdOf(chatOrder)) })}</div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-3 space-y-2">
              {chatLoading ? <div className="text-slate-300 text-sm">{t("driverChat.loading")}</div> : null}
              {chatError ? <div className="text-red-300 text-sm">❌ {chatError}</div> : null}
              {!chatLoading && !chatError && chatMessages.length === 0 ? (
                <div className="text-slate-300 text-sm">{t("driverChat.empty")}</div>
              ) : null}
              {chatMessages.map((m, idx) => {
                const by = String(m?.by || m?.from || "").toLowerCase();
                const isMe = by === "driver" || String(m?.userId || "") === String(driver?.id || driver?._id || "");
                const text = String(m?.text || m?.message || "").trim();
                const ts = m?.at || m?.createdAt || m?.ts || m?.time || null;
                return (
                  <div key={idx} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm border ${
                      isMe
                        ? "bg-emerald-600/20 border-emerald-400/30 text-white"
                        : "bg-slate-800 border-slate-700 text-white"
                    }`}>
                      <div className="whitespace-pre-line">{text}</div>
                      {text ? (
                        <button
                          type="button"
                          onClick={() => reisorStage20ACopyAndOpenTranslator(text)}
                          title={t("driverChat.translateExternalTitle")}
                          className="mt-2 rounded-full border border-slate-500/40 bg-slate-700/60 px-2 py-1 text-[11px] font-bold text-slate-100"
                        >
                          {t("driverChat.translateExternal")}
                        </button>
                      ) : null}
                      {ts ? <div className="text-[10px] text-slate-300/70 mt-1">{ruDateTime(ts)}</div> : null}
                    </div>
                  </div>
                );
              })}
            </div>
            <div
              className="p-3 border-t border-slate-700 flex gap-2"
              style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
            >
              <input
                autoFocus
                value={chatText}
                onChange={(e) => setChatText(e.target.value)}
                placeholder={t("driverChat.placeholder")}
                className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-sm outline-none text-white"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendChat();
                  }
                }}
              />
              <Button className="bg-purple-600 hover:bg-purple-700 focus:ring-purple-400" onClick={sendChat} disabled={!chatText.trim()}>
                {t("driverChat.send")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
